/**
 * Approvals — the screen the vendor-invoice chain runs on.
 *
 * The hard-crab flow: Matt Jr matches the invoice against the trip's yield sheet,
 * Matt Sr makes the return judgement (and is the only step that can cut the
 * amount), Greg gives final approval, and it lands with Mercedes to enter and pay.
 *
 * The yield match is the point of the page. Everything the approver needs to
 * answer "does this bill match what we actually received?" is on one screen:
 * what the trip cost us (crabs AND freight, which live in different tables), how
 * many bushels came in dead, and what the invoice says. The arithmetic is done
 * server-side in neon.js so this page can never disagree with what an approval
 * records.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText, Check, X, Scissors, Upload, Search, ChevronRight, ChevronDown,
  AlertTriangle, Plus, Link2, Paperclip, Clock, DollarSign, RefreshCw, Trash2,
  Camera, Mail, ArrowUp, ArrowDown, Image as ImageIcon, CornerUpLeft, Truck,
  MessageSquare, Ban, Receipt, CheckCircle2,
} from 'lucide-react';
import { neonQuery, uploadApInvoiceFile, apFileUrl, apYieldDocs, apCombinedPdf, apInvoiceUnits, apInvoiceCoach, apInvoiceContext, apQboPush, apVendorProfiles } from '../services/api';
import InvoiceContext from '../components/InvoiceContext';
import VendorProfileCard from '../components/VendorProfileCard';
import { QboNeedsAttention, QboSendDialog, QboLinesCard, QboStatusChip, qboBillUrl } from '../components/QboPush';
import InvoiceCoach, { PlanWorkspace, POWorkspace } from '../components/InvoiceCoach';
import { getCurrentUser, getAccessToken } from '../services/auth';

// ─── formatting ───────────────────────────────────────────────────────────────

// ── What a load graded out to ───────────────────────────────────────────────
// Greg, 2026-09-02: "we use the same units that are on the yield sheet. Everything
// gets converted there. So I want to see the ones, twos and threes. If there are no
// twos or threes, do not show me twos or threes - just the ones that actually have
// a positive quantity."
//
// The numbers ARE the yield sheet's own grade ids, so the figure on this screen is
// the figure on that sheet with nothing in between.
// ⚠️ THE NUMBER IS THE GRADE THE CRABBER SOLD IT AS - a #1, a #2, a #3 - which is
// what Greg means by "five bushels at a hundred and thirty dollars a bushel. That is
// five ones." It is NOT the yield sheet grade id, and it is not Colossal/Premium/
// Select: those are what the crabs graded out to AFTER we sorted them, a different
// fact arriving in quarter-bushel fractions.
const soldAsLabel = (g) => (/^[0-9]+$/.test(String(g)) ? '#' + g + 's' : String(g));
const gradeNum = (n) => Math.round(Number(n) * 100) / 100;

// Add up grades across a set of bills. Only grades with something in them come
// back, which is the whole ask - a column of zeroes says nothing.
function sumGrades(rows) {
  const out = {};
  for (const r of rows || []) {
    for (const [g, q] of Object.entries(r.grades || {})) {
      const v = Number(q);
      if (isFinite(v) && v > 0) out[g] = (out[g] || 0) + v;
    }
  }
  return out;
}
const gradeTitle = (g) => Object.entries(g || {})
  .sort((a, b) => a[0] - b[0])
  .map(([k, v]) => `${gradeNum(v)} ${gradeNum(v) === 1 ? 'box' : 'boxes'} of ${soldAsLabel(k)}`)
  .join(', ') + ' (a box and a bushel are the same thing)';

// The compact form that fits in a column: the grade number, then how much of it.
function Grades({ grades, empty }) {
  const list = Object.entries(grades || {})
    .filter(([, q]) => Number(q) > 0)
    .sort((a, b) => a[0] - b[0]);
  if (!list.length) {
    return <span style={{ color: 'var(--gray-500, #6b7280)', fontSize: 11.5 }}>{empty || '—'}</span>;
  }
  return (
    <span title={gradeTitle(grades)} style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {list.map(([g, q]) => (
        <span key={g} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <span style={{
            minWidth: 15, height: 15, borderRadius: 3, background: 'var(--navy, #00395C)',
            color: '#fff', fontSize: 9.5, fontWeight: 800, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center',
          }}>{g}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{gradeNum(q)}</span>
        </span>
      ))}
    </span>
  );
}

// The same thing in words, for a heading.
const gradeSummary = (grades) => Object.entries(grades || {})
  .filter(([, q]) => Number(q) > 0)
  .sort((a, b) => a[0] - b[0])
  .map(([g, q]) => `${gradeNum(q)} ${soldAsLabel(g)}`)
  .join(' · ');

// Approve a bill from the list, without opening it.
//
// Greg, 2026-09-03: "change the text, the final approval, make it a pill that approves
// the bill without me needing to drill into the bill. It's causing me extra clicks
// that aren't really necessary."
//
// ⚠️ IT SHOWS THE MONEY AND HAS NO CONFIRM, DELIBERATELY. A confirm here would put
// back the click that was the whole point. What makes that safe is that the button
// names the amount it is about to approve, on a row that already names the crabber
// and the invoice number - so the deliberate part is reading the button, not
// dismissing a dialog nobody reads by the fifth one.
//
// ⚠️ IT IS THE SAME HANDLER AS THE BUTTON INSIDE THE BILL. Every rule - who may
// approve, whether the paperwork changed underneath, the note a low return now
// requires - is enforced server-side and applies identically here. A refusal is shown
// on the row rather than swallowed, because the reason is the useful part: "females
// came back at 30%, put a note on the yield sheet" is an instruction, not an error.
function ApprovePill({ row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // ⚠️ WHEN THE SERVER REFUSES BECAUSE THE YIELD OR THE RETURN IS UNDER 70% AND
  // NO NOTE IS ON THE BILL, MATT SR IS STUCK. Every place where a note would
  // count (the yield sheet's note, the bill's own notes field, a reason typed
  // with the action) is either behind the 15-minute HCD sync or on a screen he
  // is not on, and the error just told him to go and do something without giving
  // him anywhere to do it. So the pill opens a small note input in place, he
  // types one word, and the retry goes through as `reason` - which lands on the
  // approval trail exactly where a typed reason on the inside button would.
  const [noteRequired, setNoteRequired] = useState(null); // { pct, message, reason }
  const amount = row.approved_amount == null ? row.invoice_amount : row.approved_amount;
  const approve = async (reason) => {
    setBusy(true); setErr('');
    try {
      await neonQuery('ap_document_action', {
        documentId: row.id,
        action: 'approve',
        ...(reason ? { reason } : {}),
      });
      setNoteRequired(null);
      onChanged && onChanged();
    } catch (ex) {
      const b = ex.body || {};
      if (b.yieldPct != null || b.returnPct != null) {
        setNoteRequired({
          message: ex.message,
          reason: '',
        });
      } else {
        setErr(ex.message);
      }
    }
    setBusy(false);
  };
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
      {!noteRequired && (
        <button
          disabled={busy}
          title={`Approve ${row.vendor_name || 'this bill'} without opening it`}
          onClick={(e) => { e.stopPropagation(); approve(); }}
          style={{
            background: busy ? '#f3f4f6' : 'var(--orange, #DC582A)', color: busy ? '#6b7280' : '#fff',
            border: 'none', borderRadius: 999, fontSize: 11.5, fontWeight: 800,
            padding: '4px 12px', cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
            whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5,
          }}
        >
          <Check size={12} />
          {busy ? 'Approving…' : `Approve ${money(amount)}`}
        </button>
      )}
      {noteRequired && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 280,
            background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6,
            padding: '6px 8px',
          }}
        >
          <span style={{ color: '#9a4a1c', fontSize: 11, lineHeight: 1.35 }}>
            {noteRequired.message}
          </span>
          <input
            autoFocus
            value={noteRequired.reason}
            onChange={(e) => setNoteRequired({ ...noteRequired, reason: e.target.value })}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter' && noteRequired.reason.trim()) approve(noteRequired.reason.trim());
              if (e.key === 'Escape') setNoteRequired(null);
            }}
            placeholder="Type one line saying why"
            style={{
              fontSize: 12, padding: '4px 6px', border: '1px solid #d1d5db',
              borderRadius: 4, width: '100%', fontFamily: 'inherit',
            }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              disabled={busy || !noteRequired.reason.trim()}
              onClick={(e) => { e.stopPropagation(); approve(noteRequired.reason.trim()); }}
              style={{
                background: busy || !noteRequired.reason.trim() ? '#f3f4f6' : 'var(--orange, #DC582A)',
                color: busy || !noteRequired.reason.trim() ? '#6b7280' : '#fff',
                border: 'none', borderRadius: 999, fontSize: 11, fontWeight: 800,
                padding: '3px 10px', cursor: busy || !noteRequired.reason.trim() ? 'default' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {busy ? 'Approving…' : `Approve ${money(amount)}`}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setNoteRequired(null); }}
              style={{
                background: 'transparent', color: 'var(--gray-500, #6b7280)',
                border: 'none', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {err && (
        <span style={{ color: '#b42318', fontSize: 11, maxWidth: 230, lineHeight: 1.35 }}>{err}</span>
      )}
    </span>
  );
}

// ⚠️ OPENS THE BILL'S PAPERWORK WITHOUT OPENING THE BILL. Greg works down this list
// and wants to read the invoice and the yield report before deciding, which until
// now meant going in and coming back out again. Same combined copy the bill itself
// builds, so there is one definition of what "the paperwork" is and what order it
// reads in.
// ⚠️ stopPropagation, or clicking it also opens the row underneath it.
function PdfPill({ documentId, needsYield, freight, onChanged }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      title={freight
        // A HAULER'S BILL IS A DIFFERENT PACKET AND SAYS SO. It carries the crabber
        // invoices for the loads TICKED onto it, and only those, so an approver who
        // sees one crabber knows the tick decided that rather than a guess of ours.
        ? 'The hauler\u2019s bill and the crabber invoices for the loads ticked onto it'
        : 'The invoice, the yield report and the hauler\u2019s bill in one tab'}
      disabled={busy}
      onClick={async (e) => {
        e.stopPropagation();
        const tab = window.open('', '_blank');
        setBusy(true);
        try {
          // \u26a0\ufe0f FETCH THE YIELD REPORT FIRST IF IT IS NOT ON THE BILL YET. Greg,
          // 2026-09-03: "when I hit PDF it just gives the bill, not the yield sheet.
          // If I go to the inside page it would pull it in for me." It did - the pull
          // only ever ran from inside the bill, so a combined copy built from the
          // list was honestly complete and still missing the document he most wanted.
          // \u26a0\ufe0f Only when it is genuinely absent (has_yield_report), because the pull
          // searches a mailbox and there is no reason to pay for that on a bill that
          // already has it. Failure is swallowed: a report that has not been emailed
          // yet must not stop the invoice opening.
          if (needsYield) {
            try { await apYieldDocs('pull', { documentId }); onChanged && onChanged(); }
            catch (_) { /* open what we do have */ }
          }
          const r = await apCombinedPdf(documentId);
          if (tab) tab.location = r.url; else window.location = r.url;
        } catch (err) {
          if (tab) tab.close();
          window.alert(err.message);
        }
        setBusy(false);
      }}
      style={{
        marginLeft: 8, verticalAlign: 'middle', cursor: busy ? 'wait' : 'pointer',
        background: '#fff', border: '1px solid #c9dcee', color: 'var(--navy, #00395C)',
        borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: '0.3px',
        padding: '2px 8px', textTransform: 'uppercase', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
      }}
    >
      <FileText size={11} />
      {busy ? 'opening…' : 'PDFs'}
    </button>
  );
}

// ── Crab & Cruise, a week at a time ─────────────────────────────────────────
// Greg, 2026-09-02: "some of these are Crab & Cruise... there is no yield, and
// there is not a whole lot for me to look at. What I'm more concerned with is how
// many total units did we hold for Crab & Cruise, because that'll look normal to
// me or not normal... give me every Crab & Cruise vendor that showed up in that
// week. We usually use twenty to thirty at the event."
//
// ⚠️ THE WEEK ENDS ON SATURDAY, HIS CALL, and it is keyed off the INVOICE DATE
// rather than when the bill was entered - the paperwork turns up days apart and a
// load bought on the Thursday belongs to that Saturday's event however late the
// invoice was keyed in.
const WEEK_ENDS_ON = 6; // Saturday
function weekEnding(dateish) {
  if (!dateish) return null;
  const d = new Date(dateish);
  if (isNaN(d)) return null;
  // Dates from Neon arrive as UTC midnight; read them as calendar days so a bill
  // does not slide into the previous week in Eastern time.
  const day = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const shift = (WEEK_ENDS_ON - day.getUTCDay() + 7) % 7;
  day.setUTCDate(day.getUTCDate() + shift);
  return day.toISOString().slice(0, 10);
}
const weekLabel = (iso) => {
  if (!iso) return 'no invoice date';
  const [y, m, d] = iso.split('-').map(Number);
  return `week of ${new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US',
    { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
};

// How much came in on one bill. ⚠️ THE TRIP WINS WHEN THERE IS ONE - a bill on a
// purchase order already records this, and a second editable copy is how two
// numbers for one load end up disagreeing. The typed figure is the fallback for a
// Crab & Cruise load, which has no trip and therefore no quantity at all: measured
// 2026-09-02, all 8 C&C bills carry purchase_order_id NULL and not one of their
// invoice numbers exists as a trip at any date, because a C&C load never gets a
// yield sheet so nobody enters it as a purchase order.
const UNIT_WORD = { '1': 'lb', '2': 'bu', lb: 'lb', lbs: 'lb', bu: 'bu', boxes: 'boxes' };
function billQuantity(row) {
  const tripQty = Number(row.trip_quantity);
  if (row.trip_quantity != null && row.trip_quantity !== '' && isFinite(tripQty)) {
    return { qty: tripQty, unit: UNIT_WORD[String(row.trip_unit)] || '', from: 'the trip' };
  }
  const own = Number(row.quantity);
  if (row.quantity != null && row.quantity !== '' && isFinite(own)) {
    return { qty: own, unit: UNIT_WORD[String(row.quantity_unit)] || row.quantity_unit || '', from: 'the bill' };
  }
  return null;
}

// Add up a set of bills by unit. ⚠️ POUNDS AND BUSHELS ARE NEVER ADDED TOGETHER
// and are never converted into each other here. Greg asked for boxes; nothing in
// the data says how many pounds make a box, and inventing a factor would produce a
// number that looks exactly like the answer and is not one. Each unit is totalled
// on its own until somebody tells us the conversion.
function totalUnits(rows) {
  const by = {};
  let missing = 0;
  for (const r of rows || []) {
    const q = billQuantity(r);
    if (!q || !q.unit) { missing++; continue; }
    by[q.unit] = (by[q.unit] || 0) + q.qty;
  }
  return { by, missing };
}

// What a list of bills comes to. Greg, 2026-09-02, working down 22 of them:
// "give me the total of how many vendors and what the total amount to approve
// is, so I have some idea of what the spend is as I go through."
//
// ⚠️ THE MONEY IS EXACT AND THE COUNT IS HONEST ABOUT WHAT IT COUNTED. A bill
// with no amount on it yet is not a zero, so it is excluded from the total and
// said out loud - a figure quietly missing three bills is worse than no figure,
// because it reads as the whole spend.
//
// ⚠️ THE PARTY IS NORMALISED, because the same crabber is typed more than one
// way: "Southern Connection" and "Southern Connection Seafood" are one outfit
// on two bills, and "Fords Seafood" and "Ford's Seafood" are one crabber. Case,
// punctuation and the trade suffixes are dropped, and a name that is a prefix of
// another folds into it - so those pair up rather than reading as four.
const PARTY_SUFFIX = /\b(seafood|crab|crabs|company|co|inc|llc|ltd|corp|and)\b/g;
// ⚠️ AND A NAME THAT IS ALL SUFFIX KEEPS ITS SUFFIX. Stripping the trade words out
// of "The Crab Company LLC" leaves "the", which is short enough to swallow an
// unrelated crabber whose name merely starts the same way. Anything under four
// characters falls back to the whole name instead.
const partyKey = (name) => {
  const full = String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const trimmed = String(name || '')
    .toLowerCase().replace(PARTY_SUFFIX, ' ').replace(/[^a-z0-9]+/g, '');
  return trimmed.length >= 4 ? trimmed : full;
};
const queueTotals = (rows) => {
  const list = rows || [];
  const amounts = list.map(r => Number(r.invoice_amount))
    .filter(v => v != null && isFinite(v));
  const keys = list.map(r => partyKey(r.vendor_name)).filter(Boolean);
  // Longest first, so a shorter name is absorbed by the fuller one it prefixes
  // rather than the other way round.
  const parties = [];
  for (const k of [...new Set(keys)].sort((a, b) => b.length - a.length)) {
    if (!parties.some(p => p.startsWith(k))) parties.push(k);
  }
  return {
    count: list.length,
    parties: parties.length,
    total: amounts.reduce((a, b) => a + b, 0),
    noAmount: list.length - amounts.length,
  };
};

// The line that sits beside a queue heading. Renders nothing on an empty list,
// where "0 crabbers · $0.00" is noise dressed up as information.
// ⚠️ The party word depends on the PAGE, not the row. On Invoice Approvals nobody
// on these bills is a crabber or a hauler - Mercedes, 2026-09-18: "this isn't the
// crabber invoice section." So the caller passes kind='invoice' and the summary
// reads "N vendors" instead.
function QueueTotals({ rows, verb, grades, kind }) {
  const t = queueTotals(rows);
  if (!t.count) return null;
  const partyWord = kind === 'invoice'
    ? (t.parties === 1 ? 'vendor' : 'vendors')
    : (t.parties === 1 ? 'crabber or hauler' : 'crabbers and haulers');
  return (
    <span style={{
      marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10,
      fontSize: 12.5, fontWeight: 600, color: 'var(--gray-500, #6b7280)',
    }}>
      <span>{t.parties} {partyWord}</span>
      {grades && gradeSummary(sumGrades(rows)) && (
        <>
          <span style={{ color: 'var(--gray-300, #d1d5db)' }}>|</span>
          <span title={gradeTitle(sumGrades(rows))} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {gradeSummary(sumGrades(rows))}
            <span style={{ fontWeight: 400 }}> graded</span>
          </span>
        </>
      )}
      <span style={{ color: 'var(--gray-300, #d1d5db)' }}>|</span>
      <span style={{ color: 'var(--navy, #00395C)', fontWeight: 800, fontSize: 14 }}>
        {money(t.total)}
      </span>
      <span style={{ fontWeight: 600 }}>{verb || 'to approve'}</span>
      {t.noAmount > 0 && (
        <span style={{ color: 'var(--orange, #DC582A)', fontWeight: 700 }}>
          + {t.noAmount} with no amount on {t.noAmount === 1 ? 'it' : 'them'} yet
        </span>
      )}
    </span>
  );
}

const money = (v) => (v == null || v === '' || !isFinite(Number(v)))
  ? '—'
  : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const num = (v, dp = 2) => (v == null || v === '' || !isFinite(Number(v)))
  ? '—'
  : Number(v).toLocaleString('en-US', { maximumFractionDigits: dp });

// ⚠️ PEOPLE TYPE "$4,000.00". Number("4,000") is NaN, JSON.stringify turns NaN
// into null, and the amount silently disappeared - three invoices were sitting in
// drafts with no amount on them, and the submit that then refused them reported
// only "Neon error: 400". Strip the formatting instead of discarding the number.
// null means genuinely empty; undefined means "not a number yet", so a half-typed
// "4," is left alone rather than saved as nothing.
const parseMoney = (raw) => {
  const s = String(raw == null ? '' : raw).replace(/[$,\s]/g, '');
  if (s === '') return null;
  const n = Number(s);
  return isFinite(n) ? n : undefined;
};

// Dates arrive as 'YYYY-MM-DD' (or a timestamp). Parse the parts by hand rather
// than with new Date(), which reads a bare date as UTC and renders the day before
// in Eastern.
const fmtDate = (v) => {
  if (!v) return '—';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(v);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Group the same vendor's bills together (Mercedes's ask), then oldest invoice
// first inside a vendor so a run of bills reads chronologically. A missing
// vendor sinks to the bottom rather than pretending to be "" at the top; a
// missing invoice date sinks below its vendor's dated bills.
// ── PAYMENT TERMS ON THE PAGE ────────────────────────────────────────────────
// The options come DOWN FROM THE SERVER (`net_terms_options` on the bill,
// `netTerms` on the inbox payload) because lib/ap-db.js owns the list and Create
// React App will not let this file import anything outside src/. These two helpers
// only ever read what was served, so there is no second copy of the terms here to
// drift away from the one the column is sorted by.
const termLabel = (value, options) => {
  if (!value) return null;
  const hit = (options || []).find((t) => t.value === value);
  return hit ? hit.label : value;
};
// Map whatever words the reader saw on the invoice onto one of the served terms.
// ⚠️ RETURNS NULL RATHER THAN ROUNDING. "Net 37" is real on some invoices and
// calling it Net 30 would put a fabricated due date on the payment run.
const normalizeTerms = (raw, options) => {
  const s = String(raw == null ? '' : raw).toLowerCase().trim();
  if (!s) return null;
  const opts = options || [];
  const exact = opts.find((t) => t.value === s || t.label.toLowerCase() === s);
  if (exact) return exact.value;
  if (/prepaid|paid in advance|pre-paid/.test(s)) {
    return opts.some((t) => t.value === 'prepaid') ? 'prepaid' : null;
  }
  if (/due (on|upon) receipt|due immediately|payable on receipt|\bcod\b|c\.o\.d|net 0\b/.test(s)) {
    return opts.some((t) => t.value === 'due_on_receipt') ? 'due_on_receipt' : null;
  }
  if (/on account|open account|statement/.test(s)) {
    return opts.some((t) => t.value === 'on_account') ? 'on_account' : null;
  }
  const m = s.match(/(?:net|terms|n)?\s*[:-]?\s*(\d{1,3})\s*(?:days?|d\b)?/);
  if (m) {
    const hit = opts.find((t) => t.days === Number(m[1]));
    if (hit) return hit.value;
  }
  return null;
};

// How a due date reads on a list somebody is paying from: the date, plus whether
// it has already gone by. ⚠️ Built from the YYYY-MM-DD parts, never `new Date(s)`
// - a DATE column arrives as UTC midnight and rendering that in Eastern shows the
// day before, which is the documented trap in this repo and would make a bill due
// today read as overdue.
const dueParts = (value) => {
  const s = String(value || '').slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const now = new Date();
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const thenUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const days = Math.round((thenUtc - todayUtc) / 86400000);
  return { text: `${m[2]}/${m[3]}/${m[1].slice(2)}`, days };
};

// The due date as it reads on a list somebody is paying from. Overdue and due-today
// are called out; everything else is just a date, because colouring a bill due in
// three weeks teaches her to ignore the colour on the one due tomorrow.
// ⚠️ A BLANK DUE DATE SAYS SO RATHER THAN SHOWING A DASH IN THE OVERDUE COLOUR.
// Nobody has told us the terms on most of the bills already in the queue, and
// "not set" is a different fact from "due whenever".
function DueCell({ value, terms }) {
  const p = dueParts(value);
  if (!p) {
    return (
      <span style={{ color: 'var(--gray-400, #9ca3af)', fontSize: 12.5 }}>
        {terms ? 'not set' : '—'}
      </span>
    );
  }
  const overdue = p.days < 0;
  const today = p.days === 0;
  const soon = p.days > 0 && p.days <= 3;
  const color = overdue ? '#b42318' : today ? 'var(--orange, #DC582A)'
    : soon ? '#9a4a1c' : 'var(--navy, #00395C)';
  return (
    <span style={{ color, fontWeight: overdue || today ? 700 : 500, whiteSpace: 'nowrap' }}>
      {p.text}
      {overdue && (
        <span style={{ fontSize: 11, marginLeft: 5 }}>
          {p.days === -1 ? '1 day late' : `${Math.abs(p.days)} days late`}
        </span>
      )}
      {today && <span style={{ fontSize: 11, marginLeft: 5 }}>today</span>}
      {soon && <span style={{ fontSize: 11, marginLeft: 5 }}>in {p.days}d</span>}
    </span>
  );
}

// Entered in QuickBooks, not yet paid. ⚠️ A BADGE ON A BILL THAT STAYS ON THE LIST,
// which is Mercedes's own call: "whether you want the Entered badge to keep the bill
// on Ready to Pay (my preference, so I do not lose it) or push it into its own tab."
// Kept on the list.
//
// ⚠️ TWO SIGNALS, EITHER IS ENOUGH. The row from ap_inbox's readyToPay query does not
// (yet) carry entered_in_qb_at, so a bill Mercedes just sent to QuickBooks had the
// column render fall through to QboStatusChip's "not entered" fallback - which is
// precisely the confusing cell she pointed at on 2026-10-14. The qbCheck comes back
// with status: 'entered' for the same bills, so pass that in as a fallback and the
// badge renders either way; the name/time still ride the row if the SELECT ever
// starts carrying them.
function EnteredBadge({ row, check }) {
  const entered = (row && row.entered_in_qb_at) || (check && check.status === 'entered');
  if (!entered) return null;
  const byName = row && row.entered_in_qb_by_name;
  const elsewhere = (row && row.entered_in_company) || (check && check.enteredIn);
  return (
    <span title={elsewhere
      ? `Entered in ${elsewhere}'s books, not sent to this company's QuickBooks.`
      : byName
      ? `Entered in QuickBooks by ${byName}. Not paid yet.`
      : 'Entered in QuickBooks. Not paid yet.'}
      style={{
        background: '#e8f5ec', color: '#1c7c43', border: '1px solid #b7e0c6',
        fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 999,
        textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap',
      }}>Entered</span>
  );
}

const sortByVendorThenInvoiceDate = (rows) => {
  const arr = Array.isArray(rows) ? rows.slice() : [];
  arr.sort((a, b) => {
    const av = (a.vendor_name || '').trim().toLowerCase();
    const bv = (b.vendor_name || '').trim().toLowerCase();
    if (!av && bv) return 1;
    if (av && !bv) return -1;
    if (av !== bv) return av.localeCompare(bv);
    const ad = a.invoice_date ? String(a.invoice_date).slice(0, 10) : '';
    const bd = b.invoice_date ? String(b.invoice_date).slice(0, 10) : '';
    if (!ad && bd) return 1;
    if (ad && !bd) return -1;
    return ad.localeCompare(bd);
  });
  return arr;
};

const fmtWhen = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const fmtBytes = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const GRADE_LABELS = { 1: '#1s (Large Male)', 2: '#2s (Medium Male)', 3: '#3s (Female)' };
const gradeLabel = (code) => GRADE_LABELS[code] || (code ? `Grade ${code}` : '—');

const STATUS_META = {
  draft: { label: 'Draft', bg: '#eef1f5', fg: 'var(--gray-600, #4b5563)' },
  returned: { label: 'Sent back', bg: '#fdeaea', fg: '#b42318' },
  pending: { label: 'Awaiting approval', bg: '#fff3e6', fg: 'var(--orange, #DC582A)' },
  approved: { label: 'Ready to pay', bg: '#e8f5ec', fg: '#1c7c43' },
  rejected: { label: 'Rejected', bg: '#fdeaea', fg: '#b42318' },
  paid: { label: 'Paid', bg: '#eef1f5', fg: 'var(--navy, #00395C)' },
};

// ─── small shared bits ────────────────────────────────────────────────────────

const card = {
  background: 'white', border: '1px solid var(--gray-200, #e5e7eb)',
  borderRadius: 8, marginBottom: 16,
};
const cardHead = {
  padding: '12px 16px', borderBottom: '1px solid var(--gray-100, #f3f4f6)',
  fontWeight: 600, fontSize: 13, color: 'var(--navy, #00395C)',
  display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between',
};
const cardBody = { padding: 16 };
const labelStyle = {
  fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px',
  color: 'var(--gray-500, #6b7280)', fontWeight: 600, marginBottom: 4,
};
const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid var(--gray-300, #d1d5db)',
  borderRadius: 6, fontSize: 13, fontFamily: 'inherit',
};

// The row's own stage in the words the team uses ("Waiting on Matt Cain"), which
// the server works out once so every list on the page says the same thing.
function StatusPill({ status, label }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span style={{
      background: m.bg, color: m.fg, fontSize: 11, fontWeight: 700,
      padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>{label || m.label}</span>
  );
}

// How long it has sat where it is. Silent until it crosses the threshold - a chip
// on every row would just become wallpaper.
function AgeChip({ row }) {
  const d = Number(row.days_in_stage || 0);
  if (!row.is_aging) {
    return <span style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
      {d <= 0 ? 'today' : `${d} day${d === 1 ? '' : 's'}`}
    </span>;
  }
  return (
    <span style={{
      background: '#fdeaea', color: '#b42318', fontSize: 11, fontWeight: 700,
      padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: 5,
    }}>
      <Clock size={11} /> {d} days here
    </span>
  );
}

// A thumbnail for a photographed invoice, a document glyph for a PDF. The signed
// URL is fetched once per file and held for the life of the screen.
function FilePreview({ file }) {
  const [src, setSrc] = useState(null);
  const isImage = /^image\//.test(file.content_type || '')
    || /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(file.filename || '');
  useEffect(() => {
    let live = true;
    if (!isImage) return undefined;
    apFileUrl(file.r2_key).then(u => { if (live) setSrc(u); }).catch(() => {});
    return () => { live = false; };
  }, [file.r2_key, isImage]);
  const box = {
    width: 34, height: 34, borderRadius: 5, flexShrink: 0,
    border: '1px solid var(--gray-200, #e5e7eb)', background: 'var(--gray-50, #f9fafb)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  };
  if (isImage && src) {
    return <span style={box}><img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></span>;
  }
  return (
    <span style={box}>
      {isImage ? <ImageIcon size={15} style={{ color: 'var(--gray-400, #9ca3af)' }} />
               : <FileText size={15} style={{ color: 'var(--gray-400, #9ca3af)' }} />}
    </span>
  );
}

function Field({ label, children, style }) {
  return (
    <div style={{ minWidth: 120, ...style }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--navy, #00395C)' }}>{children}</div>
    </div>
  );
}

function Banner({ tone = 'info', icon, children }) {
  const tones = {
    good: { bg: '#e8f5ec', border: '#b7e0c6', fg: '#1c7c43' },
    warn: { bg: '#fff3e6', border: '#f6d4b8', fg: '#9a4a1c' },
    bad: { bg: '#fdeaea', border: '#f3c2c2', fg: '#b42318' },
    info: { bg: '#eef4fa', border: '#c9dcee', fg: 'var(--navy, #00395C)' },
  };
  const t = tones[tone] || tones.info;
  return (
    <div style={{
      background: t.bg, border: `1px solid ${t.border}`, color: t.fg,
      borderRadius: 6, padding: '10px 14px', fontSize: 13,
      display: 'flex', gap: 10, alignItems: 'flex-start', lineHeight: 1.45,
    }}>
      {icon && <span style={{ flexShrink: 0, marginTop: 1 }}>{icon}</span>}
      <div>{children}</div>
    </div>
  );
}

// ─── Invoice scan banner ─────────────────────────────────────────────────────
// Mercedes, 2026-09-18: drop an invoice and the header fills itself. This is
// the little panel that appears while the reader is working and again when it
// finishes. `apply` fills only fields the person has not typed themselves, so
// dismissing the banner is always safe and pressing the button never overwrites
// anything.
function ScanBanner({ scan, onApply, onDismiss }) {
  if (!scan) return null;
  const t = { bg: '#eef4fa', border: '#c9dcee', fg: 'var(--navy, #00395C)' };
  const dismissBtn = (
    <button onClick={onDismiss} title="Dismiss"
      style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        color: 'inherit', opacity: 0.6, lineHeight: 1, fontSize: 16, marginLeft: 'auto',
      }}>×</button>
  );
  const row = {
    background: t.bg, border: `1px solid ${t.border}`, color: t.fg,
    borderRadius: 6, padding: '10px 14px', fontSize: 13,
    display: 'flex', gap: 10, alignItems: 'flex-start', lineHeight: 1.45,
  };
  if (scan.status === 'reading') {
    return (
      <div style={row}>
        <RefreshCw size={14} className="spin" style={{ marginTop: 1 }} />
        <div>Reading <strong>{scan.filename}</strong>…</div>
        {dismissBtn}
      </div>
    );
  }
  if (scan.status !== 'ready') return null;
  const f = scan.fields || {};
  const found = [];
  const qv = f.qboVendor || null;
  if (f.vendorName) found.push(`vendor ${qv && qv.match ? qv.match.name : f.vendorName}`);
  if (f.invoiceNumber) found.push(`invoice #${f.invoiceNumber}`);
  // Mercedes, Oct 14, 2026: match the vendor to QuickBooks when the bill is entered, and
  // flag it when there is no sure match rather than guessing.
  const vendorNote = !f.vendorName || !qv ? null : qv.match ? (
    <div style={{ fontSize: 12, marginTop: 4, color: '#1c7c43' }}>
      QuickBooks vendor: {qv.match.qboName}{qv.match.name !== f.vendorName ? ` (the invoice reads "${f.vendorName}")` : ''}.
    </div>
  ) : (
    <div style={{ fontSize: 12, marginTop: 4, color: '#9a4a1c' }}>
      "{f.vendorName}" is not a sure match to a QuickBooks vendor.
      {qv.candidates && qv.candidates.length
        ? ` Closest: ${qv.candidates.slice(0, 3).join(', ')}. Pick the right name if it is one of these.`
        : ' If they are a new vendor, Mercedes adds them in QuickBooks before it can go there.'}
    </div>
  );
  if (f.invoiceDate) found.push(f.invoiceDate);
  if (f.invoiceAmount != null) {
    found.push(`$${Number(f.invoiceAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  }
  if (f.dueDate) found.push(`due ${f.dueDate}`);
  if (f.netTerms) found.push(f.netTerms);
  if (!found.length) {
    return (
      <div style={row}>
        <FileText size={14} style={{ marginTop: 1 }} />
        <div>Could not read anything off <strong>{scan.filename}</strong>. Fill it in by hand.</div>
        {dismissBtn}
      </div>
    );
  }
  if (scan.applied) {
    return (
      <div style={{ ...row, background: '#e8f5ec', borderColor: '#b7e0c6', color: '#1c7c43' }}>
        <CheckCircle2 size={14} style={{ marginTop: 1 }} />
        <div>
          Filled in from <strong>{scan.filename}</strong>. Check the fields below and edit anything the reader got wrong.
          {vendorNote}
        </div>
        {dismissBtn}
      </div>
    );
  }
  return (
    <div style={row}>
      <FileText size={14} style={{ marginTop: 1 }} />
      <div style={{ flex: 1 }}>
        <div style={{ marginBottom: 6 }}>
          Read from <strong>{scan.filename}</strong>: {found.join(' · ')}.
          {vendorNote}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={onApply}>
            Fill in the blanks
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={onDismiss}>
            No thanks
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── paid by check at pickup ──────────────────────────────────────────────────
// A few crabbers will not let a load leave without a check, so the driver carries
// one and the bill arrives already settled. Mercedes, 2026-08-25: it "still needs
// to follow the exact same flow as every other bill... it just needs to carry a
// clear already paid indicator the whole way through", because "if that indicator
// isn't obvious when the bill gets kicked to me, I'll pay it a second time".
//
// So it is a flag on the document, never a status, and it is shown in three places
// on purpose: a pill on every queue row, a banner at the top of the bill, and the
// check details themselves down in the invoice card - the screen equivalent of the
// check copy that used to be stapled to the front of the invoice.

// ⚠️ NEITHER YIELD DOCUMENT IS AN INVOICE. The yield sheet CPx renders and the
// yield report that was emailed round are both documentation of what the crabs did;
// the invoice is what the crabber is charging us. Counting either as "there is a
// scan on this bill" is exactly how one reached final approval with no invoice on
// it, so the test lives here once and every gate uses it.
const isInvoiceDoc = (f) => f && f.kind !== 'yield-sheet' && f.kind !== 'yield-report';

// ⚠️ THE SAME READING AS `apDb.fileRole` ON THE SERVER, and it is here only to
// LABEL a row - the server decides the order. If these two ever disagree the
// screen shows a wrong caption; if the server were left out of it, the combined
// PDF would come out in a different order from the list above it, which is worse.
const HAULER_RE = new RegExp([
  'transport(ation)?[ _-]*(inc|llc|co|company)',
  'truck(ing)?[ _-]*(inc|llc|co|company)?',
  'haul(ing|er)', 'freight', 'logistics', 'carrier[ _-]*(inc|llc|co)?',
].join('|'), 'i');
const fileRole = (f) => {
  const kind = String((f && f.kind) || '').toLowerCase();
  const name = String((f && f.filename) || (f && f.r2_key) || '');
  if (kind === 'yield-report' || kind === 'yield-sheet') return 'yield';
  // ⚠️ A FILE LITERALLY CALLED Yield_... IS A YIELD REPORT WHATEVER ITS KIND SAYS.
  // Matt Sr saved one out of his mail and dragged it on by hand, so it was stored as
  // a plain 'invoice' and sorted above the actual crabber invoice. Reading the name
  // costs nothing and the name here is generated by the yield tool, not typed.
  if (/^yield[_ -]/i.test(name.replace(/^.*[\/]/, ''))) return 'yield';
  if (kind === 'freight-invoice' || HAULER_RE.test(name)) return 'hauler';
  if (kind === 'invoice' || kind === 'vendor-invoice') return 'invoice';
  return 'other';
};
const ROLE_LABEL = {
  invoice: 'Invoice', yield: 'Yield report', hauler: 'Hauler', other: 'Not identified',
};
const ROLE_TONE = {
  invoice: { bg: '#eef4fa', fg: '#00395C', bd: '#c9dcee' },
  yield:   { bg: '#e8f5ec', fg: '#1c7c43', bd: '#b7e0c6' },
  hauler:  { bg: '#fff3e6', fg: '#9a4a1c', bd: '#f6d4b8' },
  other:   { bg: '#f3f4f6', fg: '#6b7280', bd: '#e5e7eb' },
};

const isPrepaid = (r) => !!(r && (r.paid_at_pickup === true || r.paid_at_pickup === 't'));

// One trip can carry two bills: the crabber's and the hauler's. They run the same
// chain and stay tied to the same trip, so the only thing that tells them apart on
// a list is this - and paying the wrong one is the mistake it prevents.
const isFreight = (r) => !!(r && r.bill_kind === 'freight');
// Everything that is not a crabber or a hauler: shrimp, coolers, utilities, supplies.
// It lives on its own page (Invoice Approvals), has no trip and no yield, and the person
// entering it approves it first. See lib/ap-db.js BILL_KINDS.
const isInvoice = (r) => !!(r && r.bill_kind === 'invoice');

function FreightPill({ row, small }) {
  if (!isFreight(row)) return null;
  return (
    <span style={{
      background: '#eef4fa', color: 'var(--navy, #00395C)', border: '1px solid #c9dcee',
      fontSize: small ? 9.5 : 10.5, fontWeight: 800, letterSpacing: '0.4px',
      padding: small ? '2px 6px' : '3px 8px', borderRadius: 999,
      whiteSpace: 'nowrap', marginLeft: 8, textTransform: 'uppercase',
    }}>Freight</span>
  );
}

// The check, in the words a person would say them. Returns '' when nobody filled
// any of it in, which is allowed: the flag is what stops a second payment, the
// detail is what lets Mercedes match it to the bank.
const checkLine = (doc) => {
  const bits = [];
  if (doc.check_number) bits.push(`check #${String(doc.check_number).trim()}`);
  if (doc.check_amount != null && doc.check_amount !== '') bits.push(`for ${money(doc.check_amount)}`);
  if (doc.check_payee) bits.push(`to ${String(doc.check_payee).trim()}`);
  if (doc.check_date) bits.push(`written ${fmtDate(doc.check_date)}`);
  return bits.join(' · ');
};

// The check written against what the chain actually approved. A ticket cut after
// the check was handed over means we overpaid the crabber, and nothing else on
// this screen would say so.
const checkGap = (doc) => {
  if (!isPrepaid(doc)) return null;
  const paid = doc.check_amount == null || doc.check_amount === '' ? null : Number(doc.check_amount);
  const owed = doc.approved_amount == null ? (doc.invoice_amount == null ? null : Number(doc.invoice_amount))
    : Number(doc.approved_amount);
  if (paid == null || owed == null || !isFinite(paid) || !isFinite(owed)) return null;
  const diff = Math.round((paid - owed) * 100) / 100;
  return Math.abs(diff) <= 0.01 ? null : diff;
};

// Who this bill pays. ⚠️ ON A FREIGHT ROW THAT IS THE HAULER, NOT THE CRABBER, and
// a blank one says so out loud: an em dash reads as a formatting gap, when in fact
// it is the single thing standing between a hauler and being paid.
// ⚠️ AN ATTACHMENT DRAGGED STRAIGHT OUT OF OUTLOOK IS NOT AN ORDINARY FILE DROP.
// The bytes do not exist on disk yet, so Windows offers the browser a "virtual file":
// dataTransfer.files is empty AND item.getAsFile() returns null. The old handler read
// that as "this is not a file" and told Mercedes to save it to her computer first,
// which is the exact round trip she was trying to avoid. webkitGetAsEntry() is the
// one call that resolves a virtual file into real bytes.
// ⚠️ EVERYTHING IS READ OUT OF THE DataTransfer SYNCHRONOUSLY. The browser empties it
// the instant the drop handler returns, so a single await before the read leaves you
// holding an empty list - the entries are grabbed first and resolved afterwards.
function filesFromDataTransfer(dt) {
  if (!dt) return Promise.resolve([]);
  if (dt.files && dt.files.length) return Promise.resolve(Array.from(dt.files));
  const plain = [];
  const entries = [];
  for (const it of Array.from(dt.items || [])) {
    if (it.kind !== 'file') continue;
    const f = it.getAsFile && it.getAsFile();
    if (f) { plain.push(f); continue; }
    const entry = it.webkitGetAsEntry && it.webkitGetAsEntry();
    if (entry && entry.isFile) entries.push(entry);
  }
  if (plain.length) return Promise.resolve(plain);
  return Promise.all(entries.map((en) => new Promise((res) => {
    try { en.file(res, () => res(null)); } catch (_) { res(null); }
  }))).then((list) => list.filter(Boolean));
}

// The same question asked of a paste. Copying an attachment in Outlook puts the real
// file on the Windows clipboard, which the browser can always read.
function filesFromClipboard(cd) {
  if (!cd) return [];
  if (cd.files && cd.files.length) return Array.from(cd.files);
  return Array.from(cd.items || [])
    .filter((i) => i.kind === 'file')
    .map((i) => i.getAsFile())
    .filter(Boolean);
}

function PartyCell({ row, withPdfs, onChanged }) {
  return (
    <>
      {row.vendor_name
        ? <strong>{row.vendor_name}</strong>
        : isFreight(row)
          ? <em style={{ color: 'var(--orange, #DC582A)' }}>hauler not filled in</em>
          : <span>—</span>}
      <PaidPill row={row} small />
      <FreightPill row={row} small />
      {/* Only on the queue somebody works down. On the archive and the in-flight
          list it is one more thing between the eye and the vendor name. */}
      {withPdfs && (
        <PdfPill documentId={row.id}
          needsYield={!!row.purchase_order_id && row.has_yield_report === false}
          freight={isFreight(row)}
          onChanged={onChanged} />
      )}
    </>
  );
}

const PAID_METHOD_LABELS = {
  capone_sparks: 'Capital One Sparks', amex_plum: 'Amex Plum', cnc_amex: 'Crab & Cruise Amex',
  other_card: 'other company card', truist_debit: 'Truist debit', check: 'check', other: 'other',
};
const paidMethodLabel = (v) => PAID_METHOD_LABELS[v] || v || '';

function PaidPill({ row, small }) {
  if (!isPrepaid(row)) return null;
  return (
    <span style={{
      background: '#e8f5ec', color: '#1c7c43', border: '1px solid #b7e0c6',
      fontSize: small ? 9.5 : 10.5, fontWeight: 800, letterSpacing: '0.4px',
      padding: small ? '2px 6px' : '3px 8px', borderRadius: 999,
      whiteSpace: 'nowrap', marginLeft: 8, textTransform: 'uppercase',
    }}>{row.paid_method ? 'Already paid' : 'Paid at pickup'}</span>
  );
}

// The loud one, at the top of the bill. Deliberately not a Banner: it has to win
// against the aging and send-back banners it sits beside, because this is the one
// that stops a bill being paid twice.
function PaidAtPickupBanner({ doc, forApprover }) {
  if (!isPrepaid(doc)) return null;
  const detail = checkLine(doc);
  const gap = checkGap(doc);
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        background: '#e8f5ec', border: '2px solid #1c7c43', borderRadius: 6,
        padding: '12px 16px', color: '#14532d', lineHeight: 1.5,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800,
          fontSize: 14, letterSpacing: '0.3px', textTransform: 'uppercase',
        }}>
          <DollarSign size={16} /> Already paid — do not pay this again
        </div>
        <div style={{ fontSize: 13, marginTop: 6 }}>
          {doc.paid_method ? (
            <>
              Paid when it was bought, with <strong>{paidMethodLabel(doc.paid_method)}</strong>
              {doc.paid_method === 'check' && doc.check_number ? <> #{String(doc.check_number).trim()}</> : null}
              {doc.check_date ? <> on <strong>{fmtDate(doc.check_date)}</strong></> : null}.
              {' '}Sending it to QuickBooks enters it as a charge on that card (or a check), not as a bill to pay.
            </>
          ) : (
            <>
              The driver took a check with him and paid the crabber at pickup
              {detail ? <>: <strong>{detail}</strong></> : '.'}
            </>
          )}
          {forApprover
            ? ' You are okaying the yield and the amount here, not releasing a payment.'
            : ' It still runs the whole approval chain, so it is here to be checked and entered, not paid.'}
        </div>
        {gap != null && (
          <div style={{ fontSize: 13, marginTop: 8, color: '#b42318', fontWeight: 700 }}>
            {gap > 0
              ? `The check was ${money(Math.abs(gap))} MORE than the approved amount, so the crabber owes us that back.`
              : `The check was ${money(Math.abs(gap))} LESS than the approved amount, so there is a balance still owed.`}
          </div>
        )}
      </div>
    </div>
  );
}

// The check copy, stapled to the front of the invoice. Read-only view; the entry
// form is in the invoice card above it.
function CheckCopy({ doc }) {
  if (!isPrepaid(doc)) return null;
  const rows = [
    ['Check #', doc.check_number || '—'],
    ['Amount', doc.check_amount == null || doc.check_amount === '' ? '—' : money(doc.check_amount)],
    ['Written', doc.check_date ? fmtDate(doc.check_date) : '—'],
    ['Payable to', doc.check_payee || doc.vendor_name || '—'],
  ];
  return (
    <div style={{
      marginTop: 14, border: '1px dashed #1c7c43', borderRadius: 6,
      padding: 14, background: '#f4fbf6',
    }}>
      <div style={{ ...labelStyle, color: '#1c7c43', marginBottom: 10 }}>
        The check that went with the driver
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 26 }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ minWidth: 120 }}>
            <div style={labelStyle}>{k}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#14532d' }}>{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── the yield match ──────────────────────────────────────────────────────────

function TripPicker({ onPick, onCancel, onOpenBill }) {
  const [search, setSearch] = useState('');
  const [days, setDays] = useState(45);
  const [rows, setRows] = useState([]);
  // A trip whose bill is already in flight is not something to start, so it comes
  // OUT of this list rather than sitting in it greyed out. Mercedes, 2026-08-31:
  // "it's a long list of rows nobody should be touching, and it makes it hard to
  // tell what's actually left to do." They are still reachable behind the toggle,
  // because hiding them completely makes a trip you cannot find look like a trip
  // that does not exist. A rejected or deleted bill already stops counting as a
  // link (the `linked` CTE excludes both), so those trips come back on their own.
  const [showStarted, setShowStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async (term, d) => {
    setLoading(true); setErr('');
    try {
      const r = await neonQuery('ap_yield_candidates', { search: term || undefined, days: d, limit: 80 });
      setRows(r.candidates || []);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);

  // Debounced so typing a crabber's name does not fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => load(search, days), 250);
    return () => clearTimeout(t);
  }, [search, days, load]);

  const startedCount = rows.filter((r) => r.linkedDocumentId).length;
  // ⚠️ A TRIP HELD BY A BILL NOBODY EVER SENT IS NOT "ALREADY ON AN APPROVAL", and
  // showing it as that is what hid Yankee Canal 33671 for eight days. It stays out
  // of the ready list (one trip, one bill - starting a second would orphan the scans
  // already attached to the first), but it is NOT greyed dead: it is shown with what
  // is actually wrong with it and clicking it opens the unfinished bill so it can be
  // finished and sent. Mercedes asked for it "back in the active list... so it can go
  // through the process normally", and the bill already carrying the paperwork IS that
  // bill - a fresh one would leave her a second empty approval and lose Matt's scans.
  const stuck = rows.filter((r) => r.linkedDocumentId && r.linkedSent === false);
  // ⚠️ THE SAME LOAD, ENTERED TWICE UPSTREAM, AND ALREADY PAID ON THE OTHER COPY.
  // Not work, and not something anybody here can delete - the trip is a mirror of
  // the yield system and comes back every fifteen minutes. It comes out of the
  // ready list and says what it is, which is the whole of what Matt Sr needed.
  const dupes = rows.filter((r) => !r.linkedDocumentId && r.twinDocumentId);
  // ⚠️ THE UNSENT-DRAFT CLAUSE MUST BE TIED TO ACTUALLY HAVING A BILL. `linkedSent`
  // is built as `linked_sent === true`, so it is FALSE on every trip that has no bill
  // at all - which means a bare `|| r.linkedSent === false` re-admits everything and
  // silently cancels whatever the first clause excluded. It read correctly while the
  // first clause was just `!r.linkedDocumentId` (the two agreed on those rows), and
  // broke the moment duplicates had to be taken out. Caught by the test, not by
  // reading it: the payload looked right, the list did not change.
  const visible = showStarted ? rows
    : rows.filter((r) => (!r.linkedDocumentId && !r.twinDocumentId)
                         || (r.linkedDocumentId && r.linkedSent === false));

  return (
    <div style={cardBody}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--gray-400, #9ca3af)' }} />
          <input
            style={{ ...inputStyle, paddingLeft: 30 }}
            placeholder="Crabber name, invoice number or trip number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <select style={{ ...inputStyle, width: 'auto' }} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={14}>Last 2 weeks</option>
          <option value={45}>Last 45 days</option>
          <option value={120}>Last 4 months</option>
          <option value={365}>Last year</option>
        </select>
        {onCancel && (
          <button className="btn btn-ghost" onClick={onCancel} style={{ fontSize: 13 }}>Cancel</button>
        )}
      </div>

      {err && <Banner tone="bad" icon={<AlertTriangle size={15} />}>{err}</Banner>}
      {loading && <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>Looking for trips…</div>}
      {!loading && !visible.length && (
        <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>
          {startedCount && !showStarted
            ? `Nothing left to start in that window. All ${startedCount} ${startedCount === 1 ? 'trip is' : 'trips are'} already on an approval.`
            : 'No trips found in that window. Widen the date range or check the spelling.'}
        </div>
      )}

      {/* Matt Sr and Mercedes made and deleted a bill on these four times in one
          morning trying to get rid of them. Saying it in words is the fix. */}
      {!loading && dupes.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <Banner tone="info" icon={<AlertTriangle size={15} />}>
            {dupes.length === 1 ? 'One load was' : `${dupes.length} loads were`} entered
            twice in the yield system, and the crabber has already been paid on the
            other copy, so {dupes.length === 1 ? 'it is' : 'they are'} out of this list.
            {' '}Nothing here needs doing, and deleting a bill will not clear
            {' '}{dupes.length === 1 ? 'it' : 'them'} - the duplicate has to be voided
            {' '}in the yield system.
          </Banner>
        </div>
      )}

      {/* Said out loud, above the list, because the row on its own reads as an
          ordinary blocked trip and this is a crabber waiting to be paid. */}
      {!loading && stuck.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <Banner tone="warn" icon={<AlertTriangle size={15} />}>
            {stuck.length === 1 ? 'One trip has a bill' : `${stuck.length} trips have a bill`}
            {' '}that was started and never sent, so nobody is being asked to approve
            {' '}{stuck.length === 1 ? 'it' : 'them'} and the crabber has not been paid.
            {' '}Open {stuck.length === 1 ? 'it' : 'them'} below to finish and send.
          </Banner>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid var(--gray-100, #f3f4f6)', borderRadius: 6 }}>
          <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50, #f9fafb)', position: 'sticky', top: 0 }}>
                {/* ⚠️ THE DEAD COUNT WAS THE WRONG COLUMN TO SPEND SPACE ON. It is
                    on the bill itself once a trip is picked; what you cannot see from
                    a list is whether Montel has finished. Matt Jr: "that tells MJ and
                    Sr that a yield has been started, but not whether Montel has
                    finished and finalized it. The only way to know is to click into
                    each one." */}
                {['Received', 'Crabber', 'Their invoice #', 'Received', 'Landed cost', 'Yield', ''].map((h, i) => (
                  <th key={i} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--gray-500, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                // Held by a bill that is genuinely moving. Not startable, not openable.
                const taken = !!r.linkedDocumentId && r.linkedSent !== false;
                // Held by a bill nobody sent. Openable - that bill is the work.
                const unsent = !!r.linkedDocumentId && r.linkedSent === false;
                // The same load on another trip row, already billed there.
                const dupe = !r.linkedDocumentId && !!r.twinDocumentId;
                return (
                  <tr key={r.poId}
                    style={{
                      borderTop: '1px solid var(--gray-100, #f3f4f6)',
                      opacity: taken || dupe ? 0.5 : 1,
                      cursor: taken ? 'not-allowed' : 'pointer',
                      background: unsent ? '#fff8f0' : 'transparent',
                    }}
                    onClick={() => {
                      if (taken) return;
                      // Opens the bill the load was actually paid on, so the answer
                      // to "is this really a duplicate" is one click rather than a
                      // hunt through the archive.
                      if (dupe) onOpenBill(r.twinDocumentId);
                      else if (unsent) onOpenBill(r.linkedDocumentId);
                      else onPick(r.poId);
                    }}
                  >
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{fmtDate(r.dateReceived)}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>{r.supplier || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{r.poInvoiceNumber || '—'}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      {r.unitsReceived == null ? '—' : `${num(r.unitsReceived, 1)} ${r.unit}`}
                    </td>
                    <td style={{ padding: '8px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>{money(r.landedTotal)}</td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      {r.finalized ? (
                        <span style={{ color: '#1c7c43', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <Check size={13} /> Finalized
                        </span>
                      ) : (
                        <span style={{ color: '#9a4a1c', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <Clock size={12} /> still being entered
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 11, color: 'var(--gray-500, #6b7280)' }}>
                      {unsent ? (
                        <span style={{ color: '#9a4a1c', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          started, never sent - open #{r.linkedDocumentId}
                        </span>
                      ) : dupe ? (
                        <span style={{ color: 'var(--gray-500, #6b7280)', whiteSpace: 'nowrap' }}>
                          same load as trip {r.twinTripId}, paid on #{r.twinDocumentId}
                        </span>
                      ) : taken ? `on approval #${r.linkedDocumentId}` : <ChevronRight size={14} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {startedCount > 0 && (
        <button
          className="btn btn-ghost"
          onClick={() => setShowStarted((v) => !v)}
          style={{ marginTop: 10, fontSize: 12 }}
        >
          {showStarted
            ? `Hide the ${startedCount} already on an approval`
            : `Show ${startedCount} already on an approval`}
        </button>
      )}
      <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
        Landed cost is the crabs plus the freight on that load. <strong>Finalized</strong> means
        Montel has closed the yield sheet, so the numbers on it are final; the rest are still
        being entered and can still move. Trips already attached to another approval are greyed
        out so one trip cannot be paid twice.
      </div>
    </div>
  );
}

// ⚠️ THE BANDS AND THE MATHS BOTH COME FROM THE YIELD REPORT, deliberately. A trip
// that reads 70% and red on Inventory > Yield Report has to read 70% and red here,
// or the screen that approves the bill and the report that measures the crabber are
// two opinions about the same load. Yield % is red under 90 and green over 100; the
// return is red under 80 and green over 110, which is that report's Retail Ratio.
const bandColor = (v, low, high) => (v == null ? 'var(--navy, #00395C)'
  : v < low ? '#b42318' : v > high ? '#1c7c43' : 'var(--navy, #00395C)');

function Stat({ label, value, color, sub }) {
  return (
    <div style={{ minWidth: 150 }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15, color: color || 'var(--navy, #00395C)' }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// What the crabs actually yielded and what that is worth, on the approval screen
// itself. Matt Sr, 2026-08-24: "I don't see the yield sheet/results... I just need
// to see what they yielded and roi% in order to okay it for payment." Leaving this
// screen to look it up in the Yield Report is what a one-stop approval view exists
// to prevent.
// How long ago CPx last read the yield sheet. ⚠️ THIS IS ON THE SCREEN BECAUSE OF
// A REAL FALSE ALARM: the J&T bill showed an 80.3% return at 17:41 and 86.0% at
// 17:45 - same arithmetic, four minutes apart, because Montel was still entering
// the sheet and the mirror only refreshes every 15 minutes. Matt Sr reasonably read
// that as CPx disagreeing with the report everyone else gets. A figure nobody can
// date is a figure nobody can defend.
const ago = (v) => {
  if (!v) return null;
  const t = new Date(String(v).includes('T') || String(v).includes('+') ? v : String(v).replace(' ', 'T') + 'Z');
  if (isNaN(t)) return null;
  const mins = Math.max(0, Math.round((Date.now() - t.getTime()) / 60000));
  const when = t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (mins < 1) return { text: 'just now', mins, when };
  if (mins < 60) return { text: `${mins} min ago`, mins, when };
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return { text: `${hrs} ${hrs === 1 ? 'hour' : 'hours'} ago`, mins, when };
  return { text: t.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), mins, when };
};

// ⚠️ THE SCREEN AND THE PDF ARE PRICED ON DIFFERENT DAYS, AND THAT IS THE WHOLE
// EXPLANATION. Matt Sr, 2026-09-02, on Ford’s 2811: the approval read +116.5% and the
// yield report attached to the same bill read 125.61%, and he took it for a broken
// calculation. Nothing is broken - same formula (retail over landed cost, minus 100),
// same $4,200, same bushels, grade for grade. The report values the load at the retail
// list in force the day it was mailed and then never changes; this screen values it at
// today’s list, which is also what production does when anyone reopens the yield sheet.
// Between 28 Aug and 2 Sep three prices came down and that is every point of the gap.
// So the fix is not to change a number - changing either one would make it wrong - it is
// to say out loud which day each is speaking for.
//
// Returns the grades whose price has moved since the report went out, but ONLY where the
// ledger actually reaches back past that date for that grade. A grade whose earliest
// recorded price is AFTER the report is a grade we cannot speak for, and saying nothing
// about it is the honest answer; guessing it had not moved would invent a fact.
function priceMovesSince(results, since) {
  if (!since || !results || !Array.isArray(results.priceHistory) || !results.priceHistory.length) {
    return null;
  }
  const cut = new Date(since);
  if (isNaN(cut)) return null;
  const byGrade = new Map();
  for (const h of results.priceHistory) {
    if (!byGrade.has(h.gradedId)) byGrade.set(h.gradedId, []);
    byGrade.get(h.gradedId).push(h);
  }
  const moves = [];
  let retailDelta = 0;
  for (const g of (results.grades || [])) {
    const qty = Number(g.tally || 0) + Number(g.live || 0);
    if (!(qty > 0)) continue;
    const rows = (byGrade.get(String(g.gradedId)) || [])
      .slice().sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt));
    const thenRow = rows.find(r => new Date(r.changedAt) <= cut);
    if (!thenRow) continue;               // ledger does not reach that far back
    const then = Number(thenRow.price);
    const now = Number(g.price);
    if (!isFinite(then) || !isFinite(now) || then === now) continue;
    moves.push({ name: g.name, then, now });
    retailDelta += qty * (now - then);
  }
  if (!moves.length) return null;
  return { moves, retailDelta: Math.round(retailDelta * 100) / 100 };
}

function YieldResults({ results, po, onRefresh, refreshing, reportEmailedAt }) {
  const [showGrades, setShowGrades] = useState(false);
  if (!results || !results.recorded) {
    return (
      <div style={{ marginTop: 14 }}>
        <Banner tone="info">
          {results && results.yieldSheetId
            ? 'A yield sheet was started for this trip but nothing has been yielded on it yet, so there are no results to weigh the bill against.'
            : 'These crabs have not been cooked and graded yet, so there is no yield to weigh the bill against.'}
        </Banner>
      </div>
    );
  }

  const priced = results.retailPriced;
  const grades = (results.grades || []).filter(g => g.tally > 0 || g.live > 0);
  const male = grades.filter(g => g.type === 'male');
  const female = grades.filter(g => g.type === 'female');

  return (
    <div style={{
      marginTop: 14, border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 6, padding: 14,
    }}>
      <div style={{ ...labelStyle, marginBottom: 12 }}>What it yielded</div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28 }}>
        <Stat
          label="Yield"
          value={`${num(results.yieldUnits, 2)} bu`}
          sub={results.boughtUnits ? `from ${num(results.boughtUnits, 2)} bu bought` : null}
        />
        <Stat
          label="Yield %"
          value={results.yieldPct == null ? '—' : `${num(results.yieldPct, 1)}%`}
          color={bandColor(results.yieldPct, 90, 100)}
          sub={`male ${num(results.maleUnits, 2)} bu · female ${num(results.femaleUnits, 2)} bu`}
        />
        <Stat
          label="Retail value"
          value={priced ? money(results.totalRetail) : '—'}
          sub={!priced ? 'no retail price set on these grades'
            : results.pricedAsOf
              ? `at the retail prices on ${fmtDate(results.pricedAsOf)}`
              : 'at today’s retail prices'}
        />
        <Stat
          label="Return on the load"
          value={results.roiPct == null ? '—' : `${results.roiPct > 0 ? '+' : ''}${num(results.roiPct, 1)}%`}
          color={bandColor(results.roiPct, 80, 110)}
          sub={results.roiPct == null ? null
            : `${money(results.landedTotal)} landed → ${money(results.totalRetail)} retail`}
        />
      </div>

      <div style={{
        marginTop: 12, display: 'flex', alignItems: 'center', gap: 10,
        flexWrap: 'wrap', fontSize: 11.5, color: 'var(--gray-500, #6b7280)',
      }}>
        {/* ⚠️ FINALIZED BEATS FRESHNESS. Matt Jr, on the 80% vs 86%: "a visible
            finalized flag prevents that instead of relying on the timestamp to catch
            it after the fact." A recent read of an unfinished sheet is still an
            unfinished sheet, and an old read of a closed one is final. So the flag
            leads and the timestamp only qualifies it. */}
        {(() => {
          const a = ago(results.syncedAt);
          if (!po.finalized) {
            return (
              <span style={{ color: '#b42318', fontWeight: 600 }}>
                Montel has not finalized this yield sheet yet, so everything above can
                still change{a ? ` (read at ${a.when})` : ''}.
              </span>
            );
          }
          if (!a) return <span>Finalized. Read from the yield sheet on the last sync.</span>;
          return (
            <span>
              <strong style={{ color: '#1c7c43' }}>Finalized</strong>, so these are the
              final numbers. Read at {a.when} ({a.text}).
            </span>
          );
        })()}
        {onRefresh && (
          <button className="btn btn-ghost" style={{ fontSize: 11.5, padding: '2px 8px', textDecoration: 'underline' }}
            disabled={refreshing} onClick={onRefresh}>
            {refreshing ? 'Reading the yield sheet…' : 'Read it again now'}
          </button>
        )}
      </div>

      <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
        Cooked {po.dateCooked ? fmtDate(po.dateCooked) : 'date not recorded'}. Same figures as
        Inventory &rsaquo; Yield Report: the return is retail against landed cost, red under 80%
        and green over 110%; yield is red under 90% and green over 100%.
      </div>

      {/* Which day these prices are from, and why the PDF on this bill can disagree. */}
      {priced && (
        <div style={{
          marginTop: 8, fontSize: 11.5, color: 'var(--gray-600, #4b5563)',
          background: '#f7fafc', border: '1px solid var(--gray-200, #e5e7eb)',
          borderRadius: 5, padding: '8px 10px',
        }}>
          {/* ⚠️ THE NUMBER IS LOCKED TO THE DAY THE YIELD WAS COOKED. Matt Sr,
              2026-09-18: "Prices should be locked in at the time the yield was done."
              Until today this panel showed today's prices and EXPLAINED the gap to the
              PDF; now it closes the gap instead, so the wording has to change with it -
              a caveat describing a divergence that no longer exists is its own lie. */}
          {results.pricedAsOf ? (
            <>Retail is priced at the master list as it stood on{' '}
              {fmtDate(results.pricedAsOf)}, the day this load was cooked, so it does not
              move when prices move afterwards.</>
          ) : (
            <>Retail is priced at the master list <strong>as it stands now</strong>, because
              we hold no price list reaching back to the day this load was cooked. The
              yield report PDF that was emailed at the time is the record of that day's
              number.</>
          )}
          {results.pricedAsOf ? null : reportEmailedAt ? (
            <> The yield report on this bill was priced when it was emailed on{' '}
              {fmtDate(reportEmailedAt)}, so its Retail Ratio is that day’s prices and this
              return is today’s. Both are right, and they part company as soon as a price
              moves.</>
          ) : (
            <> A yield report PDF is priced on the day it was emailed, so an older one shows
              a different return on the same crabs.</>
          )}
          {(() => {
            const m = priceMovesSince(results, reportEmailedAt);
            if (!m) return null;
            return (
              <> Since then {m.moves.map((x, i) => (
                <span key={x.name}>
                  {i > 0 ? (i === m.moves.length - 1 ? ' and ' : ', ') : ''}
                  <strong>{x.name}</strong> {money(x.then)} → {money(x.now)}
                </span>
              ))}, worth {money(Math.abs(m.retailDelta))} of retail
              {results.landedTotal > 0 && (
                <> ({num(Math.abs(m.retailDelta) / results.landedTotal * 100, 1)} points)</>
              )}.</>
            );
          })()}
          {' '}The price used for each grade is in the breakdown below.
        </div>
      )}

      {grades.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            onClick={() => setShowGrades(g => !g)}
          >
            {showGrades ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            What it graded out to ({grades.length} {grades.length === 1 ? 'grade' : 'grades'})
          </button>
          {showGrades && (
            <div style={{
              marginTop: 8, display: 'grid', gap: 16,
              gridTemplateColumns: male.length && female.length ? 'repeat(auto-fit, minmax(260px, 1fr))' : '1fr',
            }}>
              {[{ label: 'Male', rows: male }, { label: 'Female', rows: female }]
                .filter(g => g.rows.length > 0)
                .map(({ label, rows }) => (
                  <div key={label}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 6 }}>{label}</div>
                    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          {['Grade', 'Bushels', 'Live', 'Each', 'Worth'].map(h => (
                            <th key={h} style={{
                              textAlign: h === 'Grade' ? 'left' : 'right', padding: '4px 6px',
                              fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.4px',
                              color: 'var(--gray-500, #6b7280)', borderBottom: '1px solid var(--gray-200, #e5e7eb)',
                            }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((g, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid var(--gray-100, #f3f4f6)' }}>
                            <td style={{ padding: '5px 6px' }}>{g.name}</td>
                            <td style={{ padding: '5px 6px', textAlign: 'right' }}>{num(g.tally, 2)}</td>
                            <td style={{ padding: '5px 6px', textAlign: 'right' }}>{g.live > 0 ? num(g.live, 2) : '—'}</td>
                            <td style={{ padding: '5px 6px', textAlign: 'right' }}>{g.price > 0 ? money(g.price) : '—'}</td>
                            <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 600 }}>
                              {g.value > 0 ? money(g.value) : '—'}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={4} style={{ padding: '5px 6px', textAlign: 'right', fontSize: 11, color: 'var(--gray-500, #6b7280)' }}>
                            {label} retail
                          </td>
                          <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 700 }}>
                            {money(label === 'Male' ? results.maleRetail : results.femaleRetail)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── the crabber loads a hauler's invoice covers ────────────────────────
// Mercedes, 2026-09-10: "When a freight bill comes up for approval (White Marsh,
// for example), there's no way to see which crabber loads that freight actually
// covers. The approver is looking at a dollar amount with nothing to check it
// against... This is how we catch being overcharged. Without it, we're approving
// freight on trust."
//
// On paper it was a stapled packet - every crabber invoice first, the hauler's bill
// last - and you worked down it confirming each crabber on the freight bill was
// accounted for and that the hauler had billed per box in line with what came off
// those loads. This card is that packet.
//
// ⚠️ IT PROPOSES CANDIDATES AND THE PERSON DECIDES. Picking the loads by matching
// the money is the obvious build and it does not work: run over all nine live
// freight bills, subset-sum lands on ONE answer twice, on several answers five
// times (348768A at $744 has nine different combinations of that day's loads that
// all total $744) and on none twice. So CPx gathers and totals; a human ticks. A
// guessed set of loads on a bill about to be paid looks authoritative and is wrong.
function FreightLoads({ doc, canEdit, onOpenDocument }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(null);
  const [back, setBack] = useState(7);
  const [hideClaimedLoads, setHideClaimedLoads] = useState(true);
  // Crab & Cruise bills from vendors we have never paid freight on are left off by
  // default (the Maryland crabbers deliver their own). This brings them back for a
  // new LA crabber whose first freight bill this is.
  const [allCnc, setAllCnc] = useState(false);
  // C&C loads already ticked on a different freight bill are hidden by default,
  // matching hideClaimedLoads on the regular loads above.
  const [hideCncClaimed, setHideCncClaimed] = useState(true);

  // ⚠️ MUST DEPEND ON invoice_date AND invoice_amount, not just doc.id. Mercedes,
  // 2026-09-18: opened a freight bill, typed 09/15/2026 in, and the loads section
  // stayed on "Put the hauler's invoice date on this bill" as if nothing had been
  // entered. Root cause: the server reads the date off ap_documents, so the query
  // rides the autosave, but this useEffect only re-fired on doc.id - which does
  // not change when a field autosaves. So the very first fetch was made before the
  // date reached the DB (needsDate:true) and the "loads" card sat frozen until a
  // page reload. Same trap for invoice_amount: "The hauler billed" and the
  // difference reads off it, so a fresh amount without a refresh would go stale.
  const load = useCallback((daysBack) => {
    setLoading(true); setErr('');
    return neonQuery('ap_freight_loads', { documentId: doc.id, daysBack, allCnc })
      .then((r) => { setData(r); setLoading(false); })
      .catch((e) => { setErr(e.message); setLoading(false); });
  }, [doc.id, doc.invoice_date, doc.invoice_amount, allCnc]);

  useEffect(() => { load(back); }, [load, back]);

  const tick = async (poId, checked) => {
    setSaving(poId);
    try {
      await neonQuery('ap_freight_load_tick', {
        documentId: doc.id, purchaseOrderId: poId, checked });
      await load(back);
    } catch (e) { setErr(e.message); }
    setSaving(null);
  };

  const tickCnc = async (cncId, checked) => {
    setSaving('cnc-' + cncId);
    try {
      await neonQuery('ap_freight_load_tick', {
        documentId: doc.id, cncDocumentId: cncId, checked });
      await load(back);
    } catch (e) { setErr(e.message); }
    setSaving(null);
  };

  if (loading && !data) return <div style={cardBody}>Looking for the loads…</div>;
  if (err) return (
    <div style={cardBody}>
      <Banner tone="bad" icon={<AlertTriangle size={15} />}>{err}</Banner>
    </div>
  );
  if (!data) return null;

  const { loads = [], totals, rate, rateSample, needsDate, strayTicks = [],
    cncLoads = [], cncHidden = 0, cncStrayTicks = [] } = data;

  if (needsDate) return (
    <div style={cardBody}>
      <Banner tone="info" icon={<Truck size={15} />}>
        Put the hauler's invoice date on this bill and the loads from around that
        date will be listed here to check off.
      </Banner>
    </div>
  );

  const picked = loads.filter(l => l.checked);
  const cncPicked = cncLoads.filter(l => l.checked);
  const cncDoubleClaimed = cncPicked.filter(l => l.claimedByDoc);
  const diff = totals ? totals.difference : null;
  // Only once something is ticked does a difference mean anything. With nothing
  // ticked the difference is just the whole invoice, which is not a discrepancy.
  const settled = picked.length > 0 && diff != null && Math.abs(diff) <= 0.01;
  // ⚠️ A TICKED CRAB & CRUISE LOAD MAKES "OVER" EXPECTED, NOT A WARNING. Those loads
  // have no freight recorded anywhere in CPx (no trip, no transportation cost), so
  // whatever part of the bill they account for shows up as a gap. Mercedes asked for
  // the money check to stay honest by saying that out loud rather than going green
  // or going red - see the C&C banner below.
  const over = picked.length > 0 && cncPicked.length === 0 && diff != null && diff > 0.01;
  const under = picked.length > 0 && diff != null && diff < -0.01;
  const offRate = loads.filter(l => l.offRate);
  const doubleClaimed = picked.filter(l => l.claimedByDoc);
  // Hide loads already on a different freight bill by default. They cannot be
  // ticked here without triggering the double-claim banner, so they just add
  // noise. A toggle at the bottom of the table lets Mercedes show them if she
  // needs to confirm what is on which bill.
  const visibleLoads = hideClaimedLoads
    ? loads.filter(l => !l.claimedByDoc || l.checked)
    : loads;
  const hiddenClaimedCount = loads.filter(l => l.claimedByDoc && !l.checked).length;
  // Same filter for C&C loads.
  const visibleCncLoads = hideCncClaimed
    ? cncLoads.filter(l => !l.claimedByDoc || l.checked)
    : cncLoads;
  const hiddenCncClaimedCount = cncLoads.filter(l => l.claimedByDoc && !l.checked).length;
  // A per-box hauler's invoice should divide by the rate into whole boxes. One that
  // does not is either not billed per box (Tidewater is not) or carries something
  // extra, and either way it is worth the approver's eye. Never stated as an error.
  const impliedOdd = totals && totals.impliedBoxes != null
    && Math.abs(totals.impliedBoxes - Math.round(totals.impliedBoxes)) > 0.001;

  return (
    <div style={cardBody}>
      {/* What is being checked against what. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, marginBottom: 14 }}>
        <Field label="The hauler billed">
          <strong>{money(totals && totals.billed)}</strong>
        </Field>
        <Field label={`Loads ticked (${picked.length} of ${visibleLoads.length})`}>
          {money(totals && totals.ticked)}
        </Field>
        {(cncLoads.length > 0 || cncPicked.length > 0) && (
          <Field label={`Crab & Cruise loads ticked (${cncPicked.length} of ${cncLoads.length})`}>
            {cncPicked.length ? 'no freight recorded' : '—'}
          </Field>
        )}
        <Field label="Boxes on the ticked loads">
          {totals && totals.tickedBoxes ? totals.tickedBoxes : '—'}
        </Field>
        {rate != null && (
          <Field label="Freight per box lately">
            {money(rate)}
            <div style={{ fontSize: 11, color: 'var(--gray-500, #6b7280)' }}>
              from {rateSample} loads, last 90 days
            </div>
          </Field>
        )}
        {totals && totals.impliedBoxes != null && (
          <Field label="That invoice is this many boxes">
            {totals.impliedBoxes}
          </Field>
        )}
      </div>

      {/* The verdict on the arithmetic, and only once there is arithmetic to do. */}
      <div style={{ marginBottom: 14, display: 'grid', gap: 8 }}>
        {cncPicked.length > 0 && (
          <Banner tone="info" icon={<Truck size={15} />}>
            {cncPicked.length === 1 ? 'The Crab & Cruise load you ticked has' : `The ${cncPicked.length} Crab & Cruise loads you ticked have`}
            {' '}no freight recorded in CPx, because a C&amp;C load never got a yield sheet.
            {picked.length > 0
              ? <> The trip loads you ticked account for {money(totals.ticked)}, so the
                  other <strong>{money(diff)}</strong> of this bill is not checked against
                  anything here. Check it against the hauler&rsquo;s invoice by eye.</>
              : <> So none of the <strong>{money(totals && totals.billed)}</strong> on this
                  bill is checked against a recorded figure. Check it against the
                  hauler&rsquo;s invoice by eye.</>}
          </Banner>
        )}
        {cncDoubleClaimed.length > 0 && (
          <Banner tone="bad" icon={<AlertTriangle size={15} />}>
            {cncDoubleClaimed.length === 1 ? 'One of the Crab & Cruise loads you ticked' : 'Some Crab & Cruise loads you ticked'}
            {' '}also {cncDoubleClaimed.length === 1 ? 'shows up' : 'show up'} on another freight bill
            {' '}({cncDoubleClaimed.map(l => `${l.crabber}${l.crabberInvoice ? ' ' + l.crabberInvoice : ''} on bill #${l.claimedByDoc}`).join(', ')}).
            A hauler is only paid once per load. Uncheck it here if it belongs to that other bill.
          </Banner>
        )}
        {cncStrayTicks.length > 0 && (
          <Banner tone="info" icon={<AlertTriangle size={15} />}>
            {cncStrayTicks.length === 1 ? 'A Crab & Cruise load' : `${cncStrayTicks.length} Crab & Cruise loads`} ticked
            {' '}on this bill {cncStrayTicks.length === 1 ? 'sits' : 'sit'} outside the dates below
            (bill {cncStrayTicks.map(id => '#' + id).join(', ')}). Widen the dates to see
            {cncStrayTicks.length === 1 ? ' it' : ' them'}.
          </Banner>
        )}
        {picked.length === 0 && cncPicked.length === 0 && (
          <Banner tone="info" icon={<Truck size={15} />}>
            Tick the loads that are on this hauler's invoice. The freight on the ones
            you tick is added up against what they billed, so you can see whether it
            covers what actually came in.
          </Banner>
        )}
        {settled && (
          <Banner tone="good" icon={<Check size={15} />}>
            The {picked.length === 1 ? 'load' : `${picked.length} loads`} you ticked
            come to {money(totals.ticked)}, which is what the hauler billed.
          </Banner>
        )}
        {over && (
          <Banner tone="warn" icon={<AlertTriangle size={15} />}>
            The hauler billed <strong>{money(diff)} more</strong> than the freight on
            the loads you have ticked. Either a load is missing from the tick list, or
            they have billed us for something that did not come in.
          </Banner>
        )}
        {under && (
          <Banner tone="warn" icon={<AlertTriangle size={15} />}>
            The loads you ticked carry <strong>{money(Math.abs(diff))} more</strong>
            {' '}freight than the hauler billed. A load is probably on another one of
            their invoices.
          </Banner>
        )}
        {doubleClaimed.length > 0 && (
          <Banner tone="bad" icon={<AlertTriangle size={15} />}>
            {doubleClaimed.length === 1 ? 'One of the loads you ticked' : 'Some loads you ticked'}
            {' '}also {doubleClaimed.length === 1 ? 'shows up' : 'show up'} on another freight bill
            {' '}({doubleClaimed.map(l => `trip ${l.poId} on bill #${l.claimedByDoc}`).join(', ')}).
            A hauler is only paid once per load. Uncheck it here if it belongs to that other bill.
          </Banner>
        )}
        {offRate.length > 0 && rate != null && (
          <Banner tone="warn" icon={<AlertTriangle size={15} />}>
            {offRate.length === 1 ? 'One load is' : `${offRate.length} loads are`} not at
            the usual {money(rate)} a box
            {' '}({offRate.map(l => `trip ${l.poId} at ${money(l.perBox)}`).join(', ')}).
          </Banner>
        )}
        {impliedOdd && rate != null && (
          <Banner tone="info" icon={<AlertTriangle size={15} />}>
            {money(totals.billed)} does not divide into whole boxes at {money(rate)}.
            This hauler may not bill per box, or there is something else on the
            invoice worth a look.
          </Banner>
        )}
        {strayTicks.length > 0 && (
          <Banner tone="info" icon={<AlertTriangle size={15} />}>
            {strayTicks.length === 1 ? 'A load' : `${strayTicks.length} loads`} ticked on
            this bill {strayTicks.length === 1 ? 'sits' : 'sit'} outside the dates below
            (trip {strayTicks.join(', ')}), so {strayTicks.length === 1 ? 'it is' : 'they are'}
            {' '}not in the list. Widen the dates to see {strayTicks.length === 1 ? 'it' : 'them'}.
          </Banner>
        )}
      </div>

      {/* The packet itself. */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--gray-500, #6b7280)' }}>
              <th style={{ padding: '6px 8px', width: 34 }}></th>
              <th style={{ padding: '6px 8px' }}>Crabber</th>
              <th style={{ padding: '6px 8px' }}>Their invoice</th>
              <th style={{ padding: '6px 8px' }}>Came in</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Boxes</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Freight</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Per box</th>
              <th style={{ padding: '6px 8px' }}>Their bill in CPx</th>
            </tr>
          </thead>
          <tbody>
            {visibleLoads.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 14, color: 'var(--gray-500, #6b7280)' }}>
                No loads carrying freight came in around this invoice's date. Widen the
                dates below.
              </td></tr>
            )}
            {visibleLoads.map((l) => (
              <tr key={l.poId} style={{
                borderTop: '1px solid var(--gray-100, #f3f4f6)',
                background: l.checked ? '#f2f9f4' : 'transparent',
              }}>
                <td style={{ padding: '8px' }}>
                  <input
                    type="checkbox"
                    checked={l.checked}
                    disabled={!canEdit || saving === l.poId}
                    onChange={(e) => tick(l.poId, e.target.checked)}
                    title={canEdit ? 'On this hauler\'s invoice'
                      : 'This bill is paid, so what was checked stays as it was'}
                  />
                </td>
                <td style={{ padding: '8px', fontWeight: 600, color: 'var(--navy, #00395C)' }}>
                  {l.crabber || `Trip ${l.poId}`}
                  {l.checked && l.checkedBy && (
                    <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-500, #6b7280)' }}>
                      checked by {l.checkedBy}{l.checkedAt ? `, ${l.checkedAt}` : ''}
                    </div>
                  )}
                </td>
                <td style={{ padding: '8px' }}>{l.crabberInvoice || '—'}</td>
                <td style={{ padding: '8px' }}>{fmtDate(l.received)}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{l.boxes || '—'}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{money(l.freight)}</td>
                <td style={{
                  padding: '8px', textAlign: 'right',
                  color: l.offRate ? '#9a4a1c' : 'inherit',
                  fontWeight: l.offRate ? 700 : 400,
                }}>
                  {l.perBox == null ? '—' : money(l.perBox)}
                </td>
                <td style={{ padding: '8px' }}>
                  {/* The crabber's own paperwork, reachable from the freight bill the
                      way it was reachable in the packet. */}
                  {l.crabDocId ? (
                    <a href={`#ap-${l.crabDocId}`}
                       onClick={(e) => { e.preventDefault();
                         onOpenDocument && onOpenDocument(l.crabDocId); }}
                       style={{ color: 'var(--orange, #DC582A)', fontWeight: 600 }}>
                      #{l.crabDocId} {l.crabDocStatus}
                      {l.crabFileCount > 0 ? ` (${l.crabFileCount} scan${l.crabFileCount === 1 ? '' : 's'})` : ''}
                    </a>
                  ) : (
                    <span style={{ color: '#9a4a1c' }}>not entered yet</span>
                  )}
                  {l.claimedByDoc && (
                    <div style={{ fontSize: 11, color: '#b42318', fontWeight: 600 }}>
                      also on freight #{l.claimedByDoc}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Crab & Cruise loads. Never a trip, so never in the table above - the
          crabber's C&C-marked bill is the only record of the load, and that is
          what gets ticked. Mercedes, 2026-09-29. */}
      {(cncLoads.length > 0 || cncHidden > 0) && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#9a4a1c', marginBottom: 6 }}>
            Crab &amp; Cruise loads (used for C&amp;C, so no yield sheet with the freight)
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--gray-500, #6b7280)' }}>
                  <th style={{ padding: '6px 8px', width: 34 }}></th>
                  <th style={{ padding: '6px 8px' }}>Crabber</th>
                  <th style={{ padding: '6px 8px' }}>Their invoice</th>
                  <th style={{ padding: '6px 8px' }}>Invoice date</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>How much</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Crab bill</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Freight</th>
                  <th style={{ padding: '6px 8px' }}>Their bill in CPx</th>
                </tr>
              </thead>
              <tbody>
                {visibleCncLoads.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 14, color: 'var(--gray-500, #6b7280)' }}>
                    {hiddenCncClaimedCount > 0
                      ? `${hiddenCncClaimedCount} C&C load${hiddenCncClaimedCount === 1 ? '' : 's'} already on other freight bills, hidden.`
                      : 'No Crab & Cruise bills from crabbers we pay freight on in these dates.'}
                  </td></tr>
                )}
                {visibleCncLoads.map((l) => (
                  <tr key={l.docId} style={{
                    borderTop: '1px solid var(--gray-100, #f3f4f6)',
                    background: l.checked ? '#fff7ed' : 'transparent',
                  }}>
                    <td style={{ padding: '8px' }}>
                      <input
                        type="checkbox"
                        checked={l.checked}
                        disabled={!canEdit || saving === 'cnc-' + l.docId}
                        onChange={(e) => tickCnc(l.docId, e.target.checked)}
                        title={canEdit ? 'On this hauler\'s invoice'
                          : 'This bill is paid, so what was checked stays as it was'}
                      />
                    </td>
                    <td style={{ padding: '8px', fontWeight: 600, color: 'var(--navy, #00395C)' }}>
                      {l.crabber || `Bill #${l.docId}`}
                      {l.checked && l.checkedBy && (
                        <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-500, #6b7280)' }}>
                          checked by {l.checkedBy}{l.checkedAt ? `, ${l.checkedAt}` : ''}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px' }}>{l.crabberInvoice || '—'}</td>
                    <td style={{ padding: '8px' }}>{fmtDate(l.invoiceDate)}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {l.quantity != null
                        ? `${l.quantity.toLocaleString('en-US', { maximumFractionDigits: 1 })} ${l.quantityUnit || ''}`
                        : '—'}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{money(l.crabTotal)}</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: 'var(--gray-500, #6b7280)' }}>
                      none recorded
                    </td>
                    <td style={{ padding: '8px' }}>
                      <a href={`#ap-${l.docId}`}
                         onClick={(e) => { e.preventDefault();
                           onOpenDocument && onOpenDocument(l.docId); }}
                         style={{ color: 'var(--orange, #DC582A)', fontWeight: 600 }}>
                        #{l.docId} {l.status}
                        {l.fileCount > 0 ? ` (${l.fileCount} scan${l.fileCount === 1 ? '' : 's'})` : ''}
                      </a>
                      {l.claimedByDoc && (
                        <div style={{ fontSize: 11, color: '#b42318', fontWeight: 600 }}>
                          also on freight #{l.claimedByDoc}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(cncHidden > 0 || allCnc) && (
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
              {allCnc ? (
                <button onClick={() => setAllCnc(false)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: 'var(--orange, #DC582A)', textDecoration: 'underline', fontSize: 12 }}>
                  Only show crabbers we pay freight on
                </button>
              ) : (
                <>
                  {cncHidden} Crab &amp; Cruise bill{cncHidden === 1 ? '' : 's'} from crabbers
                  we have never paid freight on hidden.{' '}
                  <button onClick={() => setAllCnc(true)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      color: 'var(--orange, #DC582A)', textDecoration: 'underline', fontSize: 12 }}>
                    Show {cncHidden === 1 ? 'it' : 'them'}
                  </button>
                </>
              )}
            </div>
          )}
          {hiddenCncClaimedCount > 0 && (
            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
              {hideCncClaimed ? (
                <>
                  {hiddenCncClaimedCount} C&amp;C load{hiddenCncClaimedCount === 1 ? '' : 's'} already
                  on other freight bills hidden.{' '}
                  <button
                    onClick={() => setHideCncClaimed(false)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      color: 'var(--orange, #DC582A)', textDecoration: 'underline', fontSize: 12 }}>
                    Show {hiddenCncClaimedCount === 1 ? 'it' : 'them'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setHideCncClaimed(true)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: 'var(--orange, #DC582A)', textDecoration: 'underline', fontSize: 12 }}>
                  Hide C&amp;C loads already on other freight bills
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* A hauler's invoice can arrive well after the run, and one invoice can span
          more than a week, so the window is adjustable rather than guessed at. */}
      <div style={{
        marginTop: 12, display: 'flex', alignItems: 'center', gap: 10,
        fontSize: 12, color: 'var(--gray-500, #6b7280)', flexWrap: 'wrap',
      }}>
        <span>Loads received in the</span>
        <select value={back} onChange={(e) => setBack(Number(e.target.value))}
                style={{ ...inputStyle, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
          <option value={7}>7 days</option>
          <option value={14}>14 days</option>
          <option value={30}>30 days</option>
          <option value={60}>60 days</option>
        </select>
        <span>before this invoice's date{loading ? ' — loading…' : ''}</span>
        {hiddenClaimedCount > 0 && (
          <span style={{ marginLeft: 8 }}>
            {hideClaimedLoads ? (
              <>
                {hiddenClaimedCount} load{hiddenClaimedCount === 1 ? '' : 's'} already
                on other freight bills hidden.{' '}
                <button
                  onClick={() => setHideClaimedLoads(false)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: 'var(--orange, #DC582A)', textDecoration: 'underline', fontSize: 12 }}>
                  Show {hiddenClaimedCount === 1 ? 'it' : 'them'}
                </button>
              </>
            ) : (
              <button
                onClick={() => setHideClaimedLoads(true)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  color: 'var(--orange, #DC582A)', textDecoration: 'underline', fontSize: 12 }}>
                Hide loads already on other freight bills
              </button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
// Starting the hauler's bill. ⚠️ THE HAULER IS ASKED FOR HERE, UP FRONT, and that
// is the whole point of this form existing rather than a bare button. A freight bill
// created blank inherits nothing correct - the yield sheet records a freight cost, a
// date and a scan, and no carrier name at all - so a blank one either gets guessed at
// or gets paid to the crabber. Mercedes, 2026-09-01: "The hauler needs to be the
// freight company that actually hauled the load, entered or selected separately from
// the crabber."
// ⚠️ AND IT DOES NOT NEED A TRIP. A C&C load is entered by hand with no yield behind
// it and still has freight on it, and a hauler's invoice often turns up days after
// the crabber's - both cases arrive here with nothing to prefill, which is fine.
function YieldMatch({ doc, onUnlink, canUnlink, onUseCut, liveAmount, vendors, sourceFiles,
                     onRefreshTrip, refreshingTrip, reloadKey, reportEmailedAt }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showLines, setShowLines] = useState(false);

  const poId = doc.purchase_order_id;
  // Mercedes: the comparison should not require mental arithmetic, and it should
  // not require waiting either. The trip's own figures are fetched once; the delta
  // against what was billed is recomputed here from whatever is in the box right
  // now, so it moves as the amount is typed rather than after a save round trip.
  const invoiceAmount = doc.invoice_amount;
  const invoiceNumber = doc.invoice_number;

  useEffect(() => {
    let live = true;
    setLoading(true); setErr('');
    neonQuery('ap_yield_match', {
      purchaseOrderId: poId, invoiceAmount, billKind: isFreight(doc) ? 'freight' : 'crab',
    })
      .then((r) => { if (live) { setData(r); setLoading(false); } })
      .catch((e) => { if (live) { setErr(e.message); setLoading(false); } });
    return () => { live = false; };
  }, [poId, invoiceAmount, reloadKey]);

  if (loading) return <div style={cardBody}>Loading the trip…</div>;
  if (err) return <div style={cardBody}><Banner tone="bad" icon={<AlertTriangle size={15} />}>{err}</Banner></div>;
  if (!data) return null;

  const typed = liveAmount === '' || liveAmount == null ? null : parseMoney(liveAmount);
  const shownAmount = typed != null && isFinite(typed) ? typed : data.match.invoiceAmount;
  const { po, items } = data;
  // ⚠️ A BILL IS WEIGHED AGAINST WHAT THIS COMPANY IS OWED, NEVER AGAINST LANDED.
  // The server sends billBasis: the crabs on a crabber's bill, the freight on a
  // hauler's. Comparing a crabber's invoice to crabs+freight is what put a green
  // "matches the trip" tick on four bills that had the hauler's money inside them,
  // and made the correct crab-only figure look like a shortfall.
  const base = data.match;
  // Bayou Treasures hauls their own crabs, so their freight is owed to them directly
  // and belongs on their crabber bill -- not split to a separate hauler approval.
  const selfHaulVendor = /bayou\s+treasures/i.test(doc.vendor_name || '');
  // Falls back to working it out here if the response predates billBasis, so a stale
  // cached payload cannot silently put the comparison back on landed.
  // For self-haul vendors the correct comparison is landed (crabs + freight), not crabs alone.
  const basis = selfHaulVendor
    ? (base.crabCost || 0) + (base.freightCost || 0)
    : (base.billBasis != null ? base.billBasis
        : (isFreight(doc) ? base.freightCost : base.crabCost));
  const match = shownAmount === base.invoiceAmount ? base : {
    ...base,
    invoiceAmount: shownAmount,
    variance: shownAmount == null ? null
      : Math.round((shownAmount - basis) * 100) / 100,
    variancePct: shownAmount == null || !(basis > 0) ? null
      : Math.round(((shownAmount - basis) / basis) * 1000) / 10,
  };
  const hasInvoice = match.invoiceAmount != null;
  const v = match.variance;
  // Typed the landed total onto a crabber's bill. Called out on its own rather than
  // as a variance, because as a variance it reads as "billed a bit over" when what it
  // actually is is the hauler's invoice sitting inside the crabber's cheque.
  // Self-haul vendors (Bayou Treasures) legitimately include freight in their invoice.
  const lumped = hasInvoice && !isFreight(doc) && !selfHaulVendor && match.freightCost > 0
    && Math.abs(match.invoiceAmount - (match.crabCost + match.freightCost)) <= 0.01;
  // A penny either way is rounding, not a discrepancy worth a conversation.
  const matches = hasInvoice && Math.abs(v) <= 0.01;

  return (
    <div style={cardBody}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, marginBottom: 16 }}>
        <Field label="Crabber">{po.supplier || '—'}</Field>
        <Field label="Trip received">{fmtDate(po.dateReceived)}</Field>
        <Field label="Invoice # on the yield sheet">
          {po.poInvoiceNumber || '—'}
          {invoiceNumber && po.poInvoiceNumber
            && String(invoiceNumber).trim().toLowerCase() !== String(po.poInvoiceNumber).trim().toLowerCase() && (
            <div style={{ fontSize: 11.5, color: '#9a4a1c', marginTop: 2 }}>
              does not match "{invoiceNumber}" on the scan
            </div>
          )}
        </Field>
        <Field label="Cooked">{po.dateCooked ? fmtDate(po.dateCooked) : 'Not cooked yet'}</Field>
        <Field label="Yield sheet">
          {po.finalized ? (
            <span style={{ color: '#1c7c43', fontWeight: 700 }}>Finalized</span>
          ) : (
            <span style={{ color: '#9a4a1c', fontWeight: 700 }}>Not finalized yet</span>
          )}
        </Field>
        <Field label="Trip #">{po.poId}</Field>
        {canUnlink && (
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={onUnlink}>
              Wrong trip? Pick another
            </button>
          </div>
        )}
      </div>

      {/* The comparison. Crab and freight are shown separately on purpose: they
          live in different tables and only the two together are what the load
          actually cost us. */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 14, marginBottom: 14,
      }}>
        <div style={{ border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 6, padding: 14 }}>
          <div style={{ ...labelStyle, marginBottom: 10 }}>
            {isFreight(doc) ? 'What the yield sheet says the freight was' : 'What the trip cost us'}
          </div>
          {isFreight(doc) ? (
            <Row k="Freight on this trip" v={money(match.freightCost)} bold />
          ) : (
            <>
              {/* ⚠️ THE CRABS ARE WHAT THIS BILL PAYS, SO THEY ARE THE BOLD LINE.
                  Landed used to be the bold one and it is not what anybody is owed -
                  it is the load's cost, which the ROI below is rightly measured on.
                  The freight line says whose it is, because reading it as part of
                  this bill is the whole mistake. */}
              <Row k="Crabs" v={money(match.crabCost)} bold />
              <Row k="Freight (the hauler's)" v={money(match.freightCost)} />
              <div style={{ borderTop: '1px solid var(--gray-200, #e5e7eb)', margin: '8px 0' }} />
              <Row k="Landed (cost of the load, not a bill)" v={money(match.landedTotal)} />
            </>
          )}
          <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
            {match.unitsReceived == null ? 'Quantity not recorded' :
              `${num(match.unitsReceived, 1)} ${match.unit} received` +
              (match.unitCost != null ? ` at ${money(match.unitCost)}/${match.unit} of crabs` : '')}
          </div>
        </div>

        <div style={{ border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 6, padding: 14 }}>
          <div style={{ ...labelStyle, marginBottom: 10 }}>
            {isFreight(doc) ? 'What the hauler billed' : 'What the crabber billed'}
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--navy, #00395C)', lineHeight: 1.1 }}>
            {hasInvoice ? money(match.invoiceAmount) : '—'}
          </div>
          <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
            {hasInvoice
              ? 'Typed from the scanned invoice'
              : 'Enter the invoice amount above to compare it against the trip'}
          </div>
        </div>
      </div>

      {hasInvoice && (
        <div style={{ marginBottom: 14 }}>
          {/* ⚠️ THE COMPARISON NAMES THE FIGURE IT USED. "Matches the trip" against
              landed is what taught two people that crabs+freight was the right number
              to approve, so nothing here says "the trip" any more - it says the crabs,
              or the freight, and shows the arithmetic. */}
          {/* ⚠️ A CUT BILL IS COMPARED ON WHAT IS BEING PAID, NOT WHAT WAS BILLED. Greg and
              Matt Jr, Oct 22, 2026, Chester River #100545: billed $1,025, cut to $585, and
              the trip came to $585.04 because the 4 bu. of #1's taken off were keyed at
              $0.01 a bushel (the old admin will not take $0). The screen read "Billed
              $439.96 more than the crabs" and a 4-cent gap Matt could not make go away.
              Within a dollar after the cut, it matches; QuickBooks gets the approved
              amount exactly either way (lib/ap-qbo-push.js cutLines). */}
          {!isFreight(doc) && isCut(doc) && match.crabCost != null
            && Math.abs(Number(doc.approved_amount) - match.crabCost) <= 1 ? (
            <Banner tone="good" icon={<Check size={15} />}>
              <strong>After the cut, this matches the crabs on this trip.</strong>{' '}
              {money(doc.approved_amount)} to pay against {money(match.crabCost)} of crabs
              {Math.abs(Number(doc.approved_amount) - match.crabCost) > 0.004
                ? `; the ${Math.round(Math.abs(Number(doc.approved_amount) - match.crabCost) * 100)}¢ is the crabs taken off, keyed at a cent a bushel because the yield sheet will not take $0. Nothing to fix: QuickBooks gets exactly ${money(doc.approved_amount)}.`
                : '.'}
            </Banner>
          ) : lumped ? (
            <Banner tone="bad" icon={<AlertTriangle size={15} />}>
              <strong>That is the crabs and the freight added together.</strong>{' '}
              {po.supplier || 'The crabber'} is owed {money(match.crabCost)} for the crabs.
              The {money(match.freightCost)} of freight is the hauler's, and it goes on its
              own freight approval. Sending this would pay the freight twice.
            </Banner>
          ) : matches ? (
            <Banner tone="good" icon={<Check size={15} />}>
              <strong>
                The invoice matches the {isFreight(doc) ? 'freight' : 'crabs'} on this trip.
              </strong>{' '}
              {money(match.invoiceAmount)} billed against {money(basis)}.
            </Banner>
          ) : (
            <Banner tone={v > 0 ? 'warn' : 'info'} icon={<AlertTriangle size={15} />}>
              <strong>
                Billed {money(Math.abs(v))} {v > 0 ? 'more' : 'less'} than
                the {isFreight(doc) ? 'freight' : 'crabs'} on this trip
                {match.variancePct != null ? ` (${Math.abs(match.variancePct)}%)` : ''}.
              </strong>{' '}
              {money(match.invoiceAmount)} billed against {money(basis)}.
            </Banner>
          )}
        </div>
      )}

      {/* Dead crabs. ⚠️ SOME DEATH LOSS IS NORMAL - Matt Sr, on 12% of a 25 bushel
          load: "that's an allowable... probably just need the warning note if the
          death loss is over like 15%." So the count and the percentage are always
          shown, and only a figure over the threshold is flagged and carries a
          suggested cut. Flagging a normal trip teaches the approver to skim past
          the one banner on this screen that should stop him. */}
      <div style={{ marginBottom: 6 }}>
        {match.deadUnits == null ? (
          <Banner tone="info">No yield sheet has been filled in for this trip yet, so there is no dead count to check.</Banner>
        ) : match.deadUnits <= 0 ? (
          <Banner tone="good" icon={<Check size={15} />}>Nothing recorded dead on this trip.</Banner>
        ) : match.deadPriceable && !match.deadIsHigh ? (
          <Banner tone="info">
            <strong>{num(match.deadUnits, 2)} bushels came in dead</strong>
            {match.deadPct != null ? ` (${match.deadPct}% of the load)` : ''}, worth{' '}
            {money(match.deadAllowance)} at {money(match.unitCost)} a bushel. That is inside the
            normal range, so it is not flagged{match.deadFlagPct != null
              ? ` (we flag over ${match.deadFlagPct}%)` : ''}.
          </Banner>
        ) : match.deadPriceable ? (
          <Banner tone="warn" icon={<AlertTriangle size={15} />}>
            <strong>{num(match.deadUnits, 2)} bushels came in dead</strong>
            {match.deadPct != null ? ` (${match.deadPct}% of the load` : ''}
            {match.deadPct != null && match.deadFlagPct != null
              ? `, over the ${match.deadFlagPct}% we treat as normal)` : match.deadPct != null ? ')' : ''}, worth{' '}
            <strong>{money(match.deadAllowance)}</strong> at {money(match.unitCost)} a bushel.
            {onUseCut && (
              <>
                {' '}Approving at <strong>{money(match.suggestedApproval)}</strong> would take that off.{' '}
                <button
                  className="btn btn-ghost"
                  style={{ fontSize: 12, padding: '2px 8px', textDecoration: 'underline' }}
                  onClick={() => onUseCut(match.suggestedApproval, `${num(match.deadUnits, 2)} bushels dead on arrival`)}
                >
                  Use that figure
                </button>
              </>
            )}
          </Banner>
        ) : (
          <Banner tone="info">
            <strong>{num(match.deadUnits, 2)} bushels came in dead.</strong> This trip was bought
            by the pound, and the yield sheet counts dead in bushels, so there is no percentage or
            price to put on it here. Put a figure on it yourself if it warrants a cut.
          </Banner>
        )}
      </div>

      {/* The haulage on this trip. It is part of what the load cost us, and when
          there is a balance owed to the hauler it is a second bill to a second
          company - Matt Jr: it "serves as documentation for the crabs received, but
          it also needs to be broken off as a separate invoice for Mercedes to pay
          if there's a balance due". */}
      {/* The haulage on this trip, shown because it is part of what the load cost
          us and the approver is signing off the landed figure above.
          ⚠️ THE "BILL THE FREIGHT SEPARATELY" BUTTON IS GONE FROM HERE ON PURPOSE.
          Mercedes, 2026-09-03: "splitting off the crabber bill has been the source
          of every freight problem we've had. Unsent drafts with nothing attached,
          bills disappearing when you back out of them, and nobody able to tell
          whether one actually went anywhere. A freight bill is its own bill to its
          own vendor, so it's cleaner to start it as one instead of deriving it from
          something else." Freight is now started from its own button on the
          Approvals page. This card still POINTS at the hauler's bill when there is
          one, because that is the question an approver actually asks here.
          ⚠️ The hauler's invoice attached to THIS bill stays where it is - her
          words: "that's the record of what arrived." */}
      {!isFreight(doc) && (
        <div style={{
          marginTop: 14, border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 6,
          padding: 14,
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'center' }}>
            <Field label="Freight on this trip">
              {match.freightCost > 0
                ? <strong>{money(match.freightCost)}</strong>
                : <span style={{ color: 'var(--gray-500, #6b7280)' }}>None on the yield sheet</span>}
            </Field>
            <Field label="Freight invoice date">
              {po.freightInvoiceDate ? fmtDate(po.freightInvoiceDate) : 'Not recorded'}
            </Field>
            <div style={{ marginLeft: 'auto', textAlign: 'right', maxWidth: 340 }}>
              {po.freightDocumentId ? (
                <div style={{ fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>
                  The hauler is billed separately on approval #{po.freightDocumentId}
                </div>
              ) : (
                <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
                  The hauler is billed on their own approval. Start one from
                  {' '}<strong>Start a freight approval</strong> on the Approvals page.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* What it yielded, and what that is worth. Matt Sr signs a bill off on this:
          "I just need to see what they yielded and roi% in order to okay it for
          payment." A blank yield sheet and a zero yield are not the same fact and
          must not read the same. */}
      <YieldResults results={data.yield} po={po}
        onRefresh={onRefreshTrip} refreshing={refreshingTrip} reportEmailedAt={reportEmailedAt} />

      {po.poNote && (
        <div style={{ marginTop: 14 }}>
          <Field label="Note on the trip">{po.poNote}</Field>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <button
          className="btn btn-ghost"
          style={{ fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          onClick={() => setShowLines(s => !s)}
        >
          {showLines ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          What we bought ({items.length} {items.length === 1 ? 'line' : 'lines'})
        </button>
        {showLines && (
          <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
              <tr style={{ background: 'var(--gray-50, #f9fafb)' }}>
                {['Grade', 'Quantity', 'Price', 'Freight', 'Line total'].map(h => (
                  <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--gray-500, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.itemId} style={{ borderTop: '1px solid var(--gray-100, #f3f4f6)' }}>
                  <td style={{ padding: '7px 10px', fontWeight: 600 }}>{gradeLabel(it.gradeCode)}</td>
                  <td style={{ padding: '7px 10px' }}>
                    {it.quantity == null ? '—' : `${num(it.quantity, 1)} ${it.quantityUnit}`}
                    {it.quantityUnit === 'lb' && it.boxes ? ` (${num(it.boxes, 0)} boxes)` : ''}
                  </td>
                  <td style={{ padding: '7px 10px' }}>
                    {it.unitPrice == null ? '—' : `${money(it.unitPrice)}/${it.quantityUnit}`}
                  </td>
                  <td style={{ padding: '7px 10px' }}>{it.lineFreight ? money(it.lineFreight) : '—'}</td>
                  <td style={{ padding: '7px 10px', fontWeight: 600 }}>{money(it.lineTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, padding: '3px 0', fontWeight: bold ? 700 : 400 }}>
      <span style={{ color: bold ? 'var(--navy, #00395C)' : 'var(--gray-600, #4b5563)' }}>{k}</span>
      <span style={{ color: 'var(--navy, #00395C)' }}>{v}</span>
    </div>
  );
}

// ─── step tracker ─────────────────────────────────────────────────────────────

function StepTrack({ doc }) {
  const steps = doc.steps || [];
  const done = (doc.approvals || []).filter(a => a.action === 'approve' || a.action === 'adjust');
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
      {steps.map((s, i) => {
        const acted = done.find(a => a.step_no === s.step_no);
        const isCurrent = doc.status === 'pending' && doc.current_step === s.step_no;
        const bg = acted ? '#e8f5ec' : isCurrent ? '#fff3e6' : 'var(--gray-50, #f9fafb)';
        const fg = acted ? '#1c7c43' : isCurrent ? 'var(--orange, #DC582A)' : 'var(--gray-500, #6b7280)';
        if (s.was_skipped) {
          return (
            <React.Fragment key={s.step_no}>
              {i > 0 && <ChevronRight size={13} style={{ color: 'var(--gray-300, #d1d5db)' }} />}
              <div style={{
                background: 'var(--gray-50, #f9fafb)', color: 'var(--gray-400, #9ca3af)',
                borderRadius: 6, padding: '6px 11px', fontSize: 12, fontWeight: 600,
                border: '1px dashed var(--gray-300, #d1d5db)',
              }}
                title={`${s.approver_name} entered this one, so their own approval step was skipped`}>
                <span style={{ textDecoration: 'line-through' }}>{s.step_label}</span>
                <span style={{ fontWeight: 400 }}> · entered it themselves</span>
              </div>
            </React.Fragment>
          );
        }
        return (
          <React.Fragment key={s.step_no}>
            {i > 0 && <ChevronRight size={13} style={{ color: 'var(--gray-300, #d1d5db)' }} />}
            <div style={{
              background: bg, color: fg, borderRadius: 6, padding: '6px 11px',
              fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
              border: isCurrent ? '1px solid #f6d4b8' : '1px solid transparent',
            }}>
              {acted ? <Check size={13} /> : isCurrent ? <Clock size={13} /> : null}
              <span>{s.step_label}</span>
              <span style={{ fontWeight: 400, opacity: 0.8 }}>· {s.approver_name}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── detail ───────────────────────────────────────────────────────────────────

function Detail({ documentId, actor, onBack, onChanged, onOpenDocument, onAfterSubmit, startedHere = false }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  // ⚠️ A REFUSAL IS NOT A NOTICE. Every message on this screen - "Saved",
  // "Sent for approval", and the server's reasons for refusing - went through one
  // blue banner that erased itself after four seconds. Mercedes, 2026-08-25: "It
  // disappears too fast for me to get a screenshot." A success may fade. The
  // sentence telling you why the work would not go through has to stay until it
  // is read or the thing it complains about is fixed.
  const [errMsg, setErrMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  // Everyone we have billed before, for the crabber/hauler typeahead. Fetched once
  // per open bill and never blocking: an empty list just means typing it out.
  const [vendors, setVendors] = useState([]);
  const [freightVendors, setFreightVendors] = useState([]);
  const [addingHauler, setAddingHauler] = useState(false);
  const [newHauler, setNewHauler] = useState('');
  // Invoice bills: their own vendor list, what the bill is for, the entrant's own
  // approval, and the deny box.
  const [invoiceVendors, setInvoiceVendors] = useState([]);
  const [invoiceCategories, setInvoiceCategories] = useState([]);
  const [addingVendor, setAddingVendor] = useState(false);
  const [newVendor, setNewVendor] = useState('');
  const [attest, setAttest] = useState(false);
  const [denying, setDenying] = useState(null); // { reason }
  const [draft, setDraft] = useState({});
  const [cut, setCut] = useState(null); // { amount, reason } when cutting the ticket
  const [sendBack, setSendBack] = useState(null); // { reason } when sending it back
  // ⚠️ THE APPROVE BUTTON DOES NOT NAME A REASON, AND A LOAD UNDER 70% NEEDS ONE.
  // Matt Sr, 2026-09-30: "it's saying that a note has to be entered in b/c of the
  // roi is under 70%. I put a note on the yield sheet... but I don't see where to
  // put an add'l note at in order to clear this error." The yield sheet note that
  // would count sits in production and rides on the 15-minute HCD sync, so a note
  // he just typed there is not visible to this approval for another quarter of an
  // hour - and nothing on this screen offered him a second place to write one.
  // Now: when the server refuses on that ground the message flips into an input
  // right here, he types one line, and the retry carries it as `reason` - the same
  // path the "Approve a different amount" and "Send it back" flows already use,
  // which the server records on ap_approvals and Greg reads on the trail.
  const [noteRequired, setNoteRequired] = useState(null); // { message, reason }
  const [dupWarning, setDupWarning] = useState(null); // { id, stage } of a REAL live duplicate
  // Set when the server refuses to send an out-of-the-ordinary invoice without a line
  // saying why (ap_document_submit, needsNote). { message, text }
  const [noteAsk, setNoteAsk] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploads, setUploads] = useState([]); // in-flight uploads, for the progress list
  // Mercedes, 2026-09-18: drop the vendor's invoice on the bill and the reader
  // pulls the header off the file so she is not typing what is already printed.
  // ⚠️ NEVER OVERWRITES A TYPED VALUE. `apply` only fills fields the person has
  // left empty - if she has already keyed the vendor while the reader was working
  // (five to ten seconds), her value wins.
  // Shape: null | { status:'reading', filename } | { status:'ready', filename, fields, applied }
  const [scan, setScan] = useState(null);
  // Which form fields the reader filled rather than a person. Keyed by draft field
  // name; a field drops out of here the moment somebody edits it, because at that
  // point they have looked at it and it is theirs.
  const [prefilled, setPrefilled] = useState(null);
  const [yieldDocs, setYieldDocs] = useState(null); // what the yield sheet holds
  const [combining, setCombining] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [editIdent, setEditIdent] = useState(false); // override the trip's crabber/invoice #
  const [refreshingTrip, setRefreshingTrip] = useState(false);
  const [tripReadKey, setTripReadKey] = useState(0); // bumped when the trip is re-read
  // Turns red when somebody clicks Send without an invoice attached, so the
  // paperwork card highlights rather than the button looking randomly broken.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const paperworkCardRef = React.useRef(null);
  // Mercedes, 2026-10-19: Matt Sr keeps starting a second bill when the first draft
  // went invisible, so when CPx spots the twin we scroll the warning into view the
  // moment it appears. The banner already lived further down the form grid and he
  // was below it typing notes by then.
  const dupWarningRef = React.useRef(null);

  // `quiet` refreshes the document in the background without ever flipping the
  // page to a loading state. Only the FIRST load of a document is allowed to blank
  // the screen; everything after it happens underneath the person's hands.
  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setErr('');
    try {
      const d = await neonQuery('ap_document_get', { documentId });
      setDoc(d);
      // The form is only seeded from the server on a cold load. Re-seeding it on
      // every background save is the other half of the same bug: it would stamp
      // the saved value back over whatever the person has typed since.
      setDraft(prev => (prev && prev._seeded ? prev : {
        _seeded: true,
        vendorName: d.vendor_name || '',
        invoiceNumber: d.invoice_number || '',
        invoiceDate: d.invoice_date ? String(d.invoice_date).slice(0, 10) : '',
        invoiceAmount: d.invoice_amount == null ? '' : String(d.invoice_amount),
        notes: d.notes || '',
        paidAtPickup: d.paid_at_pickup === true,
        paidMethod: d.paid_method || '',
        forCnc: d.for_cnc === true,
        checkNumber: d.check_number || '',
        checkAmount: d.check_amount == null ? '' : String(d.check_amount),
        checkDate: d.check_date ? String(d.check_date).slice(0, 10) : '',
        checkPayee: d.check_payee || '',
        quantity: d.quantity == null ? '' : String(d.quantity),
        quantityUnit: d.quantity_unit || '',
        invoiceCategory: d.invoice_category || '',
        netTerms: d.net_terms || '',
        dueDate: d.due_date ? String(d.due_date).slice(0, 10) : '',
      }));
      // ⚠️ THE HIGHLIGHT SURVIVES A RELOAD, which is why the fields the reader
      // filled are stored on the bill rather than only held in this component.
      // Somebody uploads an invoice, gets called away, comes back to the tab: the
      // "review these, I can be wrong" prompt has to still be pointing at the same
      // fields, or the one thing she was asked to check is the thing she cannot see.
      // Seeded once, like the draft, so confirming a field clears its highlight and
      // a background save does not put it back.
      setPrefilled(prev => (prev && prev._seeded ? prev : Object.assign(
        { _seeded: true },
        // Stored as column names; the form thinks in draft keys.
        Object.keys(d.prefill_fields || {}).reduce((m, col) => {
          const k = { vendor_name: 'vendorName', invoice_number: 'invoiceNumber',
            invoice_date: 'invoiceDate', net_terms: 'netTerms', due_date: 'dueDate',
            invoice_amount: 'invoiceAmount' }[col];
          if (k) m[k] = true;
          return m;
        }, {})
      )));
    } catch (e) { setErr(e.message); }
    if (!quiet) setLoading(false);
  }, [documentId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let alive = true;
    neonQuery('ap_vendors', {})
      .then((r) => { if (alive) setVendors((r && r.vendors) || []); })
      .catch(() => { /* a missing suggestion list is not worth an error on the page */ });
    neonQuery('ap_freight_vendors', {})
      .then((r) => { if (alive) setFreightVendors((r && r.vendors) || []); })
      .catch(() => { /* same - the box still takes a typed name */ });
    return () => { alive = false; };
  }, []);
  const docIsInvoice = isInvoice(doc);
  useEffect(() => {
    if (!docIsInvoice) return undefined;
    let alive = true;
    neonQuery('ap_invoice_vendors', {})
      .then((r) => {
        if (!alive) return;
        // Mercedes 2026-09-18: keep the vendor list alphabetical, not
        // ordered by how often each vendor has been used. The server
        // sorts by usage count for other consumers, so re-sort here.
        const list = ((r && r.vendors) || []).slice()
          .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
        setInvoiceVendors(list);
      })
      .catch(() => {});
    neonQuery('ap_invoice_categories', {})
      .then((r) => { if (alive) setInvoiceCategories((r && r.categories) || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [docIsInvoice]);

  // A vendor typed in that is not on the list yet goes on it, and on this bill.
  const addInvoiceVendor = async () => {
    const name = String(newVendor || '').trim();
    if (!name) return;
    try {
      const r = await neonQuery('ap_invoice_vendor_add', { name });
      const saved = (r && r.name) || name;
      setInvoiceVendors((list) => {
        if (list.some((v) => v.name === saved)) return list;
        // A new vendor slots into alphabetical position, not the top.
        return [...list, { name: saved, uses: 0 }]
          .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
      });
      setDraft((d) => ({ ...d, vendorName: saved }));
      queueSave({ vendorName: saved });
      setAddingVendor(false); setNewVendor('');
    } catch (e) { setErrMsg(e.message); }
  };

  // Adding a hauler puts them on the list AND on this bill, because somebody adding
  // one is telling you who this bill is for.
  const addFreightVendor = async () => {
    const name = String(newHauler || '').trim();
    if (!name) return;
    try {
      const r = await neonQuery('ap_freight_vendor_add', { name });
      setFreightVendors((r && r.vendors) || []);
      setDraft((d) => ({ ...d, vendorName: name }));
      queueSave({ vendorName: name });
      setAddingHauler(false); setNewHauler('');
    } catch (e) { flash(e.message); }
  };


  // The paperwork Montel already scanned into the yield sheet. Matt Jr: "we're
  // dragging those same files in again". Listing is cheap and read-only, so it runs
  // whenever the bill is on a trip; pulling copies the files in.
  const lookForYieldDocs = useCallback(async () => {
    if (!documentId) return;
    try { setYieldDocs(await apYieldDocs('list', { documentId })); }
    catch (_) { setYieldDocs(null); } // the yield tool being unreachable is not this page's problem to shout about
  }, [documentId]);

  const pullYieldDocs = useCallback(async (quiet) => {
    setPulling(true);
    try {
      const r = await apYieldDocs('pull', { documentId });
      await load(true);
      await lookForYieldDocs();
      if (!quiet || r.pulled) {
        flash(r.pulled
          ? `Pulled ${r.pulled} ${r.pulled === 1 ? 'document' : 'documents'} in from the yield sheet.`
          : (r.failed ? 'Could not pull the yield sheet documents in.' : 'Nothing new on the yield sheet.'));
      }
    } catch (e) { if (!quiet) flash(e.message); }
    setPulling(false);
  }, [documentId, load, lookForYieldDocs]);

  // Read this one trip again, right now, for somebody who can see the yield sheet
  // is still being filled in. The mirror is on a 15-minute cycle and that is fine
  // for a background sync and not fine for the moment before an approval.
  const refreshTrip = useCallback(async () => {
    setRefreshingTrip(true);
    try {
      await apYieldDocs('refresh-trip', { documentId });
      setTripReadKey(k => k + 1);
      await lookForYieldDocs();
      flash('Read the yield sheet again.');
    } catch (e) { flash(e.message); }
    setRefreshingTrip(false);
  }, [documentId, lookForYieldDocs]);

  // The yield sheet itself. There is no file to pull - the yield tool builds its
  // PDF in the browser to email it and keeps nothing - so CPx renders one from the
  // same figures the approval screen shows.
  const makeYieldPdf = useCallback(async () => {
    setPulling(true);
    try {
      const r = await apYieldDocs('yield-pdf', { documentId });
      await load(true);
      flash(r.replaced ? 'Yield sheet PDF redone.' : 'Yield sheet PDF added.');
    } catch (e) { flash(e.message); }
    setPulling(false);
  }, [documentId, load]);

  // ⚠️ This effect has to live BELOW the two callbacks above: a const is not
  // hoisted, and naming lookForYieldDocs in a dependency array before its
  // declaration is a TDZ ReferenceError at render, not a lint nit.
  useEffect(() => {
    if (doc && doc.purchase_order_id) lookForYieldDocs();
  }, [doc && doc.purchase_order_id, lookForYieldDocs]); // eslint-disable-line react-hooks/exhaustive-deps

  // ⚠️ THE YIELD REPORT HAS TO BE ON THE BILL BEFORE THE APPROVER GETS THERE, NOT
  // ONE CLICK AWAY. Greg, 2026-09-02, holding the last approval on a J&T bill:
  // "I need to see the yield sheet PDF and the original invoice... I need the PDF
  // of that yield sheet inserted into CPx so I can approve it." It was not there,
  // and the reason is a timing gap nobody could have clicked their way out of: the
  // only thing that ever pulled the report was a button on the DRAFT screen, and
  // the report is emailed days AFTER the bill is entered. That bill was started on
  // 26 Aug and the report went round on 28 Aug, so at no point did the person who
  // was allowed to press the button have anything to pull. The page now fetches it
  // itself the first time somebody who can attach opens a bill that is missing it.
  //
  // ⚠️ ONCE PER BILL PER VISIT, AND ONLY WHEN THE MAIL SAYS ONE EXISTS. Pulling
  // writes files and re-hashes the manifest, which is the record of what each
  // approver signed against - doing that speculatively, on a trip whose yields are
  // not finished, would churn that record for nothing. `emailedAt` comes from the
  // metadata-only mail check, so this fires only when there is a real PDF to get.
  //
  // ⚠️ canAttachRef, not canFixFiles: that const is declared below the early
  // returns, and an effect body runs after the whole render, so the ref is current
  // by the time this reads it.
  const autoPulledFor = React.useRef(null);
  useEffect(() => {
    if (!documentId || !yieldDocs || autoPulledFor.current === documentId) return;
    if (yieldDocs.frozen || !canAttachRef.current) return;
    const r = yieldDocs.yieldReport;
    // Not attached yet, or a newer report was mailed after the one on the bill.
    if (!r || (r.attached ? !r.newerEmailedAt : !r.emailedAt)) return;
    autoPulledFor.current = documentId;
    pullYieldDocs(true);
  }, [documentId, yieldDocs, pullYieldDocs]);

  // Autosave. Debounced, so tabbing quickly through four fields is one round trip
  // rather than four, and silent, so nothing re-renders under the cursor. The
  // pending patch is held in a ref because a blur can land while an earlier save
  // is still in the air.
  const pendingRef = React.useRef({});
  const timerRef = React.useRef(null);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved

  const flushSave = useCallback(async () => {
    const patch = pendingRef.current;
    pendingRef.current = {};
    if (!Object.keys(patch).length) return;
    setSaveState('saving');
    try {
      const r = await neonQuery('ap_document_update', { documentId, ...patch });
      setDupWarning(r && r.duplicateOf
        ? { id: r.duplicateOf, stage: r.duplicateStage, isDraft: r.duplicateIsDraft === true }
        : null);
      setSaveState('saved'); setErrMsg('');
      // Refresh the derived figures (the yield match keys off the saved amount)
      // without disturbing the form.
      await load(true);
      onChanged && onChanged();
    } catch (e) {
      setSaveState('idle');
      setMsg(''); setErrMsg(e.message);
    }
  }, [documentId, load, onChanged]);

  const queueSave = useCallback((patch) => {
    pendingRef.current = { ...pendingRef.current, ...patch };
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flushSave, 600);
  }, [flushSave]);

  // Anything still queued when the screen closes must not be dropped on the floor.
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // ⚠️ ONCE PER DUPLICATE ID, not on every re-render that keeps the same warning.
  // The autosave repeats the duplicate check as fields change, so the state churns
  // between an equal value and null; scrolling on every truthy render would yank
  // the page around while he types.
  const dupSeenIdRef = React.useRef(null);
  useEffect(() => {
    if (!dupWarning || !dupWarning.id) { dupSeenIdRef.current = null; return; }
    if (dupSeenIdRef.current === dupWarning.id) return;
    dupSeenIdRef.current = dupWarning.id;
    dupWarningRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [dupWarning]);

  const flash = (t) => { setErrMsg(''); setMsg(t); setTimeout(() => setMsg(''), 4000); };
  // No timer. It goes when the reader dismisses it, or when the next thing works.
  const fail = (t) => { setMsg(''); setErrMsg(t || 'That did not go through.'); };

  const dragDepth = React.useRef(0);
  // Filled in further down the render. The paste listener has to be mounted up here,
  // above the early returns where hooks are allowed to live, but the two things it
  // needs are only known after them - so it reads them through refs and never
  // re-subscribes.
  const uploadRef = React.useRef(null);
  const canAttachRef = React.useRef(false);
  useEffect(() => {
    const onPaste = (e) => {
      if (!canAttachRef.current || !uploadRef.current) return;
      // Never steal a paste that was going into a box somebody is typing in.
      const t = e.target;
      const tag = t && t.tagName ? String(t.tagName).toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || (t && t.isContentEditable)) return;
      const files = filesFromClipboard(e.clipboardData);
      if (!files.length) return;
      e.preventDefault();
      uploadRef.current(files);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);
  // A drop that lands a few pixels OUTSIDE the dropzone makes the browser open the
  // file and throw the page away mid-invoice. Swallowing it at the window is what
  // turns a near miss into a no-op instead of lost work.
  useEffect(() => {
    const stop = (e) => { e.preventDefault(); };
    window.addEventListener('dragover', stop);
    window.addEventListener('drop', stop);
    return () => {
      window.removeEventListener('dragover', stop);
      window.removeEventListener('drop', stop);
    };
  }, []);

  // A bill that was sent back is editable again by whoever entered it, so every
  // "can this still be changed" test is this, not isDraft.
  const isEditable = doc && (doc.status === 'draft' || doc.status === 'returned');

  // ── THE PREFILL HIGHLIGHT ──────────────────────────────────────────────────
  // Mercedes, 2026-09-24: "Highlight what got prefilled and put a note under it
  // saying 'review what I filled in, I can make mistakes'."
  //
  // ⚠️ A TINT AND A LEFT EDGE, NOT A RED BORDER. These fields are probably right -
  // the reader is good at invoice headers - so the marking has to read as "look at
  // this" and not as "this is wrong", or every entry starts with four false alarms.
  // ⚠️ AND IT CLEARS PER FIELD as each one is confirmed or corrected, so what stays
  // lit is exactly what nobody has checked yet. A prompt that keeps pointing at
  // fields she has already fixed is one she learns to ignore.
  const termOptions = (doc && doc.net_terms_options) || [];
  const wasPrefilled = (field) => !!(prefilled && prefilled[field]);
  const prefillStyle = (field) => (wasPrefilled(field) ? {
    ...inputStyle,
    background: '#fffbeb',
    borderColor: '#fcd34d',
    boxShadow: 'inset 3px 0 0 #f59e0b',
  } : inputStyle);
  // Confirming a field is editing it, and editing it makes it yours. The server
  // applies the same rule on save; this keeps the screen honest before it answers.
  const clearPrefill = (field) => {
    setPrefilled((prev) => {
      if (!prev || !prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };
  const prefilledLabels = !prefilled ? [] : [
    ['vendorName', 'vendor'], ['invoiceNumber', 'invoice number'],
    ['invoiceDate', 'invoice date'], ['invoiceAmount', 'amount'],
    ['netTerms', 'terms'], ['dueDate', 'due date'],
  ].filter(([k]) => prefilled[k]).map(([, label]) => label);

  // ⚠️ SAY WHAT IS MISSING BEFORE THE BUTTON IS PRESSED. The server refuses a
  // submit with no amount or no scan, and it always did so with a clear sentence -
  // but the only place that sentence appeared was after the click, and until today
  // the client threw it away. Checking here means the form tells you while you are
  // still looking at the field, which is the only place the fix belongs. The
  // server stays the authority: this is a courtesy, not the gate.
  // ⚠️ Mercedes 2026-09-15: a bill reached the queue with only the scan on it -
  // no vendor, no invoice number, no invoice date - which leaves the approver
  // reading the PDF to work out what they are approving. Everything that
  // identifies WHICH bill it is is required now, checked in the order the
  // fields appear on the form so the reason reads top-to-bottom.
  const submitBlockers = useMemo(() => {
    if (!doc || !isEditable) return [];
    const missing = [];
    // ⚠️ Matt Jr 2026-09-15: the fields show "Ford's Seafood" and "4001" from
    // the picked trip, and the button still refuses to submit because it read
    // draft.vendorName / draft.invoiceNumber and those were empty. The draft is
    // seeded ONCE on cold load (the _seeded guard, load() line above), so a
    // trip picked after that first load populates the doc but never the draft
    // - and the read-only "From the trip below" display reads the doc, not
    // the draft, so the two disagree silently. Check both: if the doc has it
    // (from the trip) OR the draft has it (typed in), the field is there.
    const vendorName = String((draft && draft.vendorName) || doc.vendor_name || '').trim();
    const invoiceNumber = String((draft && draft.invoiceNumber) || doc.invoice_number || '').trim();
    const invoiceDate = String((draft && draft.invoiceDate)
      || (doc.invoice_date ? String(doc.invoice_date).slice(0, 10) : '') || '').trim();
    const invoiceAmount = (draft && draft.invoiceAmount !== '' && draft.invoiceAmount != null)
      ? draft.invoiceAmount : doc.invoice_amount;
    if (!vendorName) missing.push(isFreight(doc) ? 'the hauler' : 'the vendor');
    if (!invoiceNumber) missing.push('their invoice number');
    if (!invoiceDate) missing.push('the invoice date');
    if (parseMoney(invoiceAmount) == null) missing.push('the amount billed');
    // ⚠️ THE YIELD SHEET PDF IS NOT AN INVOICE. It is generated by CPx and now
    // arrives automatically, which quietly satisfied a plain "has any file"
    // test - and that is how a bill reached Greg with no invoice on it at all.
    // The scan the approver has to see is the one the vendor sent.
    if (!(doc.files || []).some(f => !f.removed_at && isInvoiceDoc(f))) {
      missing.push('a scan or photo of the invoice itself');
    }
    return missing;
  }, [doc, isEditable, draft]);
  const missingInvoice = submitBlockers.includes('a scan or photo of the invoice itself');
  const myStep = useMemo(() => {
    if (!doc || doc.status !== 'pending') return null;
    return (doc.steps || []).find(s =>
      s.step_no === doc.current_step && String(s.approver_admin_id) === String(actor.adminId));
  }, [doc, actor]);

  // Used for the structural edits (picking a trip), where a visible reload is
  // correct because the whole comparison panel changes. Field edits go through
  // queueSave instead.
  // Who a send-back would land on. A rejection goes back ONE stage, so from Greg
  // it lands on Matt Senior, and only from the first live step does it go all the
  // way back to whoever entered it. Steps that were skipped because the entrant
  // owns them are skipped on the way back too, which is why this walks the same
  // list the server does rather than assuming step-1.
  // The first person who will actually be asked. If Matt Senior entered it, his own
  // step is skipped and it goes straight to Greg — the button has to say so, or the
  // screen tells him something that will not happen.
  const firstApproverName = useMemo(() => {
    if (!doc) return 'the first approver';
    const live = (doc.steps || []).find(s =>
      !doc.submitted_by || String(s.approver_admin_id) !== String(doc.submitted_by));
    return live ? (live.approver_name || 'the first approver') : 'the first approver';
  }, [doc]);

  const backTarget = useMemo(() => {
    if (!doc || doc.status !== 'pending') return 'the entrant';
    const steps = (doc.steps || []).filter(s =>
      !doc.submitted_by || String(s.approver_admin_id) !== String(doc.submitted_by));
    const prev = steps.filter(s => s.step_no < doc.current_step).pop();
    return prev ? (prev.approver_name || 'the previous approver')
                : (doc.submitted_by_name || 'whoever entered it');
  }, [doc]);

  const saveDraft = async (patch) => {
    setBusy(true);
    try {
      await neonQuery('ap_document_update', { documentId, ...patch });
      await load(true); onChanged && onChanged();
    } catch (e) { fail(e.message); }
    setBusy(false);
  };

  const doAction = async (action, extra) => {
    setBusy(true);
    try {
      const d = await neonQuery('ap_document_action', { documentId, action, ...(extra || {}) });
      setDoc(d); setCut(null); setSendBack(null); setNoteRequired(null);
      onChanged && onChanged();
      flash(action === 'reject'
        ? `Sent back to ${d.status === 'returned' ? (d.submitted_by_name || 'whoever entered it') : backTarget}.`
        : (d.status !== 'approved' ? 'Approved.'
           : isPrepaid(d)
             ? 'Approved. That is it - it was already paid by check, so nothing is waiting on a payment.'
             : 'Approved. That is it from you; it is with accounting to pay now.'));
    } catch (e) {
      // ⚠️ ROI-UNDER-70 REFUSALS OPEN A NOTE INPUT INSTEAD OF A DEAD-END BANNER.
      // The server signals it with `yieldPct` or `returnPct` in the body (see
      // neon.js ap_document_action). Only intercept a plain approve - the adjust
      // and reject paths already carry their own reason input.
      const b = e.body || {};
      if (action === 'approve' && (b.yieldPct != null || b.returnPct != null)) {
        setNoteRequired({ message: e.message, reason: draft.notes || doc.notes || '' });
      } else {
        fail(e.message);
      }
    }
    setBusy(false);
  };

  // ⚠️ THE URL BEHIND OPEN AND DRAG IS THE STAMPED FILE, NOT THE RAW ONE.
  // Mercedes, 2026-09-29: "all good but i don't see any stamps on the invoices."
  // The stamps feature had only ever touched the combined-copy path, but she
  // attaches to QuickBooks by dragging an individual file, and clicking a
  // filename in the paperwork list opens the raw file too - so the copy
  // reaching QuickBooks had never been going through the stamp path at all.
  // This helper routes both openings through ap-combined-pdf's fileId mode: the
  // server renders THIS file, stamps page 0, caches under ap-stamped/, and
  // hands back a signed URL. When there is nothing to stamp the server answers
  // with the raw file's key and we go back through apFileUrl - the old
  // behaviour, unchanged, and the same one-round-trip cost.
  // ⚠️ DECLARED HERE, ABOVE THE USEEFFECT THAT DEPENDS ON IT. `const` does not
  // hoist, so declaring it further down puts it in the temporal dead zone when
  // the effect's dependency array is evaluated during render and every render
  // of Detail throws ReferenceError - which is a blank Approvals page.
  const stampedFileUrl = React.useCallback(async (file) => {
    if (!file) return null;
    if (!documentId || !file.id) return apFileUrl(file.r2_key);
    try {
      const base = window.location.hostname === 'localhost'
        ? 'http://localhost:8888/.netlify/functions'
        : '/.netlify/functions';
      const token = getAccessToken();
      const res = await fetch(`${base}/ap-combined-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ documentId, fileId: file.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return apFileUrl(file.r2_key);
      if (data.single && data.key) return apFileUrl(data.key);
      if (data.url) return data.url;
      return apFileUrl(file.r2_key);
    } catch (_) {
      // Never let a stamp lookup break opening or attaching a file.
      return apFileUrl(file.r2_key);
    }
  }, [documentId]);

  // Hand the operating system a real file when a row is dragged out of the page.
  // ⚠️ dataTransfer has to be set SYNCHRONOUSLY inside dragstart - awaiting the
  // signed URL first loses the drag - so the URL is fetched once and cached on the
  // row, and the first drag after opening the bill primes it.
  // ⚠️ THE PRIMED URL IS THE STAMPED VERSION when the bill has approvals - see
  // stampedFileUrl. On a bill with no stamps the server answers with the raw
  // file, so we cache the same URL apFileUrl would have given us.
  const dragUrls = React.useRef({});
  const onDragFile = (e, f) => {
    const mime = f.content_type || 'application/octet-stream';
    const name = (f.filename || 'invoice.pdf').replace(/[\r\n:]/g, ' ');
    const url = dragUrls.current[f.r2_key];
    if (url) {
      e.dataTransfer.setData('DownloadURL', `${mime}:${name}:${url}`);
      e.dataTransfer.setData('text/uri-list', url);
      e.dataTransfer.effectAllowed = 'copy';
    } else {
      // Not primed yet: get it now so the next drag works, and let this one go.
      stampedFileUrl(f).then(u => { if (u) dragUrls.current[f.r2_key] = u; }).catch(() => {});
    }
  };

  // Prime the signed links as soon as the bill is on screen, so the first drag
  // works rather than the second. Re-primes whenever the file set OR the
  // approvals change, so a stamp added after the bill was opened reaches
  // whichever file gets dragged next.
  const approvalKey = (doc && doc.approvals || []).map(a => `${a.id}:${a.action}`).join('|');
  useEffect(() => {
    let live = true;
    dragUrls.current = {};
    const files = (doc && doc.files ? doc.files : []).filter(f => !f.removed_at);
    files.forEach((f) => {
      stampedFileUrl(f).then(u => { if (live && u) dragUrls.current[f.r2_key] = u; }).catch(() => {});
    });
    return () => { live = false; };
  }, [doc && doc.manifest_sha256, approvalKey, stampedFileUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const ACCEPTED = /\.(pdf|jpe?g|png|gif|webp|heic|heif|tiff?|bmp)$/i;

  const onUpload = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    const rejected = list.filter(f => !ACCEPTED.test(f.name || '') && !/^image\//.test(f.type || '')
      && f.type !== 'application/pdf');
    if (rejected.length) {
      fail(`${rejected.map(f => f.name).join(', ')} — attach a PDF or a photo of the invoice.`);
    }
    const good = list.filter(f => !rejected.includes(f));
    if (!good.length) return;

    // Each file gets its own row in the progress list so a slow one does not look
    // like a stuck batch.
    const started = good.map(f => ({ id: `${f.name}-${f.size}-${Math.random()}`, name: f.name, pct: 0 }));
    setUploads(u => [...u, ...started]);
    for (let i = 0; i < good.length; i++) {
      const track = started[i];
      try {
        await uploadApInvoiceFile(documentId, good[i], 'invoice',
          (pct) => setUploads(u => u.map(x => (x.id === track.id ? { ...x, pct } : x))));
      } catch (e) {
        setUploads(u => u.map(x => (x.id === track.id ? { ...x, error: e.message } : x)));
        fail(`${good[i].name}: ${e.message}`);
        continue;
      }
      setUploads(u => u.filter(x => x.id !== track.id));
    }
    await load(true);
    // ⚠️ THE READER RUNS ON EVERY KIND OF BILL. Mercedes, 2026-09-29: "on crab
    // and freight approvals can we get those to autofill similar to the invoice
    // approvals... they're going to have to double check that info against the
    // actual invoice because AI can make mistakes." The trip still owns the
    // crabber and their invoice number once one is picked, so the reader only
    // fills the header fields the trip cannot fill (invoice date, amount) on
    // those bills; the invoice-only fields (terms, due date) are skipped by
    // applyScannedFields below.
    // Scans the FIRST invoice-shaped file in the batch, silently: if nothing
    // comes back, the person types it the old way.
    if (isEditable) {
      const scannable = good.find(f => canScanFile(f));
      if (scannable) tryScanInvoice(scannable);
    }
  };

  // Base64 for the reader. Netlify caps the request body around 6MB and base64
  // inflates by 33%, so the raw file cannot go above ~4.5MB. Skipped silently
  // above that (the upload has already succeeded and the person can still key
  // the header by hand).
  const SCAN_MAX_BYTES = 4 * 1024 * 1024;
  const canScanFile = (f) => {
    if (!f) return false;
    if (f.size > SCAN_MAX_BYTES) return false;
    const t = String(f.type || '').toLowerCase();
    if (t === 'application/pdf' || t.startsWith('image/')) return true;
    return /\.(pdf|jpe?g|png|gif|webp)$/i.test(f.name || '');
  };
  const readAsBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || '');
      const comma = s.indexOf(',');
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.onerror = () => reject(new Error('Could not read that file.'));
    r.readAsDataURL(file);
  });
  const tryScanInvoice = async (file) => {
    setScan({ status: 'reading', filename: file.name });
    try {
      const data = await readAsBase64(file);
      const r = await apInvoiceCoach('scan-invoice', {
        documentId,
        filename: file.name,
        mediaType: file.type || '',
        data,
      });
      setScan({ status: 'ready', filename: file.name, fields: r.fields || {}, applied: false });
    } catch (_) {
      // The reader failing is not the person's problem to shout about - they
      // still have the file on the bill and every field they were going to type
      // in front of them. Clear the banner silently.
      setScan(null);
    }
  };
  // Fill only the fields the person has not typed themselves. Queues the save
  // the same way a keystroke does, so what the reader adds and what she has
  // typed land through the same code path.
  const applyScannedFields = () => {
    if (!scan || scan.status !== 'ready') return;
    const f = scan.fields || {};
    const patch = {};
    const draftPatch = {};
    const isEmpty = (v) => v == null || String(v).trim() === '';
    if (f.vendorName && isEmpty(draft.vendorName)) {
      // A sure QuickBooks match goes in under its QuickBooks name, so the bill goes to
      // QuickBooks without anybody picking the vendor again. Otherwise the name as printed.
      const v = f.qboVendor && f.qboVendor.match ? f.qboVendor.match.name : f.vendorName;
      draftPatch.vendorName = v; patch.vendorName = v;
    }
    if (f.invoiceNumber && isEmpty(draft.invoiceNumber)) {
      draftPatch.invoiceNumber = f.invoiceNumber; patch.invoiceNumber = f.invoiceNumber;
    }
    if (f.invoiceDate && isEmpty(draft.invoiceDate)) {
      draftPatch.invoiceDate = f.invoiceDate; patch.invoiceDate = f.invoiceDate;
    }
    if (f.invoiceAmount != null && (draft.invoiceAmount === '' || draft.invoiceAmount == null)) {
      draftPatch.invoiceAmount = String(f.invoiceAmount); patch.invoiceAmount = f.invoiceAmount;
    }
    // ── TERMS AND DUE DATE ARE REAL FIELDS NOW ──────────────────────────────
    // They used to be appended to Notes as a labelled line, with a comment saying
    // it was worth promoting them "if she confirms she wants them separate".
    // Mercedes confirmed on 2026-09-24: "Add both columns to the Invoice Approvals
    // list and the Ready to Pay list... I am often paying to the terms." A line in
    // Notes cannot be a column and cannot be sorted, which was the whole problem.
    //
    // ⚠️ INVOICE BILLS ONLY. Terms and due date are not on the crabber or freight
    // form (a crabber is paid on the crab-buying rhythm, not on terms), and if we
    // wrote them into a crab/freight bill they would be stored with nowhere to
    // render and no way to correct - and the "review these" prompt would name
    // fields the person cannot see.
    //
    // ⚠️ THE TERMS GO THROUGH normalizeTerms, so what the reader saw in whatever
    // words the vendor printed lands as one of the eleven values the column sorts
    // by. A term that does not map (a bare "Net 37", "see agreement") stays BLANK
    // rather than being rounded to the nearest offered one - a wrong due date on
    // the list she pays from is worse than an empty cell she fills in.
    if (isInvoice(doc) && f.netTerms && isEmpty(draft.netTerms)) {
      const v = normalizeTerms(f.netTerms, termOptions);
      if (v) { draftPatch.netTerms = v; patch.netTerms = v; }
    }
    // The reader's own due date wins over a derived one when the invoice prints
    // both: a vendor whose terms and printed due date disagree will chase us on
    // the printed one. When it prints only terms, the server derives the date.
    if (isInvoice(doc) && f.dueDate && isEmpty(draft.dueDate)) {
      draftPatch.dueDate = f.dueDate; patch.dueDate = f.dueDate;
    }
    // The free-text description is a genuine note and still goes to Notes.
    if (f.description) {
      const line = f.description;
      const cur = String(draft.notes || '').trim();
      if (!cur.toLowerCase().includes(line.toLowerCase())) {
        const combined = cur ? `${cur}\n${line}` : line;
        draftPatch.notes = combined; patch.notes = combined;
      }
    }
    if (Object.keys(draftPatch).length) {
      setDraft({ ...draft, ...draftPatch });
      // ⚠️ On a crab bill without a trip picked, the vendor and invoice number
      // inputs are hidden behind "No trip for this one? Type them in". If the
      // reader has filled those two, the person needs to see them to review
      // them - the amber "review these" prompt would otherwise name fields she
      // cannot look at. Freight bills already show their vendor input always,
      // and invoice bills have no trip picker at all, so this only fires for
      // crab bills.
      if (!isInvoice(doc) && !isFreight(doc) && !doc.purchase_order_id
          && (draftPatch.vendorName || draftPatch.invoiceNumber)) {
        setEditIdent(true);
      }
      // The column names for the fields the reader filled travel WITH the save, so
      // the server records them as read-not-typed in the same statement that stores
      // the values. Sending them separately would leave a window where the values
      // are saved and nothing says they need reviewing.
      const cols = { vendorName: 'vendor_name', invoiceNumber: 'invoice_number',
        invoiceDate: 'invoice_date', invoiceAmount: 'invoice_amount',
        netTerms: 'net_terms', dueDate: 'due_date' };
      const prefilledCols = Object.keys(draftPatch).map((k) => cols[k]).filter(Boolean);
      queueSave({ ...patch, prefilled: prefilledCols });
      // ⚠️ WHICH FIELDS THE READER FILLED, so the form can highlight exactly those
      // and say "review what I filled in, I can make mistakes" - Mercedes's words.
      // Recorded per field rather than as one was-prefilled flag, because the
      // highlight has to clear off a field as she confirms or corrects it and stay
      // on the ones she has not looked at yet.
      setPrefilled((prev) => {
        const next = { ...prev };
        Object.keys(draftPatch).forEach((k) => { next[k] = true; });
        return next;
      });
    }
    setScan({ ...scan, applied: true });
  };

  // ⚠️ IT FILLS THE FORM ITSELF NOW, RATHER THAN OFFERING TO. The reader has always
  // run on upload, but it stopped at a banner with a "Fill in the blanks" button.
  // Mercedes, 2026-09-24, asked for it to "read the PDF (or photo) and prefill the
  // vendor, invoice number, invoice date and net terms on the form" - so the click
  // is gone and the HIGHLIGHT plus "review what I filled in, I can make mistakes"
  // is the review step instead. That is the trade she asked for: one less click in,
  // and the marking tells her what to check on the way out.
  //
  // ⚠️ SAFE ONLY BECAUSE IT FILLS BLANKS AND NOTHING ELSE. applyScannedFields skips
  // every field that already has something in it, so this can never overwrite
  // typing - which is what makes doing it without asking reasonable.
  React.useEffect(() => {
    if (!scan || scan.status !== 'ready' || scan.applied) return;
    if (!isEditable) return;
    applyScannedFields();
    // applyScannedFields is recreated every render; the guard above is what stops
    // this firing twice, not the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scan, isEditable, doc]);

  const moveFile = async (fileId, dir) => {
    const ordered = (doc.files || []).filter(f => !f.removed_at).map(f => f.id);
    const i = ordered.indexOf(fileId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ordered.length) return;
    [ordered[i], ordered[j]] = [ordered[j], ordered[i]];
    setBusy(true);
    try { setDoc(await neonQuery('ap_file_reorder', { documentId, fileIds: ordered })); }
    catch (e) { fail(e.message); }
    setBusy(false);
  };

  // ⚠️ THE TAB IS OPENED BEFORE THE AWAIT, not after. A popup blocker kills a
  // window.open that is not the direct result of the click, and building the
  // merge takes a second or two - so the tab is claimed first and pointed at the
  // document once it is ready. Same reason openFile does it below.
  const openCombined = async () => {
    const tab = window.open('', '_blank');
    setCombining(true);
    try {
      const r = await apCombinedPdf(documentId);
      if (tab) tab.location = r.url; else window.location = r.url;
      // Nothing is hidden: if a page could not be rendered it is IN the PDF as a
      // placeholder, and the approver is told here as well.
      if (r.failed) {
        fail(`${r.failed} of ${r.files} ${r.failed === 1 ? 'file' : 'files'} could not be combined. `
           + 'They are marked in the PDF and are still on the bill to open on their own.');
      }
    } catch (e) {
      if (tab) tab.close();
      fail(e.message);
    }
    setCombining(false);
  };

  // Put it back to invoice / yield / hauler. Clearing the stamp is the whole
  // operation - the default is computed, never stored, so there is nothing to
  // rewrite and nothing that can drift out of step with it.
  const resetFileOrder = async () => {
    setBusy(true);
    try { await neonQuery('ap_file_order_reset', { documentId }); await load(true); }
    catch (e) { fail(e.message); }
    setBusy(false);
  };

  const openFile = async (file) => {
    // The tab has to be opened synchronously or the popup blocker eats it, so it
    // is opened first and pointed at the signed URL once that comes back.
    const tab = window.open('', '_blank');
    try {
      const url = await stampedFileUrl(file);
      if (tab) tab.location = url; else window.location = url;
    } catch (e) {
      if (tab) tab.close();
      fail(e.message);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (err) return (
    <div style={{ padding: 24 }}>
      <Banner tone="bad" icon={<AlertTriangle size={15} />}>{err}</Banner>
      <button className="btn btn-ghost" onClick={onBack} style={{ marginTop: 12 }}>Back</button>
    </div>
  );
  if (!doc) return null;

  const liveFiles = (doc.files || []).filter(f => !f.removed_at);
  const hasYieldPdf = liveFiles.some(f => f.kind === 'yield-sheet');
  // A bill that is still moving can have its paperwork fixed by whoever entered it
  // or whoever it is sitting with - Matt Sr sent one without the invoice on it and
  // had no way back in. Adding a page re-hashes the file set, so any approval
  // already given against the old set is flagged as no longer standing; that is
  // the honest trade and the screen says so.
  // The chain names one person who pays. Used to tell everybody else whose desk
  // it is on now, rather than handing them her button.
  const payerName = (actor && actor.payerName) || 'accounting';
  // The person who entered it, whoever pays, or an admin. ⚠️ Mercedes is the payer
  // and deliberately NOT an admin, so an admin-only rule would have left the person
  // doing the clearing up unable to clear anything up. Approved and paid are absent
  // on purpose and the server refuses them too.
  const canDelete = doc
    && ['draft', 'returned', 'pending', 'rejected'].includes(doc.status)
    && (String(doc.submitted_by) === String(actor.adminId)
        || actor.isPayer === true
        || actor.isAdmin === true);
  const inChain = (doc.steps || []).some(
    s => String(s.approver_admin_id) === String(actor.adminId));
  const canFixFiles = isEditable || (doc.status === 'pending'
    && (String(doc.submitted_by) === String(actor.adminId) || !!myStep || inChain));
  canAttachRef.current = canFixFiles;
  uploadRef.current = onUpload;
  // Was the only thing added since the oldest stale approval a yield document?
  // Computed from what the page already has: every file carries its kind and when
  // it was uploaded, every approval carries when it was given.
  const onlyYieldDocsAdded = (() => {
    const stale = (doc.approvals || [])
      .filter(a => !a.is_current && a.action !== 'submit' && a.action !== 'pay' && a.action !== 'skip');
    if (!stale.length) return false;
    const oldest = stale.reduce((min, a) =>
      (!min || new Date(a.created_at) < new Date(min)) ? a.created_at : min, null);
    const added = (doc.files || []).filter(f =>
      !f.removed_at && f.uploaded_at && new Date(f.uploaded_at) > new Date(oldest));
    return added.length > 0 && added.every(f => f.kind === 'yield-report' || f.kind === 'yield-sheet');
  })();
  const staleApprovals = (doc.approvals || []).some(a =>
    (a.action === 'approve' || a.action === 'adjust') && !a.is_current);

  // ── THE PAPERWORK CARD, HOISTED SO IT CAN BE RENDERED IN TWO PLACES ───────
  // Mercedes, 2026-09-24: "Right now upload is the last step. It should be the
  // first step, so the prefill has something to fill."
  //
  // ⚠️ IT MOVES ONLY WHILE THE BILL IS STILL BEING ENTERED (draft or returned).
  // That is the flow she is describing - you upload, the reader fills the form in,
  // you check it. For an APPROVER the order she asked for would be wrong: Greg works
  // down fifteen bills and wants the amount and the history first, and quietly
  // reshuffling his screen is a change nobody asked for. So the card leads on the
  // entry form and stays where it was everywhere else.
  //
  // ⚠️ ONE COPY, RENDERED TWICE - never two copies. This block carries the
  // dropzone, the drag counters, the reorder arrows and the upload progress; a
  // second pasted copy of it would be the mirror-drift trap this repo already has
  // scars from, and the two would stop behaving the same way within a month.
  // The scanned paperwork on the bill.
  const paperworkCard = (
        <div ref={paperworkCardRef} style={{
          ...card,
          ...(submitAttempted && missingInvoice ? {
            border: '2px solid #dc2626',
            boxShadow: '0 0 0 3px rgba(220,38,38,0.15)',
          } : {}),
        }}>
          {submitAttempted && missingInvoice && (
            <div style={{
              margin: '0 0 8px 0', padding: '10px 14px', borderRadius: 6,
              background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
              fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 16 }}>&#9888;</span>
              Upload the invoice before sending for approval
            </div>
          )}
          <div style={cardHead}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Paperclip size={14} /> Scanned paperwork ({liveFiles.length})
              {!liveFiles.some(isInvoiceDoc) && (
                <span style={{
                  background: '#fff3e6', color: '#9a4a1c', border: '1px solid #f6d4b8',
                  fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 999,
                  textTransform: 'uppercase', letterSpacing: '0.4px',
                }}>Invoice required</span>
              )}
            </span>
            <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              {/* ⚠️ THE COMBINED COPY IS OFFERED TO EVERYONE WHO CAN SEE THE BILL, not
                  only to whoever is allowed to change it. Reading the paperwork IS the
                  approver's job, and this is a read: it writes nothing to the bill and
                  cannot alter the file set anybody signed against. */}
              {liveFiles.length > 1 && (
                <button className="btn btn-outline" style={{ fontSize: 12 }}
                  disabled={combining} onClick={openCombined}
                  title="The yield report and the invoice, in one document, in the order they are in below">
                  <FileText size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />
                  {combining ? 'Putting it together…' : `Open all ${liveFiles.length} as one PDF`}
                </button>
              )}
              {/* The upload controls are rendered away rather than made invisible, so
                  the combined button is not left sitting in a hidden row. */}
              {canFixFiles && (
                <label className="btn btn-ghost" style={{ fontSize: 12, cursor: 'pointer', margin: 0 }}>
                  <Camera size={13} style={{ marginRight: 5 }} />
                  Take a photo
                  <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                    onChange={(e) => { onUpload(Array.from(e.target.files)); e.target.value = ''; }} />
                </label>
              )}
              {canFixFiles && (
                <label className="btn btn-ghost" style={{ fontSize: 12, cursor: 'pointer', margin: 0 }}>
                  <Upload size={13} style={{ marginRight: 5 }} />
                  {liveFiles.length ? 'Add or replace' : 'Add a scan'}
                  <input type="file" multiple
                    accept="application/pdf,image/*,.heic,.heif,.tif,.tiff"
                    style={{ display: 'none' }}
                    onChange={(e) => { onUpload(Array.from(e.target.files)); e.target.value = ''; }} />
                </label>
              )}
            </span>
          </div>
          {/* The dropzone. dragover MUST preventDefault or Chrome refuses the drop
              outright, which reads to whoever is holding the file as "nothing
              happened" - the same defect the brain chats had. dragenter/dragleave
              are counted rather than toggled, or crossing a child element cancels
              the highlight halfway through the drag. */}
          <div
            style={{
              ...cardBody,
              ...(canFixFiles ? {
                margin: 12, padding: 16, borderRadius: 8,
                border: `2px dashed ${dragging ? 'var(--orange, #DC582A)' : 'var(--gray-300, #d1d5db)'}`,
                background: dragging ? '#fff7f2' : 'transparent',
                transition: 'background 120ms, border-color 120ms',
              } : {}),
            }}
            onDragEnter={canFixFiles ? (e) => { e.preventDefault(); dragDepth.current += 1; setDragging(true); } : undefined}
            onDragOver={canFixFiles ? (e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'; } : undefined}
            onDragLeave={canFixFiles ? (e) => {
              e.preventDefault();
              dragDepth.current = Math.max(0, dragDepth.current - 1);
              if (dragDepth.current === 0) setDragging(false);
            } : undefined}
            onDrop={canFixFiles ? (e) => {
              e.preventDefault(); dragDepth.current = 0; setDragging(false);
              filesFromDataTransfer(e.dataTransfer).then((files) => {
                if (!files.length) {
                  // ⚠️ AND WHEN WINDOWS GENUINELY HANDS OVER NOTHING, THE WAY OUT IS
                  // PASTE, NOT "SAVE IT TO YOUR COMPUTER". Some Outlook builds offer a
                  // drag the browser is not allowed to read at all; copying the same
                  // attachment always puts a real file on the clipboard. Mercedes's
                  // objection was never the keystroke, it was "saving files down just
                  // to re-attach them", and this costs nothing of the sort.
                  fail('Windows did not hand that over as a file. In Outlook, right-click the '
                     + 'attachment and choose Copy, then click here and press Ctrl+V. No need to '
                     + 'save it to your computer.');
                  return;
                }
                onUpload(files);
              });
            } : undefined}
          >
            {/* ⚠️ IF THE ONLY WAY IN IS TWO SMALL LINKS IN A HEADER, IT DOES NOT
                EXIST. Matt Sr entered J&T, is allowed to add a page for as long as it
                is moving, and could not find anywhere to do it - Mercedes: "if the
                option is there and he just can't see it, it needs to be obvious on
                the screen next to the INVOICE REQUIRED tag." So when the invoice is
                genuinely missing, the thing to press is a full-width button in the
                body of the card, not a ghost link above it. */}
            {canFixFiles && !liveFiles.some(isInvoiceDoc) && (
              <label style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                margin: '0 0 12px', padding: '14px 16px', borderRadius: 8, cursor: 'pointer',
                background: '#fff7f2', border: '2px solid var(--orange, #DC582A)',
                color: 'var(--orange, #DC582A)', fontWeight: 700, fontSize: 14,
              }}>
                <Upload size={17} />
                {isInvoice(doc) ? 'Add the vendor’s invoice'
                  : isFreight(doc) ? 'Add the hauler’s invoice'
                  : 'Add the crabber’s invoice'}
                <input type="file" multiple
                  accept="application/pdf,image/*,.heic,.heif,.tif,.tiff"
                  style={{ display: 'none' }}
                  onChange={(e) => { onUpload(Array.from(e.target.files)); e.target.value = ''; }} />
              </label>
            )}

            {!liveFiles.length && !uploads.length && (
              <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)', textAlign: canFixFiles ? 'center' : 'left' }}>
                {canFixFiles
                  ? 'Drag the scan or the photo in here, straight from an email if you like. You can also copy an attachment and press Ctrl+V, or use the buttons above.'
                  : "Nothing was attached to this one."}
              </div>
            )}

            {liveFiles.map((f, idx) => (
              <div key={f.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0',
                borderBottom: '1px solid var(--gray-100, #f3f4f6)', fontSize: 13,
              }}>
                {/* ⚠️ THE POSITION IS SHOWN AS A NUMBER, not implied by where the row
                    sits. Greg asked for "a little reordering section where I can move
                    them in order" - the arrows were already there and he did not read
                    them as an ordering control, because nothing on the row said the
                    order meant anything. The number is what makes the arrows legible,
                    and it is the same order the combined PDF comes out in. */}
                <span style={{
                  minWidth: 20, height: 20, borderRadius: 999, flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--navy, #00395C)', color: '#fff',
                  fontSize: 11, fontWeight: 800,
                }}>{idx + 1}</span>
                <FilePreview file={f} />
                {/* ⚠️ DRAGGING A FILE OUT OF A WEB PAGE INTO A DESKTOP APP IS ONE
                    SPECIFIC DATA TYPE: 'DownloadURL', formatted
                    "{mime}:{filename}:{absolute url}". Nothing else works - not
                    text/uri-list, not a plain href - and without it Mercedes has been
                    saving every bill to her desktop just to re-attach it in
                    QuickBooks. The URL has to be absolute and directly fetchable, so
                    it is the signed R2 link, fetched on dragstart and cached on the
                    row. Chromium only, which is what the office runs. */}
                <span
                  draggable
                  title="Drag straight into QuickBooks"
                  onDragStart={(e) => onDragFile(e, f)}
                  style={{ cursor: 'grab', display: 'inline-flex' }}
                >
                  <button className="btn btn-ghost"
                    style={{ padding: 0, fontSize: 13, textDecoration: 'underline', textAlign: 'left' }}
                    onClick={() => openFile(f)}>{f.filename || f.r2_key}</button>
                </span>
                {(() => {
                  const role = fileRole(f);
                  const t = ROLE_TONE[role];
                  return (
                    <span style={{
                      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
                      fontSize: 10, fontWeight: 800, letterSpacing: '0.4px',
                      padding: '2px 7px', borderRadius: 999, textTransform: 'uppercase',
                      whiteSpace: 'nowrap', flexShrink: 0,
                    }}>{ROLE_LABEL[role]}</span>
                  );
                })()}
                <span style={{ color: 'var(--gray-500, #6b7280)', fontSize: 11.5 }}>{fmtBytes(f.byte_size)}</span>
                <span style={{ color: 'var(--gray-500, #6b7280)', fontSize: 11.5, marginLeft: 'auto' }}>
                  {f.uploaded_by_name} · {fmtWhen(f.uploaded_at)}
                </span>
                {canFixFiles && liveFiles.length > 1 && (
                  <span style={{ display: 'flex', gap: 2 }}>
                    <button className="btn btn-ghost" style={{ padding: '2px 5px' }} title="Move up"
                      disabled={idx === 0} onClick={() => moveFile(f.id, -1)}>
                      <ArrowUp size={13} />
                    </button>
                    <button className="btn btn-ghost" style={{ padding: '2px 5px' }} title="Move down"
                      disabled={idx === liveFiles.length - 1} onClick={() => moveFile(f.id, 1)}>
                      <ArrowDown size={13} />
                    </button>
                  </span>
                )}
                {canFixFiles && (
                  <button className="btn btn-ghost" style={{ padding: '2px 6px' }} title="Remove"
                    onClick={async () => {
                      setBusy(true);
                      try { await neonQuery('ap_file_remove', { documentId, fileId: f.id }); await load(true); }
                      catch (e) { fail(e.message); }
                      setBusy(false);
                    }}>
                    <Trash2 size={13} style={{ color: '#b42318' }} />
                  </button>
                )}
              </div>
            ))}

            {/* ⚠️ SAY WHAT THE ORDER IS AND WHERE IT CAME FROM. A default nobody was
                told about looks like an accident the first time it puts the hauler
                last, and then somebody "fixes" it. */}
            {canFixFiles && liveFiles.length > 1 && (
              <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--gray-500, #6b7280)',
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>
                  The arrows set the order, and the combined PDF comes out the same way.{' '}
                  {doc.files_ordered_at
                    ? 'You have arranged this one by hand, so it stays as you left it.'
                    : 'Default order: the invoice, then the yield report, then the hauler.'}
                </span>
                {doc.files_ordered_at && (
                  <button className="btn btn-ghost" style={{ fontSize: 11.5, padding: '2px 8px' }}
                    disabled={busy} onClick={resetFileOrder}>
                    Back to the default order
                  </button>
                )}
              </div>
            )}
            {doc.status === 'pending' && canFixFiles && (
              <div style={{ marginTop: 10, fontSize: 11.5, color: '#9a4a1c' }}>
                {String(doc.submitted_by) === String(actor.adminId)
                  ? 'You entered this one, so you can add or replace paperwork for as long as it is moving — even after you have approved it.'
                  : 'It is with you, so you can add or replace paperwork on it.'}{' '}
                Anyone who has already approved will have to look again, because their approval
                was given against the paperwork as it was.
              </div>
            )}

            {uploads.map(u => (
              <div key={u.id} style={{ padding: '8px 0', fontSize: 12.5 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: 'var(--gray-600, #4b5563)' }}>{u.name}</span>
                  <span style={{ color: u.error ? '#b42318' : 'var(--gray-500, #6b7280)' }}>
                    {u.error ? u.error : `${u.pct}%`}
                  </span>
                </div>
                <div style={{ height: 4, background: 'var(--gray-200, #e5e7eb)', borderRadius: 999 }}>
                  <div style={{
                    height: 4, width: `${u.pct}%`, borderRadius: 999,
                    background: u.error ? '#b42318' : 'var(--orange, #DC582A)', transition: 'width 150ms',
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
  );

  return (
    // ⚠️ THE WHOLE BILL TAKES A DROP, NOT JUST THE DASHED BOX. Greg tried to drop
    // the yield PDF onto the card that talks about the yield PDF and nothing
    // happened, which reads as "drag and drop is broken" rather than "you missed by
    // 200 pixels". The window-level handler already swallows a drop that lands off
    // the panel; this catches every one that lands on it.
    // ⚠️ defaultPrevented is the whole guard: the dashed dropzone below is a
    // descendant, its handler runs first and calls preventDefault, so by the time
    // the event bubbles up here it is already handled and this bails. Without that
    // check a drop into the real dropzone would upload the same file twice.
    <div
      onDragEnter={canFixFiles ? (e) => {
        if (e.defaultPrevented) return;
        e.preventDefault(); dragDepth.current += 1; setDragging(true);
      } : undefined}
      onDragOver={canFixFiles ? (e) => {
        if (e.defaultPrevented) return;
        e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      } : undefined}
      onDragLeave={canFixFiles ? (e) => {
        if (e.defaultPrevented) return;
        e.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragging(false);
      } : undefined}
      onDrop={canFixFiles ? (e) => {
        if (e.defaultPrevented) return;
        e.preventDefault(); dragDepth.current = 0; setDragging(false);
        filesFromDataTransfer(e.dataTransfer).then((files) => {
          if (!files.length) {
            fail('Windows did not hand that over as a file. In Outlook, right-click the '
               + 'attachment and choose Copy, then click here and press Ctrl+V. No need to '
               + 'save it to your computer.');
            return;
          }
          onUpload(files);
        });
      } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {/* ⚠️ NOTHING IS LEFT LYING AROUND HALF-ENTERED. Mercedes, 2026-08-31:
            "approvals that get started and not finished save as drafts, and that list
            is confusing... if someone exits a bill they're in the middle of, just pop
            up asking if they're sure they want to leave." Autosave stays exactly as it
            is - it was put in to fix her earlier complaint about the screen reloading
            between fields - so the bill is discarded on the way out instead of never
            being written. The delete is soft, so a mistaken discard is recoverable.
            A bill that has been SENT BACK is not a draft and is never discarded. */}
        {/* ⚠️ BACKING OUT OF A BILL YOU MERELY OPENED MUST NEVER DELETE IT, AND FOR
            TWO DAYS IT DID. Mercedes, 2026-09-03: "I opened the hauler bill that was
            waiting on a name just to see what it looked like, then clicked back to
            Approvals. It warned me changes wouldn't be saved, which is what I wanted,
            but now that freight bill is gone from everything in flight entirely."
            She read the warning correctly - it said her CHANGES would not be saved -
            and it then deleted the whole bill, one Matt had started two days earlier.
            The same thing swallowed four attempts at the Cajun Crab invoice, three of
            them with the yield paperwork already pulled in.
            ⚠️ THE DISCARD IS NOW SCOPED TO A BILL THIS SCREEN JUST CREATED. Her
            original ask (2026-08-31) was that a bill somebody starts and abandons must
            not linger as a draft - it was never that looking at an existing one should
            destroy it. `startedHere` is true only when Start an approval made this row
            moments ago, so an existing draft is now left exactly where it was.
            ⚠️ AND THE WORDING SAYS WHAT HAPPENS. "It will not be saved" reads as
            losing your edits. It deletes the bill, so it says so. */}
        <button className="btn btn-ghost" style={{ fontSize: 13 }} onClick={async () => {
          if (doc && doc.status === 'draft' && startedHere) {
            const started = doc.vendor_name || doc.invoice_number || doc.invoice_amount != null
              || (doc.files || []).some((f) => !f.removed_at);
            const ok = window.confirm(started
              ? 'Discard this bill? It has not been submitted, so it will be deleted and '
                + 'anything entered on it will go with it.'
              : 'Discard this empty bill? Nothing has been entered on it.');
            if (!ok) return;
            try { await neonQuery('ap_document_delete', { documentId, reason: 'Left without submitting' }); }
            catch (e) { /* if it will not delete, still let them out rather than trapping them */ }
            onChanged && onChanged();
          }
          onBack && onBack();
        }}>← All approvals</button>
        <h2 style={{ margin: 0, fontSize: 18, color: 'var(--navy, #00395C)' }}>
          {doc.vendor_name || (isFreight(doc) ? 'Freight bill — hauler not filled in' : isInvoice(doc) ? 'New invoice' : 'New approval')}
          {doc.invoice_number ? ` · ${doc.invoice_number}` : ''}
        </h2>
        <StatusPill status={doc.status} />
        <FreightPill row={doc} />
        <span style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>Approval #{doc.id}</span>
        <button className="btn btn-ghost" onClick={load} style={{ marginLeft: 'auto', fontSize: 12 }}>
          <RefreshCw size={13} />
        </button>
      </div>

      {errMsg && (
        <div style={{ marginBottom: 12 }}>
          <Banner tone="bad" icon={<AlertTriangle size={16} />}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>{errMsg}</div>
              <button onClick={() => setErrMsg('')} title="Dismiss"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  color: 'inherit', opacity: 0.7, lineHeight: 1, fontSize: 16,
                }}>×</button>
            </div>
          </Banner>
        </div>
      )}
      {msg && <div style={{ marginBottom: 12 }}><Banner tone="info">{msg}</Banner></div>}

      {doc.status === 'pending' && (
        <div style={{ marginBottom: 16 }}><StepTrack doc={doc} /></div>
      )}

      {/* Sent back. The reason travels with the document rather than only living
          in the trail, because the person it lands on needs it before they go
          looking for anything. */}
      {doc.status === 'returned' && doc.returned_reason && (
        <div style={{ marginBottom: 16 }}>
          <Banner tone="bad" icon={<CornerUpLeft size={15} />}>
            <strong>{doc.returned_by_name || 'An approver'} sent this back.</strong>{' '}
            {doc.returned_reason}
            <div style={{ marginTop: 6, fontSize: 12.5 }}>
              Sort it out and send it for approval again — it will pick up where it left off.
            </div>
          </Banner>
        </div>
      )}

      {doc.status === 'pending' && Number(doc.days_in_stage) >= Number(doc.aging_threshold || 3) && (
        <div style={{ marginBottom: 16 }}>
          <Banner tone="warn" icon={<Clock size={15} />}>
            This has been sitting with{' '}
            {(doc.steps || []).find(s => s.step_no === doc.current_step)?.approver_name || 'the current approver'}
            {' '}for <strong>{doc.days_in_stage} working days</strong>. {isInvoice(doc) ? 'The vendor gets' : 'Crabbers get'} paid when this moves.
          </Banner>
        </div>
      )}

      {/* ⚠️ SAY WHAT CHANGED, NOT JUST THAT SOMETHING DID. Pulling the yield report
          onto a bill someone had already approved re-hashes the file set and lit
          this banner - correctly, but it reads as "your invoice was swapped" when
          all that happened is a supporting document arrived. If every file added
          since the oldest stale approval is a yield document, the invoice they
          approved is provably untouched, and the banner says so instead. */}
      {staleApprovals && (
        <div style={{ marginBottom: 16 }}>
          {onlyYieldDocsAdded ? (
            <Banner tone="warn" icon={<Paperclip size={15} />}>
              <strong>The yield paperwork was added after this was approved.</strong> The invoice
              itself has not changed, so the approvals below still describe the same bill — but
              the yield report was not in front of them when they signed off.
            </Banner>
          ) : (
            <Banner tone="bad" icon={<AlertTriangle size={15} />}>
              <strong>The paperwork changed after someone approved it.</strong> The approvals below
              were given against a different set of files, so they no longer stand. Whoever signed
              off needs to look again.
            </Banner>
          )}
        </div>
      )}

      <PaidAtPickupBanner doc={doc} forApprover={!!myStep} />

      {/* Upload FIRST while the bill is still being entered, so the reader has
          something to read before anybody starts typing. See the note on
          paperworkCard for why this only moves on the entry form. */}
      {isEditable && paperworkCard}

      {/* Invoice details */}
      <div style={card}>
        <div style={cardHead}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={14} /> The invoice
            <PaidPill row={doc} />
          </span>
        </div>
        <div style={cardBody}>
          {isEditable && scan && (
            <div style={{ marginBottom: 12 }}>
              <ScanBanner scan={scan} onApply={applyScannedFields} onDismiss={() => setScan(null)} />
            </div>
          )}
          {isEditable ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14 }}>
              {/* ⚠️ THE TRIP OWNS THE CRABBER AND THE INVOICE NUMBER once one is
                  picked. Matt Jr: "it's the same information typed twice, which is
                  where typos and mismatches come from." They are still editable -
                  a bill can legitimately carry a different number from the one on
                  the yield sheet - but you have to say so on purpose. */}
              {/* ⚠️ AND DO NOT INVITE THE TYPING IN THE FIRST PLACE. Matt Sr, on a
                  bill with no trip picked yet: "entering in the date & invoice
                  number at the top seems redundant since it's pulled in further
                  down once you select the batch." Two empty boxes at the top of the
                  form are an instruction to fill them in, so before a trip is
                  chosen they are not boxes at all. */}
              {!doc.purchase_order_id && !editIdent && !isFreight(doc) && !isInvoice(doc) ? (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{
                    border: '1px dashed var(--gray-300, #d1d5db)', borderRadius: 6,
                    padding: '10px 14px', fontSize: 13, color: 'var(--gray-600, #4b5563)',
                  }}>
                    Pick the trip below and the crabber and their invoice number fill
                    themselves in.{' '}
                    <button className="btn btn-ghost"
                      style={{ padding: 0, fontSize: 12.5, textDecoration: 'underline' }}
                      onClick={() => setEditIdent(true)}>
                      No trip for this one? Type them in
                    </button>
                  </div>
                </div>
              ) : doc.purchase_order_id && !editIdent && !isFreight(doc) ? (
                <div style={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: 26, alignItems: 'flex-start' }}>
                  <Field label="Crabber / vendor">{doc.vendor_name || '—'}</Field>
                  <Field label="Their invoice #">{doc.invoice_number || '—'}</Field>
                  <div style={{ alignSelf: 'center' }}>
                    <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
                      From the trip below.{' '}
                      <button className="btn btn-ghost"
                        style={{ padding: 0, fontSize: 11.5, textDecoration: 'underline' }}
                        onClick={() => setEditIdent(true)}>
                        Not right? Change them
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <div style={labelStyle}>{isFreight(doc) ? 'Hauler' : isInvoice(doc) ? 'Vendor' : 'Crabber / vendor'}</div>
                    {/* ⚠️ A FREIGHT BILL OFFERS FREIGHT COMPANIES AND NOTHING ELSE. The
                        general list unions every name we have billed with the whole
                        crab_suppliers table, so it used to offer a hundred crabbers on a
                        hauler's bill - which is exactly how the crabber's name kept
                        landing there. A company not on the list is added right here. */}
                    {/* A list of everyone we have billed before, but still a text box:
                        a new crabber can be typed straight in and is on the list next
                        time. Mercedes asked for this because the same crabber typed three
                        slightly different ways is three vendors to the archive search. */}
                    {isInvoice(doc) ? (
                      <>
                        {/* Mercedes: a dropdown seeded from the invoice vendor list, with
                            the option to add a new one that is on the list next time. */}
                        <select style={prefillStyle('vendorName')} value={draft.vendorName || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === '__add__') { setAddingVendor(true); return; }
                            setDraft({ ...draft, vendorName: v }); clearPrefill('vendorName');
                            queueSave({ vendorName: v });
                          }}>
                          <option value="">Pick the vendor…</option>
                          {draft.vendorName
                            && !invoiceVendors.some((v) => v.name === draft.vendorName) && (
                            <option value={draft.vendorName}>{draft.vendorName}</option>
                          )}
                          <option value="__add__">+ Add a new vendor…</option>
                          {invoiceVendors.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
                        </select>
                        {addingVendor && (
                          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                            <input style={{ ...inputStyle, flex: 1 }} autoFocus value={newVendor}
                              placeholder="Their name as it should appear on the bill"
                              onChange={(e) => setNewVendor(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') addInvoiceVendor(); }} />
                            <button className="btn btn-primary" style={{ fontSize: 12 }}
                              onClick={addInvoiceVendor}>Add</button>
                            <button className="btn btn-ghost" style={{ fontSize: 12 }}
                              onClick={() => { setAddingVendor(false); setNewVendor(''); }}>Cancel</button>
                          </div>
                        )}
                      </>
                    ) : isFreight(doc) ? (
                      <>
                        <select style={inputStyle} value={draft.vendorName || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === '__add__') { setAddingHauler(true); return; }
                            setDraft({ ...draft, vendorName: v }); queueSave({ vendorName: v });
                          }}>
                          <option value="">Pick the freight company…</option>
                          {/* A hauler already on this bill but not yet on the list still
                              shows, or editing an older bill would silently blank it. */}
                          {draft.vendorName
                            && !freightVendors.some((v) => v.name === draft.vendorName) && (
                            <option value={draft.vendorName}>{draft.vendorName}</option>
                          )}
                          {freightVendors.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
                          <option value="__add__">+ Add a freight company…</option>
                        </select>
                        {addingHauler && (
                          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                            <input style={{ ...inputStyle, flex: 1 }} autoFocus value={newHauler}
                              placeholder="Their name as it should appear on the bill"
                              onChange={(e) => setNewHauler(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') addFreightVendor(); }} />
                            <button className="btn btn-primary" style={{ fontSize: 12 }}
                              onClick={addFreightVendor}>Add</button>
                            <button className="btn btn-ghost" style={{ fontSize: 12 }}
                              onClick={() => { setAddingHauler(false); setNewHauler(''); }}>Cancel</button>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <input style={prefillStyle('vendorName')} value={draft.vendorName} list="ap-vendor-list"
                          placeholder="Start typing, or pick one you have billed before"
                          onChange={(e) => { setDraft({ ...draft, vendorName: e.target.value }); clearPrefill('vendorName'); queueSave({ vendorName: e.target.value }); }} />
                        <datalist id="ap-vendor-list">
                          {vendors.map((v) => <option key={v.name} value={v.name} />)}
                        </datalist>
                      </>
                    )}
                  </div>
                  <div>
                    <div style={labelStyle}>Their invoice #</div>
                    <input style={prefillStyle('invoiceNumber')} value={draft.invoiceNumber}
                      onChange={(e) => { setDraft({ ...draft, invoiceNumber: e.target.value }); clearPrefill('invoiceNumber'); queueSave({ invoiceNumber: e.target.value }); }} />
                  </div>
                </>
              )}
              <div>
                <div style={labelStyle}>Invoice date</div>
                <div style={{ fontSize: 10.5, color: 'var(--gray-500, #6b7280)', marginBottom: 4, marginTop: -2 }}>
                  the date on their invoice, not the day the load came in
                </div>
                <input type="date" style={prefillStyle('invoiceDate')} value={draft.invoiceDate}
                  onChange={(e) => { setDraft({ ...draft, invoiceDate: e.target.value }); clearPrefill('invoiceDate'); queueSave({ invoiceDate: e.target.value || null }); }} />
              </div>
              <div>
                <div style={labelStyle}>Amount billed</div>
                <input style={prefillStyle('invoiceAmount')} inputMode="decimal" placeholder="0.00" value={draft.invoiceAmount}
                  onChange={(e) => {
                    setDraft({ ...draft, invoiceAmount: e.target.value }); clearPrefill('invoiceAmount');
                    const v = parseMoney(e.target.value);
                    if (v !== undefined) queueSave({ invoiceAmount: v });
                  }} />
              </div>
              {/* ── PAYMENT TERMS AND WHEN IT IS DUE ──────────────────────────
                  Mercedes, 2026-09-24: "Net 30 on a bill dated the 10th sits
                  differently in my week than Due on Receipt."
                  ⚠️ INVOICE BILLS ONLY, on purpose. A crabber gets paid on the
                  crab-buying rhythm and never on terms, and this file already warns
                  that two empty boxes on that form read as an instruction to fill
                  them in. Nothing about the crabber or freight forms changes. */}
              {isInvoice(doc) && (
                <>
                  <div>
                    <div style={labelStyle}>Payment terms</div>
                    <div style={{ fontSize: 10.5, color: 'var(--gray-500, #6b7280)', marginBottom: 4, marginTop: -2 }}>
                      sets the due date for you
                    </div>
                    <select style={prefillStyle('netTerms')} value={draft.netTerms || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDraft({ ...draft, netTerms: v, dueDate: '' });
                        clearPrefill('netTerms');
                        // dueDate is cleared here and re-derived by the server from
                        // the terms, so picking Net 30 moves the date without
                        // anybody typing it. A date she types afterwards sticks.
                        queueSave({ netTerms: v || null, dueDate: null });
                      }}>
                      <option value="">Not stated…</option>
                      {termOptions.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div style={labelStyle}>Due date</div>
                    <div style={{ fontSize: 10.5, color: 'var(--gray-500, #6b7280)', marginBottom: 4, marginTop: -2 }}>
                      {draft.netTerms && draft.dueDate
                        ? 'from the terms — change it if their invoice says otherwise'
                        : 'only if their invoice prints one'}
                    </div>
                    <input type="date" style={prefillStyle('dueDate')} value={draft.dueDate || ''}
                      onChange={(e) => {
                        setDraft({ ...draft, dueDate: e.target.value });
                        clearPrefill('dueDate');
                        queueSave({ dueDate: e.target.value || null });
                      }} />
                  </div>
                </>
              )}
              {/* ⚠️ THE NOTE IS IN THE READER'S OWN VOICE AND IT ADMITS THE FAILURE.
                  Mercedes asked for exactly this: "put a note under it saying
                  'review what I filled in, I can make mistakes'". It NAMES the
                  fields rather than saying "some fields", because the whole job of
                  the note is to tell her which ones to look at - and it disappears
                  as the last one is confirmed, so an empty prompt never sits under
                  a form somebody has already checked. */}
              {prefilledLabels.length > 0 && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{
                    display: 'flex', gap: 9, alignItems: 'flex-start',
                    background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6,
                    padding: '9px 12px', fontSize: 12.5, color: '#92400e', lineHeight: 1.45,
                  }}>
                    <FileText size={14} style={{ marginTop: 1, flexShrink: 0 }} />
                    <span>
                      <strong>Review what I filled in, I can make mistakes.</strong>{' '}
                      I read the {prefilledLabels.join(', ')} off the invoice you
                      uploaded. {prefilledLabels.length === 1 ? 'It is' : 'They are'}{' '}
                      highlighted above. Correcting or re-picking one clears its mark.
                    </span>
                  </div>
                </div>
              )}
              {/* Mercedes, 2026-09-17: Crab & Cruise runs on its own QuickBooks, so
                  a C&C bill must not offer Crab Place's QB categories or read Crab
                  Place's bill history. Ticking this box drops the datalist and the
                  history panel below vanishes. */}
              {isInvoice(doc) && (
                <div style={{
                  gridColumn: '1 / -1', border: '1px solid', borderRadius: 6, padding: 12,
                  background: draft.forCnc ? '#fff7ed' : 'transparent',
                  borderColor: draft.forCnc ? '#fed7aa' : 'var(--gray-200, #e5e7eb)',
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', margin: 0 }}>
                    <input type="checkbox" checked={!!draft.forCnc} style={{ width: 16, height: 16 }}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setDraft({ ...draft, forCnc: on });
                        queueSave({ forCnc: on });
                      }} />
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--navy)' }}>
                      This is a Crab &amp; Cruise bill
                    </span>
                  </label>
                  <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginTop: 6, paddingLeft: 25 }}>
                    Crab &amp; Cruise has its own QuickBooks, so this bill will not pull
                    categories or vendor history from Crab Place.
                  </div>
                </div>
              )}
              {isInvoice(doc) && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={labelStyle}>What is it for?</div>
                  <div style={{ fontSize: 10.5, color: 'var(--gray-500, #6b7280)', marginBottom: 4, marginTop: -2 }}>
                    {draft.forCnc
                      ? 'type it in - Crab & Cruise categories are not pulled from here'
                      : 'the QuickBooks category, so Greg can see who we normally buy it from'}
                  </div>
                  {draft.forCnc ? (
                    <input style={inputStyle}
                      placeholder="What this bill is for"
                      value={draft.invoiceCategory || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDraft({ ...draft, invoiceCategory: value });
                        queueSave({ invoiceCategory: value });
                      }} />
                  ) : (
                    <>
                      <input style={inputStyle} list="ap-invoice-cats"
                        placeholder="Start typing, e.g. Coolers, Shrimp, Dry Ice, Electric"
                        value={(invoiceCategories.find((c) => c.account === draft.invoiceCategory) || {}).label
                          || draft.invoiceCategory || ''}
                        onChange={(e) => {
                          const typed = e.target.value;
                          const hit = invoiceCategories.find((c) => c.label === typed);
                          const value = hit ? hit.account : typed;
                          setDraft({ ...draft, invoiceCategory: value });
                          queueSave({ invoiceCategory: value });
                        }} />
                      <datalist id="ap-invoice-cats">
                        {invoiceCategories.map((c) => <option key={c.account} value={c.label} />)}
                      </datalist>
                    </>
                  )}
                </div>
              )}
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={labelStyle}>Notes (optional)</div>
                <input style={inputStyle} value={draft.notes}
                  onChange={(e) => { setDraft({ ...draft, notes: e.target.value }); queueSave({ notes: e.target.value }); }} />
              </div>

              {/* These crabs went to Crab & Cruise, so there is no yield and that is
                  deliberate. Only offered on a crab bill with no trip picked - with a
                  trip there IS a yield, and a hauler's invoice never had one. Submit
                  refuses a crab bill that has neither, which is what makes the missing
                  yield readable rather than just absent. */}
              {!isFreight(doc) && !isInvoice(doc) && !doc.purchase_order_id && (
                <div style={{
                  gridColumn: '1 / -1', border: '1px solid', borderRadius: 6, padding: 12,
                  background: draft.forCnc ? '#fff7ed' : 'transparent',
                  borderColor: draft.forCnc ? '#fed7aa' : 'var(--gray-200, #e5e7eb)',
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', margin: 0 }}>
                    <input type="checkbox" checked={!!draft.forCnc} style={{ width: 16, height: 16 }}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setDraft({ ...draft, forCnc: on });
                        queueSave({ forCnc: on });
                      }} />
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--navy)' }}>
                      These crabs went to Crab &amp; Cruise
                    </span>
                  </label>
                  <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginTop: 6, paddingLeft: 25 }}>
                    Tick this and the bill can go through without a yield sheet. Everyone
                    who sees it afterwards will know the yield is missing on purpose.
                  </div>
                </div>
              )}

              {/* ⚠️ ALREADY PAID WHEN IT WAS BOUGHT. Mercedes, Oct 21, 2026: Rachael's Amazon
                  order (#571, $6.86) went on Capital One Sparks and then sat on Ready to Pay as
                  if it were owed. The same "already paid" flag the crabber box below uses, so
                  every banner, pill and email that stops a second payment says so here too;
                  the method says how, and Send to QuickBooks enters it as that card's charge. */}
              {isInvoice(doc) && (
              <div style={{
                gridColumn: '1 / -1', border: '1px solid var(--gray-200, #e5e7eb)',
                borderRadius: 6, padding: 12,
                background: draft.paidAtPickup ? '#f4fbf6' : 'transparent',
                borderColor: draft.paidAtPickup ? '#b7e0c6' : 'var(--gray-200, #e5e7eb)',
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', margin: 0 }}>
                  <input type="checkbox" checked={!!draft.paidAtPickup} style={{ width: 16, height: 16 }}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setDraft({ ...draft, paidAtPickup: on });
                      queueSave({ paidAtPickup: on });
                    }} />
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--navy, #00395C)' }}>
                    This was already paid when it was bought
                  </span>
                </label>
                {draft.paidAtPickup ? (
                  <>
                    <div style={{ fontSize: 12, color: 'var(--gray-600, #4b5563)', margin: '8px 0 12px' }}>
                      It still goes through approval. Everyone who sees it is told it is already paid,
                      and it goes into QuickBooks as a charge on the card, not as a bill to pay.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                      <div>
                        <div style={labelStyle}>Paid with</div>
                        <select style={inputStyle} value={draft.paidMethod || ''}
                          onChange={(e) => { setDraft({ ...draft, paidMethod: e.target.value }); queueSave({ paidMethod: e.target.value || null }); }}>
                          <option value="">Pick one…</option>
                          {(doc.paid_method_options || []).map((m) => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <div style={labelStyle}>Date paid</div>
                        <input type="date" style={inputStyle} value={draft.checkDate || ''}
                          onChange={(e) => { setDraft({ ...draft, checkDate: e.target.value }); queueSave({ checkDate: e.target.value || null }); }} />
                      </div>
                      {draft.paidMethod === 'check' && (
                        <div>
                          <div style={labelStyle}>Check #</div>
                          <input style={inputStyle} value={draft.checkNumber || ''}
                            onChange={(e) => { setDraft({ ...draft, checkNumber: e.target.value }); queueSave({ checkNumber: e.target.value }); }} />
                        </div>
                      )}
                    </div>
                    {!draft.paidMethod && (
                      <div style={{ fontSize: 12, color: '#9a4a1c', marginTop: 8 }}>
                        Pick what it was paid with, so accounting can match it to the card statement.
                      </div>
                    )}
                  </>
                ) : null}
              </div>
              )}

              {/* Paid by check at pickup. Entered by whoever enters the invoice, on
                  the same rules as every other field here: editable while it is a
                  draft or has come back, locked once it is moving. */}
              {!isInvoice(doc) && (
              <div style={{
                gridColumn: '1 / -1', border: '1px solid var(--gray-200, #e5e7eb)',
                borderRadius: 6, padding: 12,
                background: draft.paidAtPickup ? '#f4fbf6' : 'transparent',
                borderColor: draft.paidAtPickup ? '#b7e0c6' : 'var(--gray-200, #e5e7eb)',
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', margin: 0 }}>
                  <input type="checkbox" checked={!!draft.paidAtPickup} style={{ width: 16, height: 16 }}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setDraft({ ...draft, paidAtPickup: on });
                      queueSave({ paidAtPickup: on });
                    }} />
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--navy, #00395C)' }}>
                    The driver paid this one by check at pickup
                  </span>
                </label>
                {draft.paidAtPickup ? (
                  <>
                    <div style={{ fontSize: 12, color: 'var(--gray-600, #4b5563)', margin: '8px 0 12px' }}>
                      It still goes through the whole approval chain. Everyone who sees it will be
                      told it is already paid, so nobody pays it a second time.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                      <div>
                        <div style={labelStyle}>Check #</div>
                        <input style={inputStyle} value={draft.checkNumber || ''}
                          onChange={(e) => { setDraft({ ...draft, checkNumber: e.target.value }); queueSave({ checkNumber: e.target.value }); }} />
                      </div>
                      <div>
                        <div style={labelStyle}>Check amount</div>
                        <input style={inputStyle} inputMode="decimal" placeholder="0.00" value={draft.checkAmount || ''}
                          onChange={(e) => {
                            setDraft({ ...draft, checkAmount: e.target.value });
                            const v = parseMoney(e.target.value);
                            if (v !== undefined) queueSave({ checkAmount: v });
                          }} />
                      </div>
                      <div>
                        <div style={labelStyle}>Check date</div>
                        <input type="date" style={inputStyle} value={draft.checkDate || ''}
                          onChange={(e) => { setDraft({ ...draft, checkDate: e.target.value }); queueSave({ checkDate: e.target.value || null }); }} />
                      </div>
                      <div>
                        <div style={labelStyle}>Written out to</div>
                        <input style={inputStyle} placeholder={draft.vendorName || 'Crabber'} value={draft.checkPayee || ''}
                          onChange={(e) => { setDraft({ ...draft, checkPayee: e.target.value }); queueSave({ checkPayee: e.target.value }); }} />
                      </div>
                    </div>
                    {!String(draft.checkNumber || '').trim() && (
                      <div style={{ fontSize: 12, color: '#9a4a1c', marginTop: 8 }}>
                        Put the check number in if you have it. Mercedes matches these against the bank.
                      </div>
                    )}
                    {draft.checkAmount !== '' && draft.checkAmount != null && draft.invoiceAmount !== ''
                      && draft.invoiceAmount != null
                      && Math.abs(Number(draft.checkAmount) - Number(draft.invoiceAmount)) > 0.01 && (
                      <div style={{ fontSize: 12, color: '#9a4a1c', marginTop: 8 }}>
                        The check ({money(draft.checkAmount)}) is not the same as the amount billed
                        ({money(draft.invoiceAmount)}). That may be right, it just gets flagged to the approvers.
                      </div>
                    )}
                  </>
                ) : null}
              </div>
              )}
              {/* Says WHERE the other one is and opens it, rather than naming a number and
                  leaving you to hunt. Mercedes asked for that after this warning fired on
                  bills that had been deleted, where the number led nowhere at all. */}
              {/* ⚠️ AN UNFINISHED DRAFT IS NOT A DOUBLE PAYMENT AND MUST NOT BE
                  WORDED LIKE ONE. A bill in flight means somebody may already be
                  paying this invoice. A draft has never been sent to anybody, so all
                  it means is that the same paperwork got entered twice - which is
                  usually because the first attempt went invisible. Telling Matt Sr
                  he would "pay the same bill twice" over a draft sent him looking
                  for a payment that did not exist. */}
              {dupWarning && (
                <div ref={dupWarningRef} style={{ gridColumn: '1 / -1', scrollMarginTop: 90 }}>
                  <Banner tone="warn" icon={<AlertTriangle size={15} />}>
                    {dupWarning.isDraft ? (
                      <>
                        <strong>You already started this one and never sent it.</strong>
                        {' '}Invoice {doc.invoice_number} for{' '}
                        {isInvoice(doc) ? 'this vendor' : 'this crabber'} is on approval
                        {' '}#{dupWarning.id}, still unfinished. Pick one, or you will have
                        {' '}two bills for the same invoice sitting there.
                        {/* ⚠️ MERCEDES ASKED FOR THE BUTTONS, NOT JUST THE WARNING.
                            2026-10-19: "I need this to tell them you started this already...
                            either finish that one or stay here delete that... click this
                            button to delete the old one and you can continue here or you
                            can click this button and go back to the old one and delete
                            this one." The link to open the old bill was already here and
                            was ignored - a sentence saying "finish that one or discard it"
                            left Matt Sr with the same hunt the warning was supposed to end.
                            ⚠️ TWO CONFIRMS, becase both buttons destroy work. The old draft
                            or the one he is sitting in - whichever he picks is gone. */}
                        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-ghost"
                            disabled={busy}
                            style={{ fontSize: 12.5, color: 'var(--navy)', fontWeight: 600 }}
                            onClick={async () => {
                              if (!window.confirm(
                                `Open approval #${dupWarning.id} and delete this one?\n\n`
                                + `This bill (#${documentId}) will be deleted. Nothing on it will survive.`
                              )) return;
                              setBusy(true);
                              try {
                                // Drop any autosave still in flight for this bill, then delete it.
                                if (timerRef.current) clearTimeout(timerRef.current);
                                pendingRef.current = {};
                                await neonQuery('ap_document_delete', {
                                  documentId,
                                  reason: 'Duplicate of unfinished draft #' + dupWarning.id,
                                });
                                onChanged && onChanged();
                                onOpenDocument && onOpenDocument(dupWarning.id);
                              } catch (e) { fail(e.message); setBusy(false); }
                            }}
                          >
                            Open #{dupWarning.id} and delete this one
                          </button>
                          <button
                            className="btn btn-ghost"
                            disabled={busy}
                            style={{ fontSize: 12.5, color: '#b42318', fontWeight: 600 }}
                            onClick={async () => {
                              if (!window.confirm(
                                `Delete the old draft #${dupWarning.id} and keep working here?\n\n`
                                + `#${dupWarning.id} will be deleted. The paperwork and whatever was typed on it is gone.`
                              )) return;
                              setBusy(true);
                              try {
                                await neonQuery('ap_document_delete', {
                                  documentId: dupWarning.id,
                                  reason: 'Replaced by #' + documentId,
                                });
                                setDupWarning(null);
                                flash('Deleted the old draft #' + dupWarning.id + '. You are good to keep going here.');
                                onChanged && onChanged();
                              } catch (e) { fail(e.message); }
                              setBusy(false);
                            }}
                          >
                            <Trash2 size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />
                            Delete #{dupWarning.id} and keep this one
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <strong>That invoice number is already on approval #{dupWarning.id}</strong> for this
                        crabber{dupWarning.stage ? <>, and it is <strong>{String(dupWarning.stage).toLowerCase()}</strong></> : null}.
                        {' '}Check the number before you send this one, or you will pay the same bill twice.
                        {' '}
                        <a
                          href={`?page=approvals&invoice=${dupWarning.id}`}
                          onClick={(e) => { e.preventDefault(); onOpenDocument && onOpenDocument(dupWarning.id); }}
                          style={{ color: 'var(--navy)', fontWeight: 700 }}
                        >Open approval #{dupWarning.id}</a>
                      </>
                    )}
                  </Banner>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 26 }}>
              {/* ⚠️ "Crabber" ON A FREIGHT BILL IS THE WRONG WORD AND IT IS THE WORD
                  THAT CAUSED THE TROUBLE. Everything the approver reads on a hauler's
                  bill has to name the hauler, or the crabber's name on the trip below
                  becomes the only name on the screen and gets read as the payee. */}
              <Field label={isFreight(doc) ? 'Hauler' : isInvoice(doc) ? 'Vendor' : 'Crabber / vendor'}>
                {doc.vendor_name || '—'}
              </Field>
              {isInvoice(doc) && (
                <Field label="What it is for">
                  {(invoiceCategories.find((c) => c.account === doc.invoice_category) || {}).label
                    || doc.invoice_category || '—'}
                </Field>
              )}
              <Field label="Their invoice #">{doc.invoice_number || '—'}</Field>
              <Field label={isFreight(doc) ? 'Freight invoice date' : 'Invoice date'}>
                {fmtDate(doc.invoice_date)}
              </Field>
              <Field label={isFreight(doc) ? 'Freight amount' : 'Amount billed'}>
                {money(doc.invoice_amount)}
              </Field>
              {/* Readable after submission, because the approver deciding whether to
                  sit on a bill for the weekend is the person who most needs to know
                  it is due Friday. Only rendered when there is something to say - a
                  blank "Terms: —" on every crabber bill is noise. */}
              {isInvoice(doc) && (doc.net_terms || doc.due_date) && (
                <>
                  <Field label="Terms">{doc.net_terms_label || '—'}</Field>
                  <Field label="Due">
                    <DueCell value={doc.due_date} />
                  </Field>
                </>
              )}
              <Field label="Entered by">{doc.submitted_by_name || '—'}</Field>
              {/* Entered in QuickBooks but not paid. The bill is still open, so this
                  says where Mercedes got to rather than implying it is finished. */}
              {doc.status === 'approved' && doc.entered_in_qb_at && (
                <Field label="QuickBooks">
                  <span style={{ color: '#1c7c43', fontWeight: 700 }}>
                    Entered{doc.entered_in_qb_by_name ? ` by ${doc.entered_in_qb_by_name}` : ''}
                  </span>
                  <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
                    Not paid yet
                  </div>
                </Field>
              )}
              {/* The whole point of the flag is that it is readable AFTER submission -
                  an approver looking at a bill with no yield needs to know that is
                  intentional rather than something nobody got round to. On an invoice
                  bill it means something different (books in a separate QuickBooks),
                  so the wording changes. */}
              {doc.for_cnc === true && (
                <Field label={isInvoice(doc) ? 'Company' : 'Yield'}>
                  <span style={{ color: '#9a4a1c', fontWeight: 700 }}>
                    {isInvoice(doc)
                      ? 'Crab & Cruise (separate QuickBooks)'
                      : 'Went to Crab & Cruise, so there is no yield sheet'}
                  </span>
                </Field>
              )}
              {/* ⚠️ ASKED FOR ONLY WHERE IT IS GENUINELY UNKNOWN. A bill on a trip
                  already records how much came in, and a second editable copy of a
                  number is how two answers for one load end up disagreeing. A Crab &
                  Cruise load has no trip at all - measured 2026-09-02, all eight of
                  them carry no purchase order and not one of their invoice numbers
                  exists as a trip at any date, because a C&C load never gets a yield
                  sheet so nobody enters it as a purchase order. So the quantity is
                  nowhere in CPx and there is nothing to derive it from: it lives on
                  the crabber's own invoice, which is sitting on this bill. */}
              {doc.for_cnc === true && !doc.purchase_order_id && (
                <Field label="How much came in">
                  {isEditable ? (
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="number" step="0.01" min="0" placeholder="e.g. 450"
                        style={{ ...inputStyle, width: 110 }}
                        value={draft.quantity == null ? '' : draft.quantity}
                        onChange={(e) => {
                          setDraft({ ...draft, quantity: e.target.value });
                          queueSave({ quantity: e.target.value });
                        }} />
                      <select style={{ ...inputStyle, width: 110 }}
                        value={draft.quantityUnit || ''}
                        onChange={(e) => {
                          setDraft({ ...draft, quantityUnit: e.target.value });
                          queueSave({ quantityUnit: e.target.value });
                        }}>
                        <option value="">unit…</option>
                        <option value="lb">pounds</option>
                        <option value="bu">bushels</option>
                        <option value="boxes">boxes</option>
                      </select>
                    </span>
                  ) : (doc.quantity != null && doc.quantity !== ''
                    ? <span style={{ fontWeight: 700 }}>{Number(doc.quantity).toLocaleString('en-US', { maximumFractionDigits: 1 })} {doc.quantity_unit || ''}</span>
                    : <span style={{ color: 'var(--orange, #DC582A)' }}>not recorded</span>
                  )}
                  {/* ⚠️ SAY WHY IT IS BEING ASKED FOR. A field with no reason on it
                      gets skipped, and then the week's total is wrong in the
                      direction that looks fine. */}
                  <div style={{ fontSize: 11, color: 'var(--gray-500, #6b7280)', marginTop: 4, maxWidth: 340 }}>
                    Off the crabber&rsquo;s invoice below. It is the only place this number
                    exists, and it is what makes the Crab &amp; Cruise week on the approvals
                    list add up.
                  </div>
                </Field>
              )}
              {!isCut(doc) && (
                <Field label="Approved amount">
                  {doc.approved_amount == null ? '—' : (
                    <span style={{ fontWeight: 700, color: 'var(--navy, #00395C)' }}>{money(doc.approved_amount)}</span>
                  )}
                </Field>
              )}
              {doc.notes && <Field label="Notes" style={{ flexBasis: '100%' }}>{doc.notes}</Field>}
            </div>
          )}
          {!isEditable && isCut(doc) && <CutSummary doc={doc} />}
          {doc.id && doc.status !== 'draft' && <InfoRequests documentId={doc.id} />}
          <CheckCopy doc={doc} />
        </div>
      </div>

      {/* ⚠️ SAYS OUT LOUD THAT THIS IS THE HAULER'S BILL, NOT THE CRABBER'S. The trip
          card below it names the crabber - correctly, it is the crabber's trip - and
          without this the two names sit on one screen with nothing saying which one
          is being paid. That is exactly how a White Marsh bill came back reading
          "Hauler: YSC". */}
      {isFreight(doc) && (
        <div style={{ marginBottom: 16 }}>
          <Banner tone={doc.vendor_name ? 'info' : 'warn'} icon={<Truck size={15} />}>
            {doc.vendor_name ? (
              <>
                <strong>This is a freight bill.</strong> It pays <strong>{doc.vendor_name}</strong> for
                hauling the load. The crabber named on the trip below is who sold us the crabs,
                and they are billed separately.
              </>
            ) : (
              <>
                <strong>This freight bill has no hauler on it yet.</strong> Put the freight
                company in the Hauler box above. It is not the crabber named on the trip
                below, and it cannot be sent for approval until it names who is being paid.
              </>
            )}
          </Banner>
        </div>
      )}

      {/* ⚠️ AN INVOICE BILL SHOWS ITS HISTORY WHERE A CRAB BILL SHOWS ITS TRIP.
          Greg, 2026-09-17: he was approving these "blind, on paper, with no
          explanation beyond we needed it, so we ordered it". The history is built
          from past QuickBooks bills whether or not anybody planned the order.
          ⚠️ A C&C BILL SKIPS BOTH PANELS. The context and the buyer coach both
          read Crab Place's QuickBooks mirror, and a C&C bill is billed against a
          different QuickBooks - pulling from ours would either draw a blank on
          every C&C vendor or, worse, match a Crab Place vendor with the same
          name and print somebody else's history beside it. */}
      {isInvoice(doc) && doc.for_cnc === true && (
        <div style={card}>
          <div style={cardHead}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Receipt size={14} /> Crab &amp; Cruise bill
            </span>
          </div>
          <div style={{ padding: 16, fontSize: 13, color: 'var(--gray-600, #4b5563)', lineHeight: 1.5 }}>
            Marked as Crab &amp; Cruise, so the vendor history and buyer coach are
            switched off for this bill. Both read Crab Place's QuickBooks, and Crab
            &amp; Cruise has its own set of books.
          </div>
        </div>
      )}
      {/* ⚠️ THE KEY POINTS SIT ABOVE THE HISTORY AND THE COACH ON PURPOSE.
          Mercedes, 2026-09-18: Greg opened the test invoice she sent him and did
          not see any overlay with key points from the buyer coach's conversation,
          because the chevron overlay only lives on the Waiting on you list. So the
          same summary that pops out of the list overlay now sits at the top of the
          detail page too, before the history "book" and the full coach transcript.
          No "Open the bill" button here (already on the bill). */}
      {isInvoice(doc) && doc.for_cnc !== true && (
        <div style={card}>
          <div style={cardHead}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MessageSquare size={14} /> Key points
            </span>
          </div>
          <KeyPointsOverlay documentId={documentId} />
        </div>
      )}
      {isInvoice(doc) && doc.for_cnc !== true && (
        <div style={card}>
          <div style={cardHead}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Receipt size={14} /> Before you approve: the history behind this bill
            </span>
          </div>
          <InvoiceContext documentId={documentId}
            reloadKey={[doc.vendor_name, doc.invoice_category, doc.invoice_amount,
              doc.invoice_number, doc.invoice_date, doc.buy_plan_id].join('|')} />
        </div>
      )}
      {isInvoice(doc) && doc.for_cnc !== true && (
        <div style={card}>
          <div style={cardHead}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MessageSquare size={14} /> The buyer's plan
            </span>
          </div>
          <InvoiceCoach documentId={documentId}
            vendorName={doc.vendor_name} category={doc.invoice_category}
            canAttach={!['paid', 'rejected'].includes(doc.status)}
            onChanged={() => load(true)} />
        </div>
      )}

      {/* The yield match */}
      {/* ⚠️ A FREIGHT APPROVAL HAS NO CRABBER TRIP AND MUST NOT BE ASKED FOR ONE.
          Freight is started on its own now, so there is nothing to pick. Freight bills
          split off a crabber bill before 2026-09-03 DO carry a trip and keep showing
          it, which is why this tests the trip rather than the kind.
          ⚠️ A CRAB & CRUISE BILL HAS NO TRIP EITHER. Measured 2026-09-02, all eight
          C&C loads carry no purchase order and no trip is ever entered for one,
          because a C&C load never gets a yield sheet. Leaving the picker up invited
          somebody to attach an unrelated crabber trip to a C&C bill. Same shape as
          the freight rule: hide unless a trip is already linked. */}
      {((!isFreight(doc) && !isInvoice(doc) && doc.for_cnc !== true) || doc.purchase_order_id) && (
      <div style={card}>
        <div style={cardHead}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Link2 size={14} /> The trip this bill is for
          </span>
        </div>
        {(!doc.purchase_order_id || picking) ? (
          <TripPicker
            onPick={async (poId) => {
              setPicking(false);
              await saveDraft({ purchaseOrderId: poId });
              // The whole point of item 2: the scans are already in the yield, so
              // picking the trip brings them across without anybody dragging a file.
              await pullYieldDocs(true);
            }}
            onCancel={picking ? () => setPicking(false) : null}
            // A trip held by a bill nobody sent sends you to THAT bill. It already
            // carries the crabber's scans, so finishing it is the job; starting a
            // second one on the same trip would orphan them.
            onOpenBill={(id) => onOpenDocument && onOpenDocument(id)}
          />
        ) : (
          <YieldMatch
            doc={doc}
            liveAmount={isEditable ? draft.invoiceAmount : null}
            canUnlink={isEditable}
            onUnlink={() => setPicking(true)}
            onUseCut={myStep && myStep.can_adjust_amount
              ? (amount, reason) => setCut({ amount: String(amount), reason })
              : null}
            onRefreshTrip={refreshTrip}
            refreshingTrip={refreshingTrip}
            reloadKey={tripReadKey}
            vendors={vendors}
            sourceFiles={liveFiles}
            // The yield report on this bill was priced on the day it was mailed;
            // everything above is priced at today’s list. YieldResults needs the
            // date to be able to say so.
            reportEmailedAt={yieldDocs && yieldDocs.yieldReport
              && yieldDocs.yieldReport.attached
              ? yieldDocs.yieldReport.emailedAt : null}
          />
        )}
      </div>
      )}

      {/* The packet: which crabber loads this hauler's invoice covers. Freight
          bills only - a crabber's bill is checked against its own trip above. */}
      {isFreight(doc) && (
        <div style={card}>
          <div style={cardHead}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Truck size={14} /> The crabber loads this freight covers
            </span>
          </div>
          <FreightLoads doc={doc} canEdit={doc.status !== 'paid'}
                        onOpenDocument={onOpenDocument} />
        </div>
      )}


      {/* What the yield sheet already holds. */}
      {/* Shown whenever the bill is on a trip, not only when the yield holds
          scans: a trip with nothing scanned still wants the yield sheet itself,
          and hiding the card would hide the only button that makes one. */}
      {doc.purchase_order_id && yieldDocs && (
        <div style={{ ...card, borderColor: '#c9dcee' }}>
          <div style={cardHead}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Link2 size={14} /> From the yield sheet
            </span>
            {canFixFiles && (
              <span style={{ display: 'flex', gap: 6 }}>
                {!yieldDocs.frozen && (yieldDocs.pending > 0
                  || (yieldDocs.yieldReport && !yieldDocs.yieldReport.attached && yieldDocs.yieldReport.emailedAt)) && (
                  <button className="btn btn-outline" style={{ fontSize: 12 }}
                    disabled={pulling} onClick={() => pullYieldDocs(false)}>
                    {pulling ? 'Pulling…'
                      : yieldDocs.pending > 0 ? `Pull ${yieldDocs.pending} in`
                      : 'Pull the yield report in'}
                  </button>
                )}
                {/* Only offered when the real one has not turned up. The report
                    that was mailed round is what gets approved against; this is a
                    stand-in for a trip whose report has not gone out yet. */}
                {!(yieldDocs.yieldReport && yieldDocs.yieldReport.attached) && (
                  <button className="btn btn-ghost" style={{ fontSize: 12 }}
                    disabled={pulling} onClick={makeYieldPdf}>
                    {hasYieldPdf ? 'Redo the CPx summary' : 'No report yet? Use a CPx summary'}
                  </button>
                )}
              </span>
            )}
          </div>
          <div style={{ ...cardBody, fontSize: 13 }}>
            {/* ⚠️ THE REPORT IS THE HEADLINE, NOT THE SCANS. Greg, as last approver:
                "I wanna look at it like I would have looked at it had it circulated
                in paper copy." The old copy led with "nothing was scanned into the
                yield sheet", which is a fact about a different thing and read as if
                the yield report were missing. */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '2px 0 8px',
              borderBottom: '1px solid var(--gray-100, #f3f4f6)', marginBottom: 8,
            }}>
              {yieldDocs.yieldReport && yieldDocs.yieldReport.attached
                ? <Check size={15} style={{ color: '#1c7c43' }} />
                : <Paperclip size={15} />}
              {/* ⚠️ A PAPERCLIP THAT OPENS NOTHING IS NOT AN ATTACHMENT. Greg,
                  2026-09-02: "it says there's a little paper clip... but I can't
                  click it." The row said the report was on the bill and gave the
                  approver no way to read it, which on an approval screen is the
                  whole job. */}
              {(() => {
                const label = 'Yield Sheet';
                const f = liveFiles.find(x => x.kind === 'yield-report');
                if (!f) return <span style={{ fontWeight: 600 }}>{label}</span>;
                return (
                  <button className="btn btn-ghost" title="Open the report"
                    style={{
                      padding: 0, fontWeight: 600, fontSize: 13, textAlign: 'left',
                      textDecoration: 'underline', color: 'var(--navy, #00395C)',
                    }}
                    onClick={() => openFile(f)}>{label}</button>
                );
              })()}
              {/* ⚠️ SAY WHERE IT IS, not just whether a box is ticked. "Not pulled
                  in yet" on its own reads as "there isn't one", which is a
                  different and much more alarming fact. */}
              <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--gray-500, #6b7280)', textAlign: 'right' }}>
                {(() => {
                  const r = yieldDocs.yieldReport || {};
                  if (r.attached) {
                    return r.fromEmail
                      ? `on this bill, from the email${r.addedAt ? ` · added ${fmtWhen(r.addedAt)}` : ''}`
                      : 'on this bill';
                  }
                  if (r.notEmailedYet) return 'no report has been emailed for this trip yet';
                  if (r.unknown) return 'could not check the mail just now';
                  if (r.emailedAt) return `emailed ${fmtWhen(r.emailedAt)} · not pulled in yet`;
                  return 'not pulled in yet';
                })()}
              </span>
            </div>

            {/* Secondary, and only worth saying while the invoice is genuinely
                missing - otherwise it is a fact about nothing. */}
            {!(yieldDocs.available || []).length
              && !liveFiles.some(isInvoiceDoc) && (
              <div style={{ color: 'var(--gray-500, #6b7280)' }}>
                No invoice was scanned into the yield sheet for this trip, so the crabber's
                invoice still has to be added below.
              </div>
            )}
            {(yieldDocs.available || []).map((a, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0',
                color: a.attached ? 'var(--gray-500, #6b7280)' : 'var(--navy, #00395C)',
              }}>
                {a.attached ? <Check size={14} style={{ color: '#1c7c43' }} /> : <Paperclip size={14} />}
                <span>{a.kind === 'freight-invoice' ? 'Freight invoice' : 'Crabber invoice'}</span>
                {(() => {
                  const name = String(a.url).split('/').pop();
                  // Matched on source_ref, which is the exact string the pull wrote,
                  // so a row only becomes a link when THIS file is the one on the bill.
                  const f = a.attached && liveFiles.find(x => x.source_ref === a.ref);
                  const style = { fontSize: 11.5, color: 'var(--gray-500, #6b7280)' };
                  if (!f) return <span style={style}>{name}</span>;
                  return (
                    <button className="btn btn-ghost" title="Open it"
                      style={{ ...style, padding: 0, textDecoration: 'underline' }}
                      onClick={() => openFile(f)}>{name}</button>
                  );
                })()}
                <span style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                  {a.attached ? 'already on this bill' : 'not pulled in yet'}
                </span>
              </div>
            ))}
            <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
              The yield report is the PDF everyone was emailed when the yields were
              finished, pulled from that email itself so it is the same document that
              circulated. Anything else here was scanned into the yield sheet when the
              trip was entered. Pulling copies each file onto this bill, so the approval
              is recorded against the exact documents the approver saw even if the yield
              sheet changes later.
            </div>
          </div>
        </div>
      )}


      {/* For everybody past the entry stage the paperwork stays where it has always
          been: after the amount, the history and the trip. */}
      {!isEditable && paperworkCard}

      {/* What goes to QuickBooks, line by line. Shown while the bill is being approved
          too, so the people signing it see the lines and accounts that will be entered -
          the push later copies exactly these and never re-reads the scan. */}
      {!isEditable && <QboLinesCard doc={doc} onChanged={() => load(true)} />}

      {/* Trail */}
      {(doc.approvals || []).length > 0 && (
        <div style={card}>
          <div style={cardHead}><span>What has happened</span></div>
          <div style={cardBody}>
            {doc.approvals.map(a => (
              <div key={a.id} style={{
                display: 'flex', gap: 12, alignItems: 'baseline', padding: '6px 0',
                borderBottom: '1px solid var(--gray-100, #f3f4f6)', fontSize: 13,
              }}>
                <span style={{ minWidth: 130, color: 'var(--gray-500, #6b7280)', fontSize: 12 }}>
                  {fmtWhen(a.created_at)}
                </span>
                <span style={{ fontWeight: 600, minWidth: 120 }}>{a.actor_name}</span>
                <span>
                  {a.action === 'submit' && 'started the approval'}
                  {a.action === 'approve' && 'approved'}
                  {a.action === 'adjust' && (
                    <>approved at <strong>{money(a.amount_approved)}</strong> instead of {money(a.amount_at_action)}</>
                  )}
                  {a.action === 'reject' && 'sent it back'}
                  {/* Entering and paying are two events on the trail now, so a bill
                      that was posted on Tuesday and paid on Friday reads that way
                      months later instead of collapsing into one line. */}
                  {a.action === 'enter' && 'entered it in QuickBooks'}
                  {a.action === 'unenter' && 'undid entered in QuickBooks'}
                  {a.action === 'lines' && 'set the QuickBooks lines'}
                  {a.action === 'link' && 'linked it to its QuickBooks bill'}
                  {a.action === 'not_dup' && 'said a QuickBooks bill is not this one'}
                  {a.action === 'move' && 'moved it to the vendor-invoice approval chain'}
                  {a.action === 'enter_elsewhere' && "recorded it as entered in another company's books"}
                  {a.action === 'pay' && <>marked it paid at <strong>{money(a.amount_approved)}</strong></>}
                  {a.reason ? <span style={{ color: 'var(--gray-600, #4b5563)' }}> — {a.reason}</span> : null}
                  {/* "The paperwork has changed since" only means something about an
                      APPROVAL. Entering a bill in QuickBooks is bookkeeping, not a
                      sign-off, so it is excluded alongside submit and pay. */}
                  {!a.is_current && !['submit', 'pay', 'enter', 'unenter', 'lines', 'link', 'not_dup', 'move', 'enter_elsewhere'].includes(a.action) && (
                    <span style={{ color: '#b42318', fontSize: 11.5, marginLeft: 8 }}>
                      (paperwork has changed since)
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ ...card, position: 'sticky', bottom: 0, boxShadow: '0 -2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ ...cardBody, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {isEditable && isInvoice(doc) && (
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', cursor: 'pointer',
              fontSize: 13, color: 'var(--navy, #00395C)', margin: 0,
            }}>
              <input type="checkbox" checked={attest} style={{ width: 16, height: 16, marginTop: 2 }}
                onChange={(e) => setAttest(e.target.checked)} />
              <span>
                <strong>I approve this bill.</strong> It is what we ordered and the amount is right.
                {' '}<span style={{ color: 'var(--gray-500, #6b7280)' }}>
                  You are the first approver on an invoice, and your name goes on the record.
                </span>
              </span>
            </label>
          )}
          {isEditable && (
            <>
              {/* ⚠️ A DISABLED <button> DISPATCHES NO CLICK EVENT - not on itself,
                  not on its parents - so an earlier attempt to catch the click on a
                  wrapping <div onClick> never fired, and Matt Sr sat clicking a dead
                  button with the hint text he had already read beside it. Keep the
                  button clickable when there are blockers and refuse the submit
                  inside the handler, which is also where the paperwork card is
                  scrolled to and the red banner raised. */}
              <button className="btn btn-primary"
                disabled={busy || (isInvoice(doc) && !attest)}
                onClick={async () => {
                  if (submitBlockers.length > 0) {
                    setSubmitAttempted(true);
                    if (missingInvoice) {
                      paperworkCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                    return;
                  }
                  // Flush anything still sitting in the debounce before submitting,
                  // or the last field typed would not be part of what is sent.
                  if (timerRef.current) clearTimeout(timerRef.current);
                  await flushSave();
                  setBusy(true);
                  try {
                    await neonQuery('ap_document_submit',
                      isInvoice(doc) ? { documentId, attest: attest === true } : { documentId });
                    onChanged && onChanged();
                    if (onAfterSubmit) {
                      onAfterSubmit(doc && doc.bill_kind);
                    } else {
                      await load(true);
                      flash('Sent for approval.');
                    }
                  } catch (e) {
                    fail(e.message);
                    // ⚠️ THE REFUSAL HAS TO LEAD SOMEWHERE. A duplicate refused at
                    // submit used to be a bare sentence naming a bill you then had to
                    // go and find, which on an invisible draft meant you could not.
                    // The banner it raises carries the link.
                    const b = e && e.body;
                    if (b && b.needsNote) setNoteAsk({ message: e.message, text: '' });
                    if (b && b.duplicateOf) {
                      setDupWarning({
                        id: b.duplicateOf, stage: b.duplicateStage,
                        isDraft: b.duplicateIsDraft === true,
                      });
                    }
                  }
                  setBusy(false);
                }}>
                Send for approval
              </button>
              {/* ⚠️ THE NOTE IS ASKED FOR WHERE THE REFUSAL HAPPENED. Greg, Oct 21, 2026:
                  flag it when they put it in, "this is higher than normal, can you give
                  me a note explaining why?" Saved, then the send is retried in one click,
                  so the person entering it never goes hunting for where the note lives. */}
              {noteAsk && (
                <div style={{
                  flexBasis: '100%', background: '#fff7ed', border: '1px solid #fed7aa',
                  borderRadius: 6, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                  <span style={{ color: '#9a4a1c', fontSize: 12.5 }}>{noteAsk.message}</span>
                  <textarea className="form-input" rows={2} autoFocus value={noteAsk.text}
                    onChange={(e) => setNoteAsk({ ...noteAsk, text: e.target.value })}
                    placeholder="e.g. ordered extra, a lot of frozen going out that week"
                    style={{ width: '100%', resize: 'vertical', fontSize: 13 }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" style={{ fontSize: 12.5 }}
                      disabled={busy || !noteAsk.text.trim()}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          await neonQuery('ap_document_note', { documentId, note: noteAsk.text.trim() });
                          await neonQuery('ap_document_submit',
                            isInvoice(doc) ? { documentId, attest: attest === true } : { documentId });
                          setNoteAsk(null);
                          onChanged && onChanged();
                          if (onAfterSubmit) onAfterSubmit(doc && doc.bill_kind);
                          else { await load(true); flash('Sent for approval.'); }
                        } catch (e2) { fail(e2.message); }
                        setBusy(false);
                      }}>
                      Save the note and send
                    </button>
                    <button className="btn btn-outline" style={{ fontSize: 12.5 }} disabled={busy}
                      onClick={() => setNoteAsk(null)}>Cancel</button>
                  </div>
                </div>
              )}
              <span style={{
                fontSize: 12.5,
                color: submitBlockers.length ? '#9a4a1c' : 'var(--gray-500, #6b7280)',
              }}>
                {submitBlockers.length
                  ? `Add ${
                      submitBlockers.length === 1
                        ? submitBlockers[0]
                        : submitBlockers.length === 2
                          ? submitBlockers.join(' and ')
                          : `${submitBlockers.slice(0, -1).join(', ')} and ${submitBlockers[submitBlockers.length - 1]}`
                    } first.`
                  : `It goes to ${firstApproverName} first.`}
              </span>
              {saveState !== 'idle' && (
                <span style={{ fontSize: 12, color: 'var(--gray-400, #9ca3af)', marginLeft: 'auto' }}>
                  {saveState === 'saving' ? 'Saving…' : 'Saved'}
                </span>
              )}
            </>
          )}

          {myStep && !cut && !noteRequired && (
            <>
              <button className="btn btn-primary" disabled={busy}
                onClick={() => doAction('approve')}>
                <Check size={14} style={{ marginRight: 6 }} />
                Approve {doc.approved_amount != null ? money(doc.approved_amount) : money(doc.invoice_amount)}
              </button>
              {myStep.can_adjust_amount && (
                <button className="btn btn-outline" disabled={busy}
                  onClick={() => setCut({ amount: '', reason: '' })}>
                  <Scissors size={14} style={{ marginRight: 6 }} />
                  Approve a different amount
                </button>
              )}
              <button className="btn btn-ghost" disabled={busy}
                style={{ color: '#b42318' }}
                onClick={() => { setDenying(null); setSendBack({ reason: '' }); }}>
                <X size={14} style={{ marginRight: 6 }} />
                {isInvoice(doc) ? 'Ask for more' : 'Send it back'}
              </button>
              {isInvoice(doc) && (
                <button className="btn btn-ghost" disabled={busy}
                  style={{ color: '#b42318' }}
                  onClick={() => { setSendBack(null); setDenying({ reason: '' }); }}>
                  <Ban size={14} style={{ marginRight: 6 }} />
                  Deny
                </button>
              )}
            </>
          )}

          {myStep && noteRequired && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', width: '100%' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={labelStyle}>
                  {noteRequired.message} (required — it will land on the approval trail)
                </div>
                <input style={inputStyle} autoFocus value={noteRequired.reason}
                  onChange={(e) => setNoteRequired({ ...noteRequired, reason: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && noteRequired.reason.trim()) {
                      doAction('approve', { reason: noteRequired.reason.trim() });
                    }
                  }} />
              </div>
              <button className="btn btn-primary"
                disabled={busy || !noteRequired.reason.trim()}
                onClick={() => doAction('approve', { reason: noteRequired.reason.trim() })}>
                <Check size={14} style={{ marginRight: 6 }} />
                Approve {doc.approved_amount != null ? money(doc.approved_amount) : money(doc.invoice_amount)}
              </button>
              <button className="btn btn-ghost" onClick={() => setNoteRequired(null)}>Cancel</button>
            </div>
          )}

          {myStep && denying && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', width: '100%' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={labelStyle}>
                  Why is it denied? (required — {doc.submitted_by_name || 'whoever entered it'} will see this, and it will not be paid)
                </div>
                <input style={inputStyle} autoFocus value={denying.reason}
                  onChange={(e) => setDenying({ reason: e.target.value })} />
              </div>
              <button className="btn btn-primary" style={{ background: '#b42318', borderColor: '#b42318' }}
                disabled={busy || !denying.reason.trim()}
                onClick={async () => { await doAction('deny', { reason: denying.reason.trim() }); setDenying(null); }}>
                Deny this invoice
              </button>
              <button className="btn btn-ghost" onClick={() => setDenying(null)}>Cancel</button>
            </div>
          )}

          {myStep && sendBack && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', width: '100%' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={labelStyle}>
                  {isInvoice(doc) ? 'What do you need to know?' : 'What needs sorting out?'} (required — {backTarget} will see this)
                </div>
                <input style={inputStyle} autoFocus value={sendBack.reason}
                  onChange={(e) => setSendBack({ reason: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && sendBack.reason.trim()) {
                      doAction('reject', { reason: sendBack.reason.trim() });
                    }
                  }} />
              </div>
              <button className="btn btn-primary" style={{ background: '#b42318', borderColor: '#b42318' }}
                disabled={busy || !sendBack.reason.trim()}
                onClick={() => doAction('reject', { reason: sendBack.reason.trim() })}>
                Send it back to {backTarget}
              </button>
              <button className="btn btn-ghost" onClick={() => setSendBack(null)}>Cancel</button>
            </div>
          )}

          {myStep && cut && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', width: '100%' }}>
              <div style={{ width: 150 }}>
                <div style={labelStyle}>Approve this much</div>
                <input style={inputStyle} inputMode="decimal" autoFocus value={cut.amount}
                  onChange={(e) => setCut({ ...cut, amount: e.target.value })} />
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={labelStyle}>Why (required — this goes on the record)</div>
                <input style={inputStyle} value={cut.reason}
                  onChange={(e) => setCut({ ...cut, reason: e.target.value })} />
              </div>
              <button className="btn btn-primary"
                disabled={busy || parseMoney(cut.amount) == null || !cut.reason.trim()}
                onClick={() => doAction('adjust', { amount: parseMoney(cut.amount), reason: cut.reason.trim() })}>
                Approve {parseMoney(cut.amount) != null ? money(parseMoney(cut.amount)) : ''}
              </button>
              <button className="btn btn-ghost" onClick={() => setCut(null)}>Cancel</button>
            </div>
          )}

          {doc.status === 'pending' && !myStep && (
            <span style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>
              Waiting on {(doc.steps || []).find(s => s.step_no === doc.current_step)?.approver_name || 'the next approver'}.
            </span>
          )}

          {/* ⚠️ AN APPROVER IS NOT THE BOOKKEEPER. Greg, looking at a bill he had
              just given final approval to: "then I get this button, entered it,
              closes out... I think that's more of a message for her. Gotta do
              something besides tell me to enter it in because I don't do the
              books." Closing a bill out is an accounting action, so it is offered
              to the person the chain names as the payer and to nobody else. What
              everyone else needs at that moment is confirmation that their part is
              finished and somewhere to go next. */}
          {doc.status === 'approved' && !actor.isPayer && (
            <>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: '#e8f5ec', border: '1px solid #b7e0c6', color: '#14532d',
                borderRadius: 6, padding: '9px 14px', fontSize: 13.5, fontWeight: 600,
              }}>
                <Check size={16} />
                Fully approved. Nothing else needed from you.
              </span>
              <span style={{ fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>
                {isPrepaid(doc)
                  ? `It was already paid by check at pickup, so no payment is waiting. ${payerName} just records it against the books.`
                  : `It is with ${payerName} to enter and pay ${money(doc.approved_amount)}.`}
              </span>
              <button className="btn btn-outline" style={{ marginLeft: 'auto' }} onClick={onBack}>
                Back to approvals
              </button>
            </>
          )}

          {doc.status === 'approved' && actor.isPayer && (
            <>
              {/* ── ENTERED IN QUICKBOOKS, THEN PAID. TWO CLICKS. ──────────────
                  Mercedes, 2026-09-24: "entering a bill in QuickBooks and paying it
                  are not the same day... Right now they collapse into one click and
                  I lose the middle state." She does entry in the morning and pays
                  that afternoon.
                  ⚠️ NOT OFFERED ON A BILL PAID BY CHECK AT PICKUP. That money left
                  weeks ago and closing it out is a single bookkeeping act - a
                  two-step entered-then-paid on it would be inventing a payment that
                  is not going to happen. */}
              {!isPrepaid(doc) && (
                <button className="btn btn-outline" disabled={busy}
                  style={doc.entered_in_qb_at ? {
                    borderColor: '#b7e0c6', background: '#e8f5ec', color: '#1c7c43',
                  } : undefined}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      const d = await neonQuery('ap_document_mark_entered', {
                        documentId, undo: !!doc.entered_in_qb_at,
                      });
                      setDoc(d); onChanged && onChanged();
                      flash(doc.entered_in_qb_at
                        ? 'Back to not entered.' : 'Marked entered in QuickBooks.');
                    } catch (e) { fail(e.message); }
                    setBusy(false);
                  }}>
                  {doc.entered_in_qb_at
                    ? <><CheckCircle2 size={14} style={{ marginRight: 6 }} />Entered in QuickBooks</>
                    : <><FileText size={14} style={{ marginRight: 6 }} />Entered in QuickBooks</>}
                </button>
              )}
              <button className="btn btn-primary" disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const d = await neonQuery('ap_document_mark_paid', { documentId });
                    setDoc(d); onChanged && onChanged();
                    flash(isPrepaid(doc) ? 'Closed out.' : 'Marked paid.');
                  } catch (e) { fail(e.message); }
                  setBusy(false);
                }}>
                <DollarSign size={14} style={{ marginRight: 6 }} />
                {isPrepaid(doc) ? 'Entered it — close this out'
                  : doc.entered_in_qb_at ? 'Paid — money has left' : 'Mark it paid'}
              </button>
              <span style={{ fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>
                {isPrepaid(doc)
                  ? `Already paid by check${doc.check_number ? ` #${String(doc.check_number).trim()}` : ''}. This just records ${money(doc.approved_amount)} against it and closes it.`
                  : `Pay ${money(doc.approved_amount)}${doc.approved_amount != null && doc.invoice_amount != null
                      && Number(doc.approved_amount) !== Number(doc.invoice_amount)
                        ? ` — cut from the ${money(doc.invoice_amount)} billed` : ''}.`}
                {!isPrepaid(doc) && (
                  <>
                    {' '}
                    {doc.entered_in_qb_at && doc.entered_in_company
                      ? `Entered in ${doc.entered_in_company}'s books, so it is not sent to QuickBooks here.`
                      : doc.entered_in_qb_at
                      ? `Entered in QuickBooks${doc.entered_in_qb_by_name
                          ? ` by ${doc.entered_in_qb_by_name}` : ''} on ${fmtWhen(doc.entered_in_qb_at)}.`
                      : 'Mark it entered when you post it in QuickBooks; it stays on Ready to pay until the money leaves.'}
                    {doc.due_date && (
                      <>
                        {' '}Due <strong><DueCell value={doc.due_date} /></strong>.
                      </>
                    )}
                  </>
                )}
              </span>
            </>
          )}

          {doc.status === 'paid' && (
            <span style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>
              {isPrepaid(doc)
                ? `Paid by check at pickup${checkLine(doc) ? ` (${checkLine(doc)})` : ''}. Closed out by ${doc.paid_by_name} on ${fmtWhen(doc.paid_at)}.`
                : `Paid ${money(doc.approved_amount)} by ${doc.paid_by_name} on ${fmtWhen(doc.paid_at)}.`}
              {doc.entered_in_company && (
                ` Entered in ${doc.entered_in_company}'s books, so it is not sent to QuickBooks here.`
              )}
            </span>
          )}

          {doc.status === 'rejected' && (
            <span style={{ fontSize: 13, color: '#b42318' }}>
              {isInvoice(doc)
                ? `Denied${doc.returned_by_name ? ` by ${doc.returned_by_name}` : ''}${doc.returned_reason ? `: ${doc.returned_reason}` : '.'} It will not be paid.`
                : 'Sent back. Start a fresh approval once it is sorted out with the crabber.'}
            </span>
          )}

          {/* ⚠️ DELETING IS FOR A BILL ENTERED BY MISTAKE, AND NOTHING ELSE. It
              never touches the trip or the yield, an approved or paid one refuses
              (that is the record of who authorised money leaving), and it is a soft
              delete server-side so the wrong click is recoverable. Kept to the far
              right, quiet, and behind a confirm that names what is going.
              ⚠️ A BILL THAT CAME FROM A PLAN GETS A MODAL, NOT A CONFIRM. Mercedes,
              2026-09-18: "when someone deletes a bill that started as a plan and a PO
              we need an option for it to go back to being a PO or a plan or to fully
              delete it." Otherwise the plan is orphaned as invoiced with no invoice,
              and the only way to re-enter is to make a fresh plan from scratch. */}
          {canDelete && (
            doc.buy_plan_id
              ? <PlanBillDeleteButton doc={doc} documentId={documentId} busy={busy}
                  setBusy={setBusy} fail={fail} onChanged={onChanged} onBack={onBack} />
              : <button className="btn btn-ghost" disabled={busy}
                  style={{ marginLeft: 'auto', fontSize: 12.5, color: '#b42318' }}
                  onClick={async () => {
                    const what = doc.vendor_name
                      ? `${doc.vendor_name}${doc.invoice_number ? ` invoice ${doc.invoice_number}` : ''}`
                      : 'this empty bill';
                    if (!window.confirm(
                      `Delete ${what}?\n\nThis removes the bill only. The trip and the yield sheet are untouched, `
                      + `and the trip becomes available to bill again.`)) return;
                    setBusy(true);
                    try {
                      await neonQuery('ap_document_delete', { documentId });
                      onChanged && onChanged();
                      onBack && onBack();
                    } catch (e) { fail(e.message); }
                    setBusy(false);
                  }}>
                  <Trash2 size={13} style={{ marginRight: 5 }} />
                  Delete this bill
                </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Deleting an invoice that came from a plan. Mercedes, 2026-09-18: "when someone deletes
// a bill that started as a plan and a PO we need an option for it to go back to being a
// PO or a plan or to fully delete it or archive it." The plan lives on ap_buy_plans and
// carries lifecycle markers inside its own messages JSONB (see lib/invoice-coach.js
// stateOf), so reverting is a matter of stripping the right marker and clearing the plan-
// to-bill link; the bill itself is still soft-deleted afterwards, so a wrong click is
// recoverable either way.
//
// ⚠️ TWO CALLS, IN ORDER: revert the plan first, then delete the bill. Doing it the other
// way would leave the plan pointing at a deleted bill for a moment, and the plan tabs
// filter live plans by state; a mid-flight refresh would show it as a ghost. If the second
// call fails the plan is only DETACHED, never wrongly re-stated, so trying delete again is
// safe.
function PlanBillDeleteButton({ doc, documentId, busy, setBusy, fail, onChanged, onBack }) {
  const [open, setOpen] = React.useState(false);
  const what = doc.vendor_name
    ? `${doc.vendor_name}${doc.invoice_number ? ` invoice ${doc.invoice_number}` : ''}`
    : 'this bill';
  const run = async (to) => {
    setBusy(true);
    try {
      await apInvoiceCoach('revert', { planId: doc.buy_plan_id, to });
      await neonQuery('ap_document_delete', { documentId });
      setOpen(false);
      onChanged && onChanged();
      onBack && onBack();
    } catch (e) { fail(e.message); }
    setBusy(false);
  };
  return (
    <>
      <button className="btn btn-ghost" disabled={busy}
        style={{ marginLeft: 'auto', fontSize: 12.5, color: '#b42318' }}
        onClick={() => setOpen(true)}>
        <Trash2 size={13} style={{ marginRight: 5 }} />
        Delete this bill
      </button>
      {open && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }} onClick={() => !busy && setOpen(false)}>
          <div style={{
            background: '#fff', borderRadius: 10, maxWidth: 520, width: '100%',
            padding: 24, boxShadow: '0 10px 32px rgba(0,0,0,0.25)',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--navy, #00395C)', marginBottom: 6 }}>
              Delete {what}?
            </div>
            <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)', marginBottom: 18 }}>
              This bill came from a plan you thought through in the coach. Pick what should happen to that plan.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button className="btn btn-outline" disabled={busy}
                style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '10px 14px' }}
                onClick={() => run('po')}>
                <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 2 }}>
                  Turn it back into a PO
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
                  The plan goes back onto My POs, ready to convert to an invoice again when the real bill arrives.
                </div>
              </button>
              <button className="btn btn-outline" disabled={busy}
                style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '10px 14px' }}
                onClick={() => run('plan')}>
                <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 2 }}>
                  Turn it back into a plan
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
                  The plan goes back onto Plan an Order for more thinking. Nothing was ordered.
                </div>
              </button>
              <button className="btn btn-outline" disabled={busy}
                style={{
                  justifyContent: 'flex-start', textAlign: 'left', padding: '10px 14px',
                  color: '#b42318', borderColor: '#f1c9c3',
                }}
                onClick={() => run('archive')}>
                <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 2 }}>
                  Delete the bill and the plan
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
                  Both go off every list. The record stays in the database so accounting can recover it if needed.
                </div>
              </button>
            </div>
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button className="btn btn-ghost" disabled={busy} onClick={() => setOpen(false)}
                style={{ fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── inbox ────────────────────────────────────────────────────────────────────

// One week of Crab & Cruise buying, as a single row that opens.
//
// ⚠️ IT IS A SUMMARY OF BILLS THAT STILL EACH NEED APPROVING, so the count of
// bills is shown on the group and every one of them is still reachable underneath.
// Collapsing them into a line that could be signed off in one click would be a
// different and much worse feature: these are separate crabbers owed separate
// money.
// `lead` is true when the table carries the key-points chevron column, so this row and
// the bills under it start one cell in and stay under the same headings as every other
// row. Without it the week total sat under "Invoice #" and the age chip fell off the
// end of the card.
function CncWeekRow({ week, rows, columns, onOpen, onChanged, actor, lead }) {
  const [open, setOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const total = rows.reduce((a, r) => a + (Number(r.invoice_amount) || 0), 0);
  const units = totalUnits(rows);
  const amountAt = Math.max(0, columns.findIndex(c => c.key === 'a'));
  const unitBits = Object.entries(units.by)
    .map(([u, n]) => `${n.toLocaleString('en-US', { maximumFractionDigits: 1 })} ${u}`);
  return (
    <>
      <tr style={{ borderTop: '1px solid var(--gray-100, #f3f4f6)', cursor: 'pointer',
        background: open ? '#f7fbff' : 'transparent' }}
        onClick={() => setOpen(v => !v)}>
        {/* ⚠️ THE TOTAL HAS TO SIT UNDER "Billed", NOT AT A FIXED OFFSET FROM THE
            END. Counting back from the last column meant that adding the Units
            column silently slid the money under "Entered by". The amount column is
            found by key, so the row cannot drift again when another is added. */}
        <td style={{ padding: '9px 12px' }} colSpan={Math.max(1, amountAt) + (lead ? 1 : 0)}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <strong>Hard crabs</strong>
            <span style={{ color: 'var(--gray-500, #6b7280)' }}>{weekLabel(week)}</span>
            <span style={{
              background: '#eef4fa', color: '#00395C', border: '1px solid #c9dcee',
              fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 999,
              textTransform: 'uppercase', letterSpacing: '0.4px',
            }}>{rows.length} {rows.length === 1 ? 'bill' : 'bills'}</span>
          </span>
          <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginTop: 3, marginLeft: 22 }}>
            {rows.length} {rows.length === 1 ? 'crabber' : 'crabbers'}
            {gradeSummary(sumGrades(rows)) && (
              <> · <strong>{gradeSummary(sumGrades(rows))}</strong> held for the event</>
            )}
            {/* ⚠️ WHAT IS NOT COUNTED, SAID OUT LOUD. A week total quietly missing
                three of seven bills reads as the whole week, and whether the week
                looks normal is the only reason this row exists. */}
            {(() => {
              const none = rows.filter(r => !Object.keys(r.grades || {}).length);
              if (!none.length) return null;
              return (
                <span style={{ color: 'var(--orange, #DC582A)', fontWeight: 700 }}>
                  {' · '}{none.length} still unread
                </span>
              );
            })()}
            {/* ⚠️ SAY WHAT IS NOT COUNTED. A total that quietly leaves out four of
                five bills reads as the whole week, and the whole point of this row
                is whether the week looks normal. */}
            {units.missing > 0 && (
              <span style={{ color: 'var(--orange, #DC582A)', fontWeight: 700 }}>
                {unitBits.length > 0 ? ' · ' : ''}
                {units.missing} with no quantity recorded
              </span>
            )}
          </div>
        </td>
        <td style={{ padding: '9px 12px', fontWeight: 700 }}>{money(total)}</td>
        {columns.length - amountAt - 1 > 0 && (
          <td style={{ padding: '9px 12px' }} colSpan={columns.length - amountAt - 1}>
            {/* ⚠️ ONE CLICK, BUT STILL ONE APPROVAL PER BILL. Greg: "you could also
                give me an approve all for the Crab & Cruise row." These are separate
                crabbers owed separate money, so each is approved on its own through
                the same handler as the button inside the bill - every rule about who
                may approve what, and every trail entry, is the one that already
                exists. What is saved is the fourteen clicks, not the record.
                ⚠️ It names the money in the confirm, because a mis-click here is
                seven approvals rather than one. */}
            <button className="btn btn-outline" style={{ fontSize: 12 }}
              disabled={approving}
              onClick={async (e) => {
                e.stopPropagation();
                const ok = window.confirm(
                  `Approve all ${rows.length} Crab & Cruise bills for the ${weekLabel(week)}, `
                  + `totalling ${money(total)}?`);
                if (!ok) return;
                setApproving(true);
                let done = 0; const failed = [];
                for (const r of rows) {
                  try {
                    // The same handler the button inside the bill uses, so every rule
                    // about who may approve what is the one that already exists.
                    await neonQuery('ap_document_action', { documentId: r.id, action: 'approve' });
                    done++;
                  } catch (err) {
                    failed.push(`${r.vendor_name || r.id}: ${err.message}`);
                  }
                }
                setApproving(false);
                onChanged && onChanged();
                // ⚠️ SAY WHICH ONES DID NOT GO THROUGH. A partial run reported as
                // success leaves a crabber unpaid and nobody looking for them.
                if (failed.length) {
                  window.alert(`${done} approved. ${failed.length} did not go through:\n\n`
                    + failed.join('\n'));
                }
              }}>
              {approving ? 'Approving…' : `Approve all ${rows.length}`}
            </button>
          </td>
        )}
      </tr>
      {open && rows.map(r => (
        <tr key={r.id} style={{ borderTop: '1px solid var(--gray-100, #f3f4f6)',
          cursor: 'pointer', background: '#fcfdff' }}
          onClick={() => onOpen(r.id)}>
          {lead && <td style={{ width: 28 }} />}
          {columns.map((c, i) => (
            <td key={c.key} style={{ padding: '8px 12px', paddingLeft: i === 0 && !lead ? 34 : 12 }}>
              {c.render(r)}
              {i === 0 && (() => {
                const q = billQuantity(r);
                return (
                  <div style={{ fontSize: 11, color: q ? 'var(--gray-500, #6b7280)' : 'var(--orange, #DC582A)', marginTop: 2 }}>
                    {q ? `${q.qty.toLocaleString('en-US', { maximumFractionDigits: 1 })} ${q.unit}`
                       : 'how much came in is not recorded'}
                  </div>
                );
              })()}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// The key-points overlay Greg gets on his Waiting on you list. Mercedes, 2026-09-18:
// "he's going to need a layover on his list of approvals waiting on him that kind of
// gives him the key points of everything that was discussed so he's not trying to read a
// book." Fetches the bill's context (which already carries the plan) and renders the
// keyPoints array; a bill with no plan says so honestly.
//
// ⚠️ FETCHED ON EXPAND, NOT UP FRONT. Loading a context for every row in the list burns
// through Neon on a page that mostly sits idle - and the overlay is a "poke this one to
// see", not a wall.
// ── This order: size, pattern, QuickBooks account, and the buyer's note ─────
// Greg, Oct 21, 2026: "What I'm really looking for here is what do we normally buy, and
// is it out of range... and we buy once a week, so I want to know that it fits the
// pattern... and Matt's got a note on it that we ordered extra. That would make this
// approval a lot easier." Plus: "tell me what account this is going to post to when it
// gets to QuickBooks." Everything here is computed in lib/invoice-context.js
// (orderPattern); this only prints it.
// ⚠️ A CUT BILL READS AS THE CUT, NOT AS THE INVOICE. Greg, Oct 22, 2026, on Chester River
// #100545: billed $1,025, Matt Sr took $440 off for 4 bu. of poor #1's, and the screen still
// led with "$1,025.00" - "I'm just scared if I approve it, I don't know what amount she's
// going to pay." So a cut bill shows billed, the credit with who took it off and why, and
// the total that gets paid, in that order: the same three lines QuickBooks gets.
// The Billed cell on the approval lists. A cut bill shows what is being paid, with the
// invoice figure under it, so a $1,025 bill cut to $585 never reads as $1,025 (Greg,
// Oct 22, 2026, Chester River #100545).
function BilledCell({ r }) {
  if (!isCut(r)) return money(r.invoice_amount);
  return (
    <span>
      <strong style={{ color: 'var(--orange, #DC582A)' }}>{money(r.approved_amount)}</strong>
      <div style={{ fontSize: 11, color: 'var(--gray-500, #6b7280)', whiteSpace: 'nowrap' }}>
        cut from <span style={{ textDecoration: 'line-through' }}>{money(r.invoice_amount)}</span>
      </div>
    </span>
  );
}
const isCut = (doc) => doc && doc.approved_amount != null && doc.invoice_amount != null
  && Number(doc.approved_amount) < Number(doc.invoice_amount) - 0.004;
function lastCut(doc) {
  const cuts = (doc.approvals || []).filter((a) => a.action === 'adjust' && a.amount_approved != null);
  return cuts.length ? cuts[cuts.length - 1] : null;
}
function CutSummary({ doc }) {
  const cut = lastCut(doc);
  const off = Number(doc.invoice_amount) - Number(doc.approved_amount);
  const row = { display: 'flex', justifyContent: 'space-between', gap: 16, padding: '3px 0', fontSize: 13.5 };
  return (
    <div style={{ marginTop: 14, maxWidth: 520, border: '1px solid #fed7aa', background: '#fffaf5', borderRadius: 8, padding: '10px 14px' }}>
      <div style={row}><span>Billed on the invoice</span><span>{money(doc.invoice_amount)}</span></div>
      <div style={{ ...row, color: '#9a4a1c' }}>
        <span>
          Credit{cut && cut.actor_name ? `, taken off by ${cut.actor_name}` : ''}
          {cut && cut.reason && (
            <div style={{ fontSize: 12, color: 'var(--gray-600, #4b5563)' }}>{cut.reason}</div>
          )}
        </span>
        <span style={{ whiteSpace: 'nowrap' }}>−{money(off)}</span>
      </div>
      <div style={{ ...row, borderTop: '1px solid #fed7aa', marginTop: 4, paddingTop: 6, fontWeight: 700, color: 'var(--navy, #00395C)' }}>
        <span>To pay</span><span>{money(doc.approved_amount)}</span>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginTop: 4 }}>
        Ready to Pay and QuickBooks both use {money(doc.approved_amount)}. QuickBooks gets the bill as
        billed with the credit as its own line, so it still matches the crabber&rsquo;s invoice.
      </div>
    </div>
  );
}

const PATTERN_TONE = {
  ok: { fg: '#1c7c43', bar: '#1c7c43' },
  warn: { fg: '#9a4a1c', bar: 'var(--orange, #DC582A)' },
  info: { fg: 'var(--navy, #00395C)', bar: '#9bb7d1' },
};
// ─── Ask for more information ─────────────────────────────────────────────────
// Greg, Oct 22, 2026: "I'd like the ability to request more information. And then that
// kicks an email to him. He's got to fill it out. I get notified that it's been updated,
// and then I can come back to it and approve it." The bill does not move: a question is
// not a send-back. The person asked answers here (the email link opens this bill), and
// whoever asked is emailed the answer.
const fmtAskedWhen = (v) => (v ? new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
function InfoRequests({ documentId, tick = 0, compact = false }) {
  const [data, setData] = useState(null);
  const [asking, setAsking] = useState(false);
  const [q, setQ] = useState('');
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [n, setN] = useState(0);
  useEffect(() => {
    let alive = true;
    neonQuery('ap_info_list', { documentId }).then((d) => { if (alive) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, [documentId, tick, n]);
  if (!data) return null;
  const reqs = data.requests || [];
  const who = (data.enteredBy && data.enteredBy.name) || 'whoever entered it';
  const run = async (fn) => {
    setBusy(true); setErr('');
    try { await fn(); setN((x) => x + 1); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  if (!reqs.length && !data.open) return null;
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: compact ? 8 : 14 }}>
      {reqs.length > 0 && (
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500, #6b7280)', marginBottom: 4 }}>Questions on this bill</div>
      )}
      {reqs.map((r) => (
        <div key={r.id} style={{ border: '1px solid ' + (r.answered_at ? '#b7e0c6' : '#fed7aa'), background: r.answered_at ? '#f2f8f4' : '#fffaf5',
          borderRadius: 6, padding: '6px 10px', margin: '5px 0', fontSize: 12.5 }}>
          <div><strong>{r.asked_by_name}</strong> asked {r.asked_to_name}: {r.question}
            <span style={{ color: 'var(--gray-500, #6b7280)', fontSize: 11.5 }}> ({fmtAskedWhen(r.asked_at)})</span></div>
          {r.answered_at ? (
            <div style={{ marginTop: 3 }}><strong>{r.answered_by_name}:</strong> {r.answer}
              <span style={{ color: 'var(--gray-500, #6b7280)', fontSize: 11.5 }}> ({fmtAskedWhen(r.answered_at)})</span></div>
          ) : r.mine ? (
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <textarea className="form-input" rows={2} placeholder="Your answer" value={answers[r.id] || ''}
                onChange={(e) => setAnswers({ ...answers, [r.id]: e.target.value })} style={{ width: '100%', fontSize: 12.5 }} />
              <div>
                <button className="btn btn-primary" style={{ fontSize: 12 }} disabled={busy || !(answers[r.id] || '').trim()}
                  onClick={() => run(() => neonQuery('ap_info_answer', { requestId: r.id, answer: answers[r.id] }))}>
                  {busy ? 'Sending…' : `Answer ${r.asked_by_name}`}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 3, color: '#9a4a1c' }}>
              Waiting on {r.asked_to_name}&rsquo;s answer. They were emailed.
              {r.askedByMe && (
                <button className="btn btn-ghost" style={{ fontSize: 12, padding: '0 0 0 8px' }} disabled={busy}
                  onClick={() => run(() => neonQuery('ap_info_cancel', { requestId: r.id }))}>Take it back</button>
              )}
            </div>
          )}
        </div>
      ))}
      {data.open && !asking && (
        <button className="btn btn-outline" style={{ fontSize: 12, marginTop: 4 }} onClick={() => setAsking(true)}>
          Ask {who} for more information
        </button>
      )}
      {asking && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
          <textarea className="form-input" rows={2} autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. Why is this one $1,500 when it is usually $750?" style={{ width: '100%', fontSize: 12.5 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" style={{ fontSize: 12 }} disabled={busy || !q.trim()}
              onClick={() => run(async () => { await neonQuery('ap_info_ask', { documentId, question: q }); setQ(''); setAsking(false); })}>
              {busy ? 'Sending…' : `Email ${who} the question`}
            </button>
            <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => setAsking(false)} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}
      {err && <div style={{ color: '#b42318', fontSize: 12, marginTop: 4 }}>{err}</div>}
    </div>
  );
}

// Questions waiting on me, and answers to mine, at the top of the page.
function QuestionsBanner({ onOpen }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    neonQuery('ap_info_mine', {}).then((d) => setRows((d && d.requests) || [])).catch(() => {});
  }, []);
  if (!rows.length) return null;
  const forMe = rows.filter((r) => r.for_me);
  const answered = rows.filter((r) => !r.for_me);
  const line = (r, text) => (
    <div key={r.id} style={{ fontSize: 13, margin: '3px 0' }}>
      <button className="btn btn-ghost" style={{ padding: 0, fontSize: 13, fontWeight: 700, color: 'var(--navy, #00395C)', textDecoration: 'underline' }}
        onClick={() => onOpen(r.document_id)}>
        {r.vendor_name}{r.invoice_number ? ` #${r.invoice_number}` : ''}
      </button>{' '}{text}
    </div>
  );
  return (
    <div style={{ marginBottom: 16, border: '1px solid #fed7aa', background: '#fffaf5', borderRadius: 8, padding: '10px 14px' }}>
      {forMe.length > 0 && (
        <>
          <div style={{ fontWeight: 700, color: '#9a4a1c', marginBottom: 4 }}>
            {forMe.length === 1 ? 'A question is' : `${forMe.length} questions are`} waiting on your answer
          </div>
          {forMe.map((r) => line(r, `: ${r.asked_by_name} asked "${r.question}"`))}
        </>
      )}
      {answered.length > 0 && (
        <>
          <div style={{ fontWeight: 700, color: '#1c7c43', margin: forMe.length ? '8px 0 4px' : '0 0 4px' }}>
            Answered, ready for you to look at again
          </div>
          {answered.map((r) => line(r, `: ${r.answered_by_name} said "${r.answer}"`))}
        </>
      )}
    </div>
  );
}

// ─── Account card + vendor card, filled in from the bill ───────────────────────
// Greg, Oct 22, 2026, on Delmarva Power: "this account number goes to the 504 Maryland
// Avenue facility... let's build in a place that we can just kind of fill these in on the
// fly instead of doing it as a one-time seed." And: "If there's two to fill out at the same
// time, let's just knock them both out." So one box: which account this is (company and
// QuickBooks account, one click from what the books already show), and what the vendor
// sells when their vendor card has not been confirmed. Saving either teaches every bill
// after it.
const COMPANY_NAMES = { cp: 'Crab Place', cnc: 'Crab & Cruise' };

// The whole vendor card, opened in place: what they sell, their range and cadence, the
// accounts they post to, and the facts - payment terms, sales rep, freight, lead time,
// minimum order. Greg, Oct 22, 2026: "I was expecting much more information on the card...
// What about terms? ... maybe a rep name." Same card as Accounting > Vendors.
function VendorCardToggle({ vendorKey, label }) {
  const [open, setOpen] = useState(false);
  const [card, setCard] = useState(null);
  const [books, setBooks] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open]);
  const show = async () => {
    setOpen(true);
    if (!card) apVendorProfiles('get', { key: vendorKey }).then((r) => setCard(r.vendor)).catch((e) => setErr(e.message));
    if (!books) apVendorProfiles('books', { key: vendorKey }).then((r) => setBooks(r)).catch((e) => setBooks({ books: null, why: e.message }));
  };
  if (!vendorKey) return null;
  const row = (k, val) => (val == null || val === '' ? null : (
    <div style={{ display: 'flex', gap: 12, padding: '3px 0', fontSize: 13 }}>
      <span style={{ width: 150, flexShrink: 0, color: 'var(--gray-600, #4b5563)' }}>{k}</span>
      <span style={{ flex: 1, minWidth: 0 }}>{val}</span>
    </div>
  ));
  const b = books && books.books;
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 6 }}>
      <button type="button" className="btn btn-ghost" style={{ padding: 0, fontSize: 12, textDecoration: 'underline' }} onClick={show}>
        {label || 'Open the full vendor card: terms, rep, contact, how we pay them'}
      </button>
      {open && (
        <div onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '40px 16px', overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 10, width: 'min(860px, 100%)', padding: '18px 22px', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <strong style={{ fontSize: 16, color: 'var(--navy, #00395C)' }}>{(card && card.name) || (b && b.displayName) || 'Vendor card'}</strong>
              <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => setOpen(false)}>Close</button>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500, #6b7280)', margin: '4px 0' }}>FROM QUICKBOOKS</div>
            {!books && <div style={{ fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>Reading the vendor in QuickBooks…</div>}
            {books && !b && <div style={{ fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>{books.why}</div>}
            {b && (
              <div style={{ marginBottom: 12 }}>
                {row('Name in QuickBooks', b.displayName + (b.companyName && b.companyName !== b.displayName ? ' (' + b.companyName + ')' : ''))}
                {row('Payment terms', b.terms || 'not set in QuickBooks')}
                {row('How we pay them', b.paidBy)}
                {row('Our account # with them', b.ourAccountNumber)}
                {row('Contact', b.contact)}
                {row('Phone', [b.phone, b.mobile && 'mobile ' + b.mobile].filter(Boolean).join(', '))}
                {row('Email', b.email)}
                {row('Website', b.web)}
                {row('Address', b.address)}
                {row('1099 vendor', b.vendor1099 ? 'Yes' : 'No')}
                {row('Tax ID (EIN/TIN)', b.taxIdLast4 ? 'on file, ends in ' + b.taxIdLast4 : 'not on file')}
                {row('Open balance', b.balance == null ? null : money(b.balance))}
                <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginTop: 4 }}>
                  QuickBooks is the record for these; change them there.{' '}
                  <a href={b.link} target="_blank" rel="noreferrer">Open in QuickBooks</a>.
                  Bank (ACH) details are not shown: Intuit does not share them, and keeping them out of CPx is safer.
                </div>
              </div>
            )}
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500, #6b7280)', margin: '8px 0 4px' }}>IN CPx</div>
            {err ? <span style={{ color: '#b42318', fontSize: 12 }}>{err}</span>
              : card ? <VendorProfileCard vendor={card} onChanged={(v) => v && setCard(v)} />
                : <span style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>Loading the card…</span>}
          </div>
        </div>
      )}
    </div>
  );
}
function CardsBox({ documentId, data, onSaved }) {
  const meter = data && data.meter;
  const profile = data && data.profile;
  const vendorUnconfirmed = !!(profile && !profile.reviewed && data.document && data.document.vendorKey);
  const needAccount = !!(meter && !meter.card);
  const [open, setOpen] = useState(needAccount);
  const [opts, setOpts] = useState(null);
  const [f, setF] = useState({ accountNumber: (meter && meter.accountNumber) || '', label: '', company: 'cp', accountId: '' });
  const [what, setWhat] = useState((profile && profile.what) || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!open || !meter || opts) return;
    neonQuery('ap_service_account_options', { documentId }).then((o) => {
      setOpts(o);
      const c = o.card;
      setF((x) => ({
        accountNumber: x.accountNumber || o.accountNumber || '',
        label: c ? c.label : x.label,
        company: c ? c.company : (o.guess ? o.guess.company : x.company),
        accountId: c ? String(c.qboAccountId) : (o.guess ? String(o.guess.accountId) : x.accountId),
      }));
    }).catch((e) => setErr(e.message));
  }, [open, meter, opts, documentId]);
  if (!meter && !vendorUnconfirmed) return null;

  const save = async () => {
    setBusy(true); setErr('');
    try {
      if (meter && (needAccount || open)) {
        await neonQuery('ap_service_account_save', { documentId, ...f });
      }
      if (vendorUnconfirmed && what.trim()) {
        await apVendorProfiles('confirm', { key: data.document.vendorKey, name: data.document.vendor, what: what.trim() });
      }
      setOpen(false); setOpts(null);
      onSaved && onSaved();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const box = { border: '1px solid #fed7aa', background: '#fffaf5', borderRadius: 8, padding: '8px 12px', margin: '0 0 10px', fontSize: 12.5 };
  const lab = { fontSize: 11, fontWeight: 700, color: 'var(--gray-500, #6b7280)', margin: '8px 0 3px' };

  // A card that exists, not being changed: one line.
  if (meter && meter.card && !open && !vendorUnconfirmed) {
    const c = meter.card;
    return (
      <div onClick={(e) => e.stopPropagation()} style={{ ...box, background: '#f7fafc', border: '1px solid #dbe4ee' }}>
        <strong>Account {c.accountNumber}:</strong> {c.label}, {c.companyName}, posts to{' '}
        <strong>{String(c.qboAccountName || '').replace(/:/g, ' › ')}</strong>
        {c.setByName ? <span style={{ color: 'var(--gray-500, #6b7280)' }}> (card by {c.setByName})</span> : null}
        <button className="btn btn-ghost" style={{ fontSize: 12, padding: '0 0 0 8px' }} onClick={() => setOpen(true)}>Change</button>
      </div>
    );
  }

  const sugg = opts ? (opts.suggestions[f.company] || []) : [];
  const all = opts ? (opts.accounts[f.company] || []) : [];
  const knownLabels = opts ? [...new Set((opts.known || []).map((k) => k.label))] : [];
  return (
    <div onClick={(e) => e.stopPropagation()} style={box}>
      <div style={{ fontWeight: 700, color: '#9a4a1c' }}>
        {needAccount && vendorUnconfirmed ? 'Fill in the account card and the vendor card'
          : needAccount || open ? 'Which account is this?' : 'Confirm the vendor card'}
      </div>
      {meter && (needAccount || open) && (
        <>
          <div style={{ color: 'var(--gray-600, #4b5563)', marginTop: 2 }}>
            {data.document.vendor} bills more than one account. Say which this is once and every bill for it
            codes itself, lands in the right company and is compared with its own past bills.
            {meter.known && meter.known.length > 0 && ` Cards so far: ${meter.known.map((k) => k.label).join(', ')}.`}
          </div>
          <div style={lab}>Account number on the bill</div>
          <input className="form-input" value={f.accountNumber} onChange={(e) => setF({ ...f, accountNumber: e.target.value })}
            placeholder="e.g. 5500 1408 982" style={{ fontSize: 12.5, maxWidth: 220 }} />
          <div style={lab}>What it is for</div>
          <input className="form-input" list={`acct-labels-${documentId}`} value={f.label}
            onChange={(e) => setF({ ...f, label: e.target.value })}
            placeholder="e.g. 504 Maryland Ave plant" style={{ fontSize: 12.5, maxWidth: 320 }} />
          <datalist id={`acct-labels-${documentId}`}>
            {[...knownLabels, '504 Maryland Ave plant', 'Main St offices', 'Condo', "Byrd's building (7th St)", "Byrd's apartment"]
              .filter((v, i, a) => a.indexOf(v) === i).map((v) => <option key={v} value={v} />)}
          </datalist>
          <div style={lab}>Whose books</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {['cp', 'cnc'].map((co) => (
              <button key={co} type="button" className={f.company === co ? 'btn btn-primary' : 'btn btn-outline'}
                style={{ fontSize: 12 }} onClick={() => setF({ ...f, company: co, accountId: '' })}>{COMPANY_NAMES[co]}</button>
            ))}
          </div>
          <div style={lab}>Posts to</div>
          {!opts && !err && <div style={{ color: 'var(--gray-500, #6b7280)' }}>Reading where their bills have gone…</div>}
          {opts && sugg.map((s) => (
            <label key={s.id} style={{ display: 'flex', gap: 6, alignItems: 'baseline', margin: '2px 0', cursor: 'pointer' }}>
              <input type="radio" name={`acct-${documentId}`} checked={f.accountId === s.id} onChange={() => setF({ ...f, accountId: s.id })} />
              <span><strong>{s.name.replace(/:/g, ' › ')}</strong>
                <span style={{ color: 'var(--gray-500, #6b7280)' }}> - {s.bills} bills, usually about {money(s.typical)}
                  {opts.guess && opts.guess.accountId === s.id && opts.guess.company === f.company ? ' (closest to this bill)' : ''}</span>
              </span>
            </label>
          ))}
          {opts && (
            <select className="form-input" value={sugg.some((s) => s.id === f.accountId) ? '' : f.accountId}
              onChange={(e) => setF({ ...f, accountId: e.target.value })} style={{ fontSize: 12.5, maxWidth: 420, marginTop: 4 }}>
              <option value="">{sugg.length ? 'Or another account…' : 'Pick the account…'}</option>
              {all.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
        </>
      )}
      {vendorUnconfirmed && (
        <>
          <div style={lab}>What {data.document.vendor} sells us (the vendor card)</div>
          <input className="form-input" value={what} onChange={(e) => setWhat(e.target.value)}
            placeholder="e.g. Electric service for the plant, offices, condo and Byrd's" style={{ fontSize: 12.5, width: '100%' }} />
          <VendorCardToggle vendorKey={data.document.vendorKey} />
        </>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={save}
          disabled={busy || (meter && (needAccount || open) && (!f.label.trim() || !f.accountId || !f.accountNumber.trim()))
            || (vendorUnconfirmed && !(meter && (needAccount || open)) && !what.trim())}>
          {busy ? 'Saving…' : (needAccount || open) && vendorUnconfirmed ? 'Save both cards' : 'Save the card'}
        </button>
        {meter && meter.card && open && (
          <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
        )}
        {err && <span style={{ color: '#b42318', fontSize: 12 }}>{err}</span>}
      </div>
    </div>
  );
}

// One line's QuickBooks account, changed where it is seen (or every line at once). Greg,
// Oct 22, 2026: ABS's copier lease was headed for Hard Crabs and Cherrystone's sample
// oysters for Clams, and "we just would want a little tool to be able to change the
// account." Saved as the person's pick, which the coder reads first on the next bill.
let accountListCache = {};
function LineAccountPicker({ documentId, line, lineCount, onDone, onCancel }) {
  const [list, setList] = useState(accountListCache[documentId] || null);
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (list) return;
    apQboPush('accounts', { documentId })
      .then((r) => { accountListCache[documentId] = r.accounts || []; setList(r.accounts || []); })
      .catch((e) => setErr(e.message));
  }, [documentId, list]);
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = (list || []).filter((a) => words.every((w) => a.name.toLowerCase().includes(w))).slice(0, 12);
  const pick = async (a) => {
    setBusy(true); setErr('');
    try {
      await apQboPush('set_line_account', { documentId, lineNo: line.lineNo, accountId: a.id, all });
      onDone();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  return (
    <div onClick={(e) => e.stopPropagation()}
      style={{ flexBasis: '100%', margin: '4px 0 8px', padding: 10, border: '1px solid var(--gray-300, #d1d5db)', borderRadius: 6, background: '#fff' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="form-input" autoFocus placeholder="Type to find an account, e.g. samples"
          value={q} onChange={(e) => setQ(e.target.value)} style={{ fontSize: 12.5, flex: '1 1 240px' }} />
        {lineCount > 1 && (
          <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
            every line on this bill
          </label>
        )}
        <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
      {!list && !err && <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', marginTop: 6 }}>Loading the accounts…</div>}
      {list && (
        <div style={{ marginTop: 6, maxHeight: 220, overflowY: 'auto' }}>
          {shown.map((a) => (
            <button key={a.id} type="button" disabled={busy} onClick={() => pick(a)}
              className="btn btn-ghost"
              style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 12.5, padding: '4px 6px',
                fontWeight: a.name === line.accountFull ? 700 : 400 }}>
              {a.name.replace(/:/g, ' › ')}
            </button>
          ))}
          {!shown.length && <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>No account matches that.</div>}
        </div>
      )}
      {err && <div style={{ color: '#b42318', fontSize: 12, marginTop: 4 }}>{err}</div>}
    </div>
  );
}

function OrderPattern({ documentId, pattern, onSaved }) {
  const [picking, setPicking] = useState(null);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!pattern) return null;
  const items = pattern.items || [];
  const accounts = pattern.accounts || [];
  const unusual = (pattern.unusual || []).length > 0;
  const note = pattern.note;
  if (!items.length && !accounts.length && !pattern.accountNote && !pattern.reading && !unusual && !note) return null;
  const save = async () => {
    setBusy(true); setErr('');
    try {
      await neonQuery('ap_document_note', { documentId, note: text });
      setEditing(false);
      onSaved && onSaved();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const head = { fontSize: 11, fontWeight: 700, color: 'var(--gray-500, #6b7280)', marginBottom: 4 };
  return (
    <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
      <div style={head}>This order</div>
      {items.map((it) => {
        const t = PATTERN_TONE[it.tone] || PATTERN_TONE.info;
        return (
          <div key={it.key} style={{ borderLeft: `3px solid ${t.bar}`, padding: '3px 10px', margin: '5px 0', fontSize: 12.5 }}>
            <strong style={{ color: t.fg }}>{it.label}: </strong>{it.text}
          </div>
        );
      })}
      {/* ⚠️ THE NOTE SITS RIGHT UNDER THE NUMBER IT EXPLAINS. "Twice our usual order"
          and "we ordered extra, a lot of frozen going out" belong side by side. */}
      {(unusual || note) && (
        <div style={{ margin: '6px 0', fontSize: 12.5 }}>
          {note && !editing && (
            <div style={{ background: '#f2f8f4', border: '1px solid #b7e0c6', borderRadius: 6, padding: '6px 10px' }}>
              <strong>Why: </strong>{note.text}
              <span style={{ color: 'var(--gray-500, #6b7280)' }}>
                {note.byName ? ` (${note.byName})` : ''}
              </span>
              <button className="btn btn-ghost" style={{ padding: '0 0 0 8px', fontSize: 12 }}
                onClick={() => { setText(note.text); setEditing(true); }}>Edit</button>
            </div>
          )}
          {!note && !editing && (
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, padding: '6px 10px', color: '#9a4a1c' }}>
              Higher than normal, and nobody has said why yet.
              <button className="btn btn-ghost" style={{ padding: '0 0 0 8px', fontSize: 12 }}
                onClick={() => { setText(''); setEditing(true); }}>Add the reason</button>
              {/* Or ask the person who entered it: they are emailed, their answer becomes
                  the reason, and whoever asked is emailed back (Greg, Oct 22, 2026). */}
              <button className="btn btn-ghost" style={{ padding: '0 0 0 8px', fontSize: 12 }} disabled={busy}
                onClick={async () => {
                  setBusy(true); setErr('');
                  try {
                    await neonQuery('ap_info_ask', { documentId, kind: 'unusual',
                      question: 'This bill is higher than normal (' + (pattern.unusual || [])[0] + '). Why?' });
                    onSaved && onSaved();
                  } catch (e) { setErr(e.message); }
                  setBusy(false);
                }}>Ask who entered it why</button>
              {err && !editing && <span style={{ color: '#b42318', fontSize: 12, marginLeft: 8 }}>{err}</span>}
            </div>
          )}
          {editing && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <textarea className="form-input" rows={2} autoFocus value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. ordered extra, a lot of frozen going out that week"
                style={{ width: '100%', resize: 'vertical', fontSize: 12.5 }} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="btn btn-primary" style={{ fontSize: 12 }} disabled={busy || !text.trim()} onClick={save}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button className="btn btn-outline" style={{ fontSize: 12 }} disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
                {err && <span style={{ color: '#b42318', fontSize: 12 }}>{err}</span>}
              </div>
            </div>
          )}
        </div>
      )}
      {(accounts.length > 0 || pattern.accountNote || pattern.reading) && (
        <div style={{ marginTop: 6 }}>
          <div style={head}>Posts to QuickBooks as</div>
          {pattern.reading && (
            <div style={{ fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>Reading the lines off the invoice…</div>
          )}
          {!pattern.reading && accounts.map((a, i) => (
            <div key={i} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, fontSize: 12.5, padding: '2px 0' }}>
              <span>
                {a.ours && (
                  <strong style={{ color: 'var(--navy, #00395C)', marginRight: 6 }}>{a.ours.code}</strong>
                )}
                {a.description || 'Line ' + (i + 1)}
                {a.ours && a.ours.eachPrice != null && (
                  <span style={{ color: 'var(--gray-600, #4b5563)' }}>
                    {' '}= {a.ours.eachCount != null ? a.ours.eachCount.toLocaleString('en-US') + ' ' : ''}
                    @ ${a.ours.eachPrice.toFixed(2)} each{a.ours.perCase ? ` (${a.ours.perCase} to a case)` : ''}
                  </span>
                )}
              </span>
              <span style={{ whiteSpace: 'nowrap' }}>
                {money(a.amount)} to{' '}
                {pattern.canChangeAccounts && a.lineNo != null ? (
                  <button type="button" className="btn btn-ghost" title="Change the account"
                    onClick={() => setPicking(picking === a.lineNo ? null : a.lineNo)}
                    style={{ padding: 0, fontSize: 12.5, fontWeight: 700, textDecoration: 'underline dotted',
                      color: a.account ? 'var(--navy, #00395C)' : '#9a4a1c' }}>
                    {a.account || 'pick an account'}
                  </button>
                ) : (
                  <strong style={{ color: a.account ? 'var(--navy, #00395C)' : '#9a4a1c' }}>
                    {a.account || 'account not picked yet'}
                  </strong>
                )}
              </span>
              {picking === a.lineNo && (
                <LineAccountPicker documentId={documentId} line={a} lineCount={accounts.length}
                  onDone={() => { setPicking(null); onSaved && onSaved(); }}
                  onCancel={() => setPicking(null)} />
              )}
            </div>
          ))}
          {!pattern.reading && !accounts.length && pattern.accountNote && (
            <div style={{ fontSize: 12.5, color: 'var(--gray-600, #4b5563)' }}>{pattern.accountNote}</div>
          )}
          {!pattern.reading && accounts.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--gray-500, #6b7280)', marginTop: 2 }}>
              The same accounts Ready to Pay will send.
              {pattern.canChangeAccounts ? ' Click an account to change it; the next bill from them learns from it.' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KeyPointsOverlay({ documentId, onOpen }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [reading, setReading] = useState(false);
  const readTriedRef = React.useRef(false);
  useEffect(() => {
    let alive = true;
    apInvoiceContext(documentId).then((d) => {
      if (!alive) return;
      setData(d); setLoading(false);
    }).catch((e) => { if (alive) { setErr(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [documentId, tick]);
  // ⚠️ THE LINES ARE READ ONCE, HERE, THE FIRST TIME ANYONE LOOKS. They are the same
  // lines Ready to Pay would read anyway (lib/ap-qbo-push.js readInvoiceLines, saved on
  // the bill), so reading them early costs nothing later and gives this panel the
  // pounds and the QuickBooks account. Quiet on failure: the panel still shows what it
  // has, and the server records why so it is not retried forever.
  useEffect(() => {
    if (!data || !data.pattern || !data.pattern.canReadLines || readTriedRef.current) return;
    readTriedRef.current = true;
    setReading(true);
    apQboPush('read_lines', { documentId })
      .catch(() => {})
      .finally(() => { setReading(false); setTick((t) => t + 1); });
  }, [data, documentId]);
  if (loading) {
    return (
      <div style={{ padding: 14, fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>
        Reading the plan and the recent history…
      </div>
    );
  }
  if (err) {
    return <div style={{ padding: 14, fontSize: 12.5, color: '#b42318' }}>Could not read the key points: {err}</div>;
  }
  if (!data) return null;
  const plan = data.plan;
  const flags = (data.flags || []).filter((f) => f.key !== 'no_plan').slice(0, 3);
  const kp = (plan && plan.keyPoints) || [];
  return (
    <div style={{ padding: 14, fontSize: 13, color: 'var(--gray-800, #1f2937)' }}>
      {plan ? (
        <>
          <div style={{ fontSize: 12.5, color: 'var(--gray-600, #4b5563)', marginBottom: 8 }}>
            <strong>{plan.title || 'Plan'}</strong>{plan.startedBy ? `, started by ${plan.startedBy}` : ''}
          </div>
          {kp.length === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--gray-500, #6b7280)', marginBottom: 8 }}>
              The plan is on the bill but does not yet have a discussion to summarise.
            </div>
          )}
          {kp.map((k, i) => (
            <div key={i} style={{
              borderLeft: `3px solid ${k.role === 'coach' ? 'var(--navy, #00395C)' : 'var(--orange, #DC582A)'}`,
              padding: '4px 10px', margin: '6px 0',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500, #6b7280)', marginBottom: 2 }}>
                {k.label}
              </div>
              {k.bullets ? (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {k.bullets.map((b, j) => <li key={j} style={{ margin: '2px 0' }}>{b}</li>)}
                </ul>
              ) : (
                <div>{k.text}</div>
              )}
            </div>
          ))}
        </>
      ) : (
        <div style={{ fontSize: 12.5, color: 'var(--gray-500, #6b7280)', marginBottom: 8 }}>
          Nobody talked this one through with the buyer coach, so the history-only view on
          the bill is all there is.
        </div>
      )}
      <CardsBox documentId={documentId} data={data} onSaved={() => setTick((t) => t + 1)} />
      <OrderPattern documentId={documentId}
        pattern={data.pattern ? { ...data.pattern, reading } : null}
        onSaved={() => setTick((t) => t + 1)} />
      {(flags.length > 0 || (data.profile && data.profile.text)) && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500, #6b7280)', marginBottom: 4 }}>
            What history says
          </div>
          {/* What the vendor is, from its profile (Accounting > Vendors), first. */}
          {data.profile && data.profile.text && (
            <div style={{ fontSize: 12.5, margin: '3px 0', color: 'var(--gray-800, #1f2937)' }}>
              • {data.profile.text}
              {!data.profile.reviewed && (
                <span style={{ color: '#9a4a1c', fontSize: 11.5 }}> (vendor not yet confirmed)</span>
              )}
            </div>
          )}
          {data.profile && data.profile.reviewed && data.document && (
            <VendorCardToggle vendorKey={data.document.vendorKey} label="Open the vendor card" />
          )}
          {flags.map((f) => (
            <div key={f.key} style={{ fontSize: 12.5, margin: '3px 0', color: '#4b5563' }}>
              • {f.text}
            </div>
          ))}
        </div>
      )}
      <InfoRequests documentId={documentId} tick={tick} compact />
      {onOpen && (
        <div style={{ marginTop: 10 }}>
          <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => onOpen(documentId)}>
            Open the bill
          </button>
        </div>
      )}
    </div>
  );
}

// ⚠️ GROUPING IS OPT-IN PER TABLE. The Waiting on you list wants it; the paid
// archive and the in-flight list are searched by vendor, and a row that hides the
// crabber's name behind a group heading makes them harder to find, not easier.
// `canExpand` lets a mixed list offer the key points on some rows only: on the combined
// page a crabber bill has no plan behind it, so its chevron cell stays empty.
function QueueTable({ rows, onOpen, columns, groupCnc, onChanged, renderExpand, canExpand }) {
  // ⚠️ TWO COMPANIES, SAID OUT LOUD, AND ONLY HARD CRABS ROLL UP BY WEEK. Greg,
  // 2026-10-02: "let's separate Crab and Cruise from Crab Place in these approvals, so I
  // know what I'm approving for, which company." The week roll-up exists because the
  // crabs for an event are held and counted as one load; a Cintas or Sysco bill tagged
  // to Crab & Cruise is not part of any load ("if it's not hard crabs tagged to a date,
  // then just put them out like you do the Crab Place ones, one at a time"), and
  // counting it as "1 crabber with no quantity recorded" was simply wrong.
  //
  // ⚠️ CLICKABLE HEADERS. Mercedes, Oct 14, 2026: "can I click on them and sort
  // them in ascending order... so I can try to work in order which in the way that
  // I see fit." A column with a `sort` function becomes clickable: first click is
  // ascending, second flips to descending, third clears back to the server's order
  // (which is due date, undated last, for Ready to Pay). A column with no `sort`
  // stays a plain label - the checkbox column, buttons, and anything whose cell is
  // a widget rather than a value have nothing to sort by.
  const [sortBy, setSortBy] = useState(null);
  const toggleSort = (key) => setSortBy((cur) => {
    if (!cur || cur.key !== key) return { key, dir: 'asc' };
    if (cur.dir === 'asc') return { key, dir: 'desc' };
    return null;
  });
  const sortedRows = useMemo(() => {
    if (!sortBy) return rows;
    const col = columns.find((c) => c.key === sortBy.key);
    if (!col || !col.sort) return rows;
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = col.sort(a);
      const vb = col.sort(b);
      // Nulls always last, regardless of direction - a bill with no due date
      // sorts to the bottom whether she clicked asc or desc, because "due date
      // ascending with the dateless first" is never what she means.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      let cmp;
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return sortBy.dir === 'desc' ? -cmp : cmp;
    });
    return arr;
  }, [rows, sortBy, columns]);
  const cncAll = groupCnc ? sortedRows.filter(r => r.for_cnc === true) : [];
  const cncOther = cncAll.filter(isInvoice);
  const plain = groupCnc ? sortedRows.filter(r => r.for_cnc !== true) : sortedRows;
  const cncWeeks = [];
  if (groupCnc) {
    const byWeek = new Map();
    for (const r of cncAll.filter(x => !isInvoice(x))) {
      const wk = weekEnding(r.invoice_date || r.submitted_at || r.created_at);
      if (!byWeek.has(wk)) byWeek.set(wk, []);
      byWeek.get(wk).push(r);
    }
    // Newest week first, and a week with no invoice date at all sorts last rather
    // than pretending to be the oldest.
    for (const [wk, list] of [...byWeek.entries()].sort((a, b) =>
      String(b[0] || '').localeCompare(String(a[0] || '')))) {
      cncWeeks.push({ week: wk, rows: list });
    }
  }
  // ⚠️ ONE ROW OPEN AT A TIME, deliberately. Mercedes + Greg, 2026-09-18: the point of
  // the overlay is a fast glance while walking down the queue; several open panels turn
  // it back into a wall of reading.
  const [expandId, setExpandId] = useState(null);
  const colSpan = columns.length + (renderExpand ? 1 : 0);
  const sumOf = (list) => list.reduce((a, r) => a + (Number(r.invoice_amount) || 0), 0);
  // The company heading only appears when the list is split, so every other table
  // that uses this component looks exactly as it did.
  const companyHead = (name, list) => (groupCnc && cncAll.length > 0 && list.length > 0) && (
    <tr style={{ background: '#eef4fa', borderTop: '1px solid #c9dcee' }}>
      <td colSpan={colSpan} style={{ padding: '7px 12px', color: 'var(--navy, #00395C)' }}>
        <strong style={{ fontSize: 13 }}>{name}</strong>
        <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
          {list.length} {list.length === 1 ? 'bill' : 'bills'} · <strong>{money(sumOf(list))}</strong>
        </span>
      </td>
    </tr>
  );
  const billRow = (r) => (
          <React.Fragment key={r.id}>
            <tr style={{ borderTop: '1px solid var(--gray-100, #f3f4f6)', cursor: 'pointer' }}
              onClick={() => onOpen(r.id)}>
              {renderExpand && canExpand && !canExpand(r) && <td style={{ width: 28 }} />}
              {renderExpand && (!canExpand || canExpand(r)) && (
                <td style={{ padding: '9px 0 9px 12px', width: 28 }}
                  onClick={(e) => { e.stopPropagation(); setExpandId(x => x === r.id ? null : r.id); }}>
                  <button className="btn btn-ghost" style={{ padding: 4, fontSize: 12 }}
                    title={expandId === r.id ? 'Hide the key points' : 'Show the key points'}
                    aria-label={expandId === r.id ? 'Hide the key points' : 'Show the key points'}>
                    {expandId === r.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                </td>
              )}
              {columns.map(c => (
                <td key={c.key} style={{ padding: '9px 12px' }}>{c.render(r)}</td>
              ))}
            </tr>
            {renderExpand && expandId === r.id && (
              <tr style={{ background: '#f5f8fc' }}>
                <td colSpan={colSpan} style={{ padding: 0 }}>
                  {renderExpand(r, () => setExpandId(null))}
                </td>
              </tr>
            )}
          </React.Fragment>
  );
  return (
    // The scroll box keeps a wide table inside its card on a narrow screen instead
    // of bleeding past the right edge.
    <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: 'var(--gray-50, #f9fafb)' }}>
          {renderExpand && <th style={{ width: 28 }} />}
          {columns.map(c => {
            const sortable = !!c.sort;
            const active = sortBy && sortBy.key === c.key;
            return (
              <th key={c.key}
                onClick={sortable ? () => toggleSort(c.key) : undefined}
                title={sortable
                  ? (active
                    ? (sortBy.dir === 'asc' ? 'Click to flip to descending' : 'Click to clear sort')
                    : `Sort by ${c.label}`)
                  : undefined}
                style={{
                  padding: '7px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                  color: active ? 'var(--navy, #00395C)' : 'var(--gray-500, #6b7280)',
                  textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap',
                  cursor: sortable ? 'pointer' : 'default',
                  userSelect: 'none',
                }}>
                {c.label}
                {active && (
                  <span style={{ marginLeft: 4, display: 'inline-flex', verticalAlign: 'middle' }}>
                    {sortBy.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
                  </span>
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {companyHead('Crab & Cruise', cncAll)}
        {cncWeeks.map(g => (
          <CncWeekRow key={g.week || 'none'} week={g.week} rows={g.rows}
            columns={columns} onOpen={onOpen} onChanged={onChanged} lead={!!renderExpand} />
        ))}
        {cncOther.map(billRow)}
        {companyHead('Crab Place', plain)}
        {plain.map(billRow)}
      </tbody>
    </table>
    </div>
  );
}

// ─── the paid archive ─────────────────────────────────────────────────────────
// Mercedes, 2026-08-27: "once a bill is paid it moves out of in flight and into a
// Paid invoices list... the archive needs to be searchable, by vendor name, invoice
// number, invoice date, month, and amount at a minimum. The point is that if anyone
// wants to go back and pull up something that was already cleared, they can find it
// easily instead of digging. This is the record of what we paid and when."
//
// Every filter is optional and they combine, so "Ford's in August over a grand" is
// one search rather than three passes by eye. The server does the filtering, so a
// long archive never has to arrive in the browser to be searched.
function PaidArchive({ onOpen, kind }) {
  // The invoice page has no crabbers and no trips - the parties here are shrimp
  // suppliers, utilities, coolers, dry ice - so the placeholders and the party
  // column say Vendor instead. Mercedes, 2026-09-18: "this isn't the crabber
  // invoice section."
  const isInvoice = kind === 'invoice';
  const partyLabel = kind === 'trip' ? 'Crabber' : 'Vendor';
  const searchPlaceholder = isInvoice
    ? 'Vendor or invoice number'
    : (kind === 'all' ? 'Vendor, invoice number or trip' : 'Crabber, invoice number or trip');
  const [filters, setFilters] = useState({
    search: '', vendor: '', invoiceNumber: '', month: '', amount: '',
  });
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ count: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async (f) => {
    setLoading(true); setErr('');
    try {
      // The kind goes to the server, so the count and the total above the list are for
      // this page's bills only.
      const params = kind ? { kind } : {};
      Object.keys(f).forEach((k) => { if (String(f[k]).trim()) params[k] = String(f[k]).trim(); });
      const r = await neonQuery('ap_paid_list', params);
      // ⚠️ neonQuery unwraps a payload that has a `rows` key and hands back just
      // that array, so the server answers with `invoices` instead. If it ever
      // arrives as a bare array again, show it rather than rendering an empty
      // archive over the top of real data.
      const list = Array.isArray(r) ? r : (r.invoices || []);
      setRows(list);
      setSummary({
        count: r && r.count != null ? r.count : list.length,
        total: r && r.total != null ? r.total : list.reduce(
          (sum, x) => sum + Number(x.approved_amount != null ? x.approved_amount : x.invoice_amount || 0), 0),
      });
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [kind]);

  // Debounced, so typing a crabber's name is one query rather than one per letter.
  useEffect(() => {
    const t = setTimeout(() => load(filters), 250);
    return () => clearTimeout(t);
  }, [filters, load]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const anyFilter = Object.keys(filters).some((k) => String(filters[k]).trim());

  // The last few months, for the month picker. Built from today rather than from
  // the data so it does not need a second query to know what to offer.
  const months = [];
  const now = new Date();
  for (let i = 0; i < 14; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    });
  }

  return (
    <div style={card}>
      <div style={cardHead}>
        <span>Paid invoices{summary.count ? ` (${summary.count})` : ''}</span>
        {summary.count > 0 && (
          <span style={{ fontWeight: 700, color: 'var(--navy, #00395C)' }}>
            {money(summary.total)} paid
          </span>
        )}
      </div>
      <div style={cardBody}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--gray-400, #9ca3af)' }} />
            <input style={{ ...inputStyle, paddingLeft: 30 }} placeholder={searchPlaceholder}
              value={filters.search} onChange={(e) => set('search', e.target.value)} />
          </div>
          <input style={{ ...inputStyle, width: 150 }} placeholder={partyLabel}
            value={filters.vendor} onChange={(e) => set('vendor', e.target.value)} />
          <input style={{ ...inputStyle, width: 130 }} placeholder="Invoice #"
            value={filters.invoiceNumber} onChange={(e) => set('invoiceNumber', e.target.value)} />
          <select style={{ ...inputStyle, width: 'auto' }} value={filters.month}
            onChange={(e) => set('month', e.target.value)}>
            <option value="">Any month</option>
            {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <input style={{ ...inputStyle, width: 120 }} placeholder="Amount" inputMode="decimal"
            value={filters.amount} onChange={(e) => set('amount', e.target.value)} />
          {anyFilter && (
            <button className="btn btn-ghost" style={{ fontSize: 12.5 }}
              onClick={() => setFilters({ search: '', vendor: '', invoiceNumber: '', month: '', amount: '' })}>
              Clear
            </button>
          )}
        </div>

        {err && <Banner tone="bad" icon={<AlertTriangle size={15} />}>{err}</Banner>}
        {loading && <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>Looking…</div>}
        {!loading && !rows.length && (
          <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>
            {anyFilter ? 'Nothing paid matches that.' : 'Nothing has been paid through here yet.'}
          </div>
        )}

        {!loading && rows.length > 0 && (
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50, #f9fafb)' }}>
                {[partyLabel, 'Invoice #', 'Invoice date', 'Paid', 'How', 'Entered by', ''].map((h) => (
                  <th key={h} style={{
                    padding: '7px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                    color: 'var(--gray-500, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.4px',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--gray-100, #f3f4f6)', cursor: 'pointer' }}
                  onClick={() => onOpen(r.id)}>
                  <td style={{ padding: '9px 12px' }}>
                    <strong>{r.vendor_name || '—'}</strong>
                    <FreightPill row={r} small />
                  </td>
                  <td style={{ padding: '9px 12px' }}>{r.invoice_number || '—'}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{fmtDate(r.invoice_date)}</td>
                  <td style={{ padding: '9px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {money(r.approved_amount != null ? r.approved_amount : r.invoice_amount)}
                    {r.approved_amount != null && r.invoice_amount != null
                      && Number(r.approved_amount) !== Number(r.invoice_amount) && (
                      <div style={{ fontSize: 11, color: 'var(--orange, #DC582A)', fontWeight: 400 }}>
                        cut from {money(r.invoice_amount)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '9px 12px', fontSize: 12 }}>
                    {isPrepaid(r) ? (
                      <span style={{ color: '#1c7c43' }}>
                        check at pickup{r.check_number ? ` #${String(r.check_number).trim()}` : ''}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--gray-500, #6b7280)' }}>
                        {r.paid_by_name || '—'}{r.paid_at ? ` · ${fmtWhen(r.paid_at)}` : ''}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '9px 12px', fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
                    {r.submitted_by_name || '—'}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                    <ChevronRight size={14} style={{ color: 'var(--gray-400, #9ca3af)' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && rows.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
            Paid bills live here rather than in the approvals queue. Open one to see the
            invoice, the yield it came off and every approval it collected.
          </div>
        )}
      </div>
    </div>
  );
}

// `initialDocumentId` is the invoice named in an approval email (App.js reads it
// off ?invoice=<id>). Opening straight onto that bill is the whole point of the
// link - an approver should not have to hunt the queue for the thing they were
// just asked to sign off. Back still returns to the queue as normal.
// `kind` splits one board into two pages (Greg + Mercedes, 2026-09-17): 'trip' is the
// crabber and freight bills, 'invoice' is everything else. Same chain machinery, same
// ready-to-pay, same archive; each page shows only its own bills.
// 'all' puts the two back on one screen (Greg, 2026-10-02: "I don't think I need to
// split crabber and freight approvals and regular invoice approvals... for me anyway,
// you can just combine them all on one screen"). It is what the waiting-on-you banner
// opens, because that banner counts both kinds and used to land on only one of them.
// On that page `invoicePage` is true (the invoice tools are on) and `tripPage` is too.
export default function Approvals({ initialDocumentId = null, kind = 'trip' }) {
  const allPage = kind === 'all';
  const invoicePage = kind === 'invoice' || allPage;
  const tripPage = kind === 'trip' || allPage;
  const inKind = (r) => allPage || (invoicePage ? isInvoice(r) : !isInvoice(r));
  const partyLabel = allPage ? 'Vendor / crabber' : (invoicePage ? 'Vendor' : 'Crabber / hauler');
  const user = getCurrentUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [openId, setOpenId] = useState(initialDocumentId || null);
  const [startedHereId, setStartedHereId] = useState(null);
  const [creating, setCreating] = useState(false);
  // Mercedes asked for paying to be its own view rather than a card buried in the
  // approvals queue: her relationship to these is "pay them", not "approve them".
  // Whoever pays lands on it; everyone else lands on approvals and can still see it.
  const [view, setView] = useState(null); // 'plan' | 'po' | 'approvals' | 'pay' | 'paid'
  // Counts on the tab strip. Loaded from the coach's own list endpoint (filtered to me,
  // never the whole team) so a person can see at a glance how many plans and POs of
  // their own are open without opening the tab.
  const [myCounts, setMyCounts] = useState({ plan: 0, po: 0 });
  // The coach tab offers the same vendor and category lists as the bill form.
  const [planVendors, setPlanVendors] = useState([]);
  const [planCategories, setPlanCategories] = useState([]);
  useEffect(() => {
    if (!invoicePage) return;
    neonQuery('ap_invoice_vendors', {}).then((r) => setPlanVendors((r && r.vendors) || [])).catch(() => {});
    neonQuery('ap_invoice_categories', {}).then((r) => setPlanCategories((r && r.categories) || [])).catch(() => {});
  }, [invoicePage]);

  // ⚠️ COUNTS SCOPED TO ME, NEVER THE WHOLE TEAM. Mercedes's rule: my plans and POs are
  // mine only. A team-wide count on my tab would leak the very thing the tab hides.
  const refreshMyCounts = useCallback(() => {
    if (!invoicePage) return;
    apInvoiceCoach('list', { mineOnly: true }).then((r) => {
      const plans = (r && r.plans) || [];
      setMyCounts({
        plan: plans.filter((p) => p.state === 'plan').length,
        po: plans.filter((p) => p.state === 'po').length,
      });
    }).catch(() => {});
  }, [invoicePage]);
  useEffect(() => { refreshMyCounts(); }, [refreshMyCounts]);

  // Which in-flight bills were planned with the buyer coach? A small icon shows
  // next to the vendor name on those rows. Mercedes, Oct 21, 2026. Scoped to
  // the in-flight list only (that is what she asked about); and reads a bare
  // set of doc ids back, never plan contents, so a private plan stays private.
  const [coachMarks, setCoachMarks] = useState(() => new Set());
  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const d = await neonQuery('ap_inbox', {});
      setData(d);
      setView(v => v || (d.actor && d.actor.isPayer ? 'pay' : 'approvals'));
      const inFlightIds = ((d && d.inFlight) || []).map((r) => r.id).filter(Boolean);
      if (inFlightIds.length) {
        apInvoiceCoach('plan-marks', { documentIds: inFlightIds })
          .then((res) => setCoachMarks(new Set(((res && res.withPlan) || []).map(Number))))
          .catch(() => setCoachMarks(new Set()));
      } else {
        setCoachMarks(new Set());
      }
    }
    catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Ready to Pay -> QuickBooks ──────────────────────────────────────────────
  // Greg + Mercedes, 2026-10-14: tick the approved bills and send them to QuickBooks
  // itemized, instead of downloading each scan and feeding it to QuickBooks' reader a
  // second time. Each bill is checked on arrival; one that cannot go as approved sits
  // under Needs attention with the reason, and nothing is changed to make it fit.
  const [qbChecks, setQbChecks] = useState({});
  const [qbSel, setQbSel] = useState(() => new Set());
  const [qbSending, setQbSending] = useState(false);
  const [qbErr, setQbErr] = useState('');
  const canEnter = !!(data && data.actor && (data.actor.isPayer || data.actor.isAdmin));
  // ⚠️ A FEW BILLS PER REQUEST, NOT THE WHOLE LIST IN ONE. Each bill's check reads the
  // vendor, the lines and the QuickBooks copy, and one request for thirty-odd bills could
  // run past the function's time limit - every chip then sat on "checking…" for good,
  // which is the state Mercedes asked about on Oct 14, 2026. Five at a time, in order,
  // each chip resolving as its batch comes back; a batch that fails says "Could not
  // check" with the reason. A newer run (the list reloaded) abandons an older one.
  const qbRun = React.useRef(0);
  const runQbCheck = useCallback(async (rows) => {
    const run = qbRun.current + 1;
    qbRun.current = run;
    setQbErr('');
    const ids = (rows || []).map((r) => r.id);
    setQbChecks((m) => {
      const keep = {};
      ids.forEach((id) => { if (m[id]) keep[id] = m[id]; });
      return keep;
    });
    const acc = {};
    for (let i = 0; i < ids.length; i += 5) {
      const chunk = ids.slice(i, i + 5);
      let got = {};
      try {
        const r = await apQboPush('check', { documentIds: chunk });
        (r.results || []).forEach((c) => { got[c.documentId] = c; });
      } catch (e) {
        got = {};
        chunk.forEach((id) => {
          got[id] = { documentId: id, status: 'error', issues: [{ code: 'error', text: 'Could not check this one: ' + e.message }] };
        });
      }
      if (qbRun.current !== run) return;
      Object.assign(acc, got);
      setQbChecks((m) => ({ ...m, ...got }));
    }
    if (qbRun.current !== run) return;
    setQbSel((sel) => new Set([...sel].filter((id) => acc[id] && acc[id].status === 'ready')));
  }, []);
  useEffect(() => { if (view === 'pay' && data) runQbCheck(data.readyToPay || []); }, [view, data, runQbCheck]);
  const [paidSync, setPaidSync] = useState(null);   // null | 'running' | { text, tone }
  const checkPayments = async () => {
    setPaidSync('running');
    try {
      const r = await apQboPush('paid_sync', {});
      const n = (r.paid || []).length, p = (r.partial || []).length, e = (r.errors || []).length;
      setPaidSync({
        tone: e ? 'bad' : 'ok',
        text: 'Checked ' + r.checked + ' bill' + (r.checked === 1 ? '' : 's') + ' in QuickBooks. '
          + (n ? n + ' paid, moved to Paid invoices. ' : 'None newly paid. ')
          + (p ? p + ' partly paid. ' : '')
          + (e ? e + ' could not be read: ' + r.errors.map((x) => '#' + x.documentId + ' ' + x.error).join('; ') : ''),
      });
      if (n || p) load();
    } catch (e) { setPaidSync({ tone: 'bad', text: e.message }); }
  };
  const setOneCheck = (c) => { if (c && c.documentId) setQbChecks((m) => ({ ...m, [c.documentId]: c })); };

  // ⚠️ THE UNITS ARE READ OFF THE INVOICE, ONCE, FOR THE BILLS THAT HAVE NO TRIP.
  // A Crab & Cruise load never becomes a purchase order, so the only place the box
  // count survives is the crabber PDF already attached to the bill. Reading is
  // idempotent and the server refuses to re-read anything it has already read or
  // already failed on, so this fires at most once per bill however often the board
  // reloads - and it is deliberately quiet, because a failure here must not put an
  // error over a queue somebody is working down.
  const readTried = React.useRef(false);
  useEffect(() => {
    if (readTried.current || !data) return;
    const need = (data.waitingOnYou || []).some(
      r => r.for_cnc === true && !Object.keys(r.grades || {}).length && !r.units_failed_reason);
    if (!need) return;
    readTried.current = true;
    apInvoiceUnits({}).then(r => { if (r && r.read) load(); }).catch(() => {});
  }, [data, load]);

  // ⚠️ Which bill THIS screen created, and nothing else. Only that one may be
  // discarded on the way out; a draft that already existed is left alone, because
  // deleting a bill for the crime of being looked at is what lost Matt his freight
  // bill and four runs at the Cajun Crab invoice.
  const startNew = async (billKind = 'crab') => {
    setCreating(true);
    try {
      // ⚠️ THE KIND DECIDES THE CHAIN, NEVER THE DEFAULT. An invoice bill without an
      // explicit docType lands on the hard crab chain and gets routed to Matt Sr
      // for a yield he cannot judge; Mercedes caught this 2026-10-14 after a
      // Delmarva Power bill was sent to Matt instead of straight to Greg. The
      // server will default to hard_crab_invoice when docType is omitted, so the
      // mapping has to be pinned here. Crab and freight both ride the hard crab
      // chain and need no docType; only invoice has to say which chain it is on.
      const payload = {};
      if (billKind !== 'crab') payload.billKind = billKind;
      if (billKind === 'invoice') payload.docType = 'vendor_invoice';
      const r = await neonQuery('ap_document_create', payload);
      setStartedHereId(r.document.id);
      setOpenId(r.document.id);
      await load();
    } catch (e) { setErr(e.message); }
    setCreating(false);
  };

  if (openId) {
    return (
      <div style={{ padding: 24, paddingBottom: 96, maxWidth: 1100 }}>
        <Detail
          key={openId}
          documentId={openId}
          actor={(data && data.actor) || { adminId: user?.id }}
          startedHere={startedHereId === openId}
          onBack={() => { setStartedHereId(null); setOpenId(null); load(); }}
          onChanged={load}
          onOpenDocument={(id) => { setStartedHereId(null); setOpenId(id); }}
          onAfterSubmit={(billKind) => startNew(billKind || (kind === 'invoice' ? 'invoice' : 'crab'))}
        />
      </div>
    );
  }

  // Emailed invoices are crabber bills today, so the intake tray stays on that page.
  const intake = tripPage ? ((data && data.intake) || []).filter(inKind) : [];
  const waiting = ((data && data.waitingOnYou) || []).filter(inKind);
  // yourDrafts carries two different things: bills SENT BACK to you, which have to
  // stay visible, and half-entered drafts.
  const sentBack = ((data && data.yourDrafts) || []).filter(inKind).filter((d) => d.status === 'returned');
  // ⚠️ AN UNFINISHED BILL WITH WORK ON IT IS NOT CLUTTER, AND HIDING IT LOST TWO DAYS
  // OF MATT SR'S TIME. The drafts list was removed on 2026-09-03 as clutter, on the
  // assumption that a draft is always discarded when its author leaves it. That only
  // holds for the back button on a bill this screen created; the browser's own back, a
  // refresh, a closed tab or clicking another page all leave the draft alive and on NO
  // list at all. On 2026-09-10 Matt Sr attached an invoice to two of them and reported
  // both as disappeared - they were sitting there with his files on them.
  //
  // So the split is by whether anything is ON it, not by status. A draft carrying a
  // scan, a trip, a vendor, an invoice number or an amount is somebody's work in
  // progress and has to be findable. An empty shell - the row you get by clicking
  // "Start an approval" and walking away - stays hidden, which is the clutter Mercedes
  // actually asked to be rid of. Measured on the live board the day this shipped: 24
  // live drafts, 4 carrying work, 20 empty.
  const unfinished = ((data && data.yourDrafts) || []).filter(inKind).filter((d) =>
    d.status === 'draft'
    && (d.bill_kind || 'crab') !== 'freight'   // freight drafts already ride "in flight"
    && ((d.file_count || 0) > 0 || d.purchase_order_id
        || (d.vendor_name && String(d.vendor_name).trim())
        || (d.invoice_number && String(d.invoice_number).trim())
        || d.invoice_amount != null));
  // Somebody else started these and stopped. They are the ones that go quiet, so
  // the heading counts them separately rather than letting them sit in a list
  // whose title implies they are your own typing.
  const strandedCount = unfinished.filter((d) => d.mine === false).length;
  const ready = ((data && data.readyToPay) || []).filter(inKind);
  // ⚠️ ENTERED BILLS LEAVE THE TO-DO LIST BUT NOT THE PAGE. Mercedes, Oct 14, 2026:
  // "Entered bills drop out of the to-do queue/filter." Her earlier rule still holds
  // ("so I do not lose it"): they move to their own list just below, In QuickBooks,
  // waiting to be paid, with the QuickBooks bill linked, until QuickBooks says paid.
  const isEnteredRow = (r) => !!(r.entered_in_qb_at || r.qbo_bill_id
    || (qbChecks[r.id] && (qbChecks[r.id].status === 'entered' || qbChecks[r.id].status === 'pushed')));
  const toSend = ready.filter((r) => !isEnteredRow(r));
  const inQb = ready.filter(isEnteredRow);
  const inFlight = ((data && data.inFlight) || []).filter(inKind);
  // ⚠️ WAITING ON APPROVAL means bills STILL IN THE CHAIN - not ones already
  // signed off. Mercedes, Oct 14, 2026: "in that list only have bills that are
  // waiting on approval. once they get approved they move over to tab ready to
  // pay." inFlight carries approved + rejected bills from the last 30 days as a
  // convenience overview, which duplicates every approved bill between this tab
  // and Ready to Pay - exactly what she asked to end. Strip them here, on the
  // display side, so Ready to Pay keeps its own query (approved + not paid) and
  // nothing else on the page shifts.
  const stillWaiting = inFlight.filter((r) => r.status !== 'approved' && r.status !== 'rejected');
  // Served by ap_inbox from lib/ap-db.js, so the label on a row and the value the
  // query sorted by can never be two different lists.
  const termOptions = (data && data.netTerms) || [];

  return (
    // 1320, not 1100: Ready to Pay carries ten columns and at 1100 the table ran
    // past the right edge of its card (Greg, Oct 14, 2026).
    <div style={{ padding: 24, maxWidth: 1320 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--navy, #00395C)' }}>
          {allPage ? 'All Approvals' : (invoicePage ? 'Invoice Approvals' : 'Crabber & Freight Approvals')}
        </h1>
        <button className="btn btn-ghost" onClick={load} style={{ fontSize: 12 }}><RefreshCw size={13} /></button>
        {/* ⚠️ TWO BUTTONS, BECAUSE A FREIGHT BILL IS ITS OWN BILL TO ITS OWN COMPANY.
            Mercedes, 2026-09-03: "splitting off the crabber bill has been the source of
            every freight problem we've had... it's cleaner to start it as one instead
            of deriving it from something else." Downstream nothing differs: same chain,
            same statuses, same ready to pay. */}
        <span style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {invoicePage && (
            <>
              <button className="btn btn-outline" onClick={() => setView('plan')}>
                <MessageSquare size={14} style={{ marginRight: 6 }} />
                Plan an Order
              </button>
              <button className="btn btn-primary" onClick={() => startNew('invoice')} disabled={creating}>
                <Plus size={14} style={{ marginRight: 6 }} />
                Enter an invoice
              </button>
            </>
          )}
          {tripPage && (
            <>
              <button className={allPage ? 'btn btn-outline' : 'btn btn-primary'} onClick={() => startNew('crab')} disabled={creating}>
                <Plus size={14} style={{ marginRight: 6 }} />
                Start a crabber approval
              </button>
              <button className="btn btn-outline" onClick={() => startNew('freight')} disabled={creating}>
                <Truck size={14} style={{ marginRight: 6 }} />
                Start a freight approval
              </button>
            </>
          )}
        </span>
      </div>
      <p style={{ marginTop: 0, marginBottom: 14, fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>
        {allPage
          ? 'Every bill in one place: crabbers, freight, and every other vendor invoice. Same sign-off chain, same ready to pay, same archive.'
          : invoicePage
          ? 'Shrimp, boxes, supplies, utilities: every other vendor bill, with the history behind it. Plan the order with the buyer coach, mark it as ordered, then convert the PO to an invoice when the bill arrives.'
          : 'Vendor paperwork, signed off digitally instead of on paper.'}
      </p>

      {/* ⚠️ TAB ORDER IS THE FLOW OF WORK, not the age of the feature. Mercedes,
          2026-09-18: "the steps are planning, order, approvals, ready to pay and then
          pay invoices, so rearrange the tabs at the top in that order since that's the
          order it would go in anyway." Plan and PO are per person; approvals + pay +
          paid are the shared queue. */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          ...(invoicePage ? [
            { key: 'plan', label: 'Plan an Order', count: myCounts.plan },
            { key: 'po', label: 'My POs', count: myCounts.po },
          ] : []),
          // ⚠️ "WAITING ON APPROVAL" is Mercedes's own label (Oct 14, 2026). Her flow:
          // waiting on approval -> ready to pay -> paid invoices.
          // ⚠️ THE COUNT IS BILLS ACTUALLY WAITING ON AN APPROVER, not every row on
          // this tab. Mercedes, Oct 21, 2026: "I want it to be counting what's
          // sitting in there that's waiting on someone to be approved" - unfinished
          // drafts have not been sent yet, so nobody is waiting on them and they
          // make the number read higher than her workload actually is. The drafts
          // section below is kept so Matt Sr still finds his half-entered bills;
          // this is only the number on the tab.
          { key: 'approvals', label: 'Waiting on approval', count: waiting.length + sentBack.length },
          { key: 'pay', label: 'Ready to pay', count: ready.length },
          // No count: the archive grows forever and a number that only ever goes up
          // is not information. The search inside it is the point.
          { key: 'paid', label: 'Paid invoices', count: 0 },
        ].map(t => (
          <button key={t.key}
            className={view === t.key ? 'btn btn-primary' : 'btn btn-outline'}
            style={{ fontSize: 13 }}
            onClick={() => setView(t.key)}>
            {t.label}{t.count ? ` (${t.count})` : ''}
          </button>
        ))}
      </div>

      {err && <div style={{ marginBottom: 16 }}><Banner tone="bad" icon={<AlertTriangle size={15} />}>{err}</Banner></div>}
      {loading && <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>Loading…</div>}
      <QuestionsBanner onOpen={setOpenId} />

      {view === 'paid' && <PaidArchive onOpen={setOpenId} kind={kind} />}
      {invoicePage && view === 'plan' && (
        <PlanWorkspace vendors={planVendors} categories={planCategories} onChanged={refreshMyCounts} />
      )}
      {invoicePage && view === 'po' && (
        <POWorkspace onChanged={() => { refreshMyCounts(); load(); }}
          onOpenDocument={setOpenId} />
      )}

      {!loading && data && view === 'pay' && (
        <>
          {qbErr && <div style={{ marginBottom: 12 }}><Banner tone="bad" icon={<AlertTriangle size={15} />}>QuickBooks check: {qbErr}</Banner></div>}
          {(() => {
            const failed = toSend.filter((r) => qbChecks[r.id] && qbChecks[r.id].status === 'error');
            return failed.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <Banner tone="bad" icon={<AlertTriangle size={15} />}>
                  {failed.length} bill{failed.length === 1 ? '' : 's'} could not be checked against QuickBooks
                  ({(qbChecks[failed[0].id].issues || [])[0] ? qbChecks[failed[0].id].issues[0].text : 'no reason given'}).{' '}
                  <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => runQbCheck(data.readyToPay || [])}>Check again</button>
                </Banner>
              </div>
            );
          })()}
          <QboNeedsAttention
            checks={toSend.map((r) => qbChecks[r.id]).filter(Boolean)}
            rows={ready}
            onOpen={setOpenId}
            onCheckChanged={setOneCheck}
            onEntered={() => { load(); }}
            canEnter={canEnter} />
          <div style={card}>
            <div style={cardHead}>
              <span>Approved, not in QuickBooks yet{toSend.length ? ` (${toSend.length})` : ''}</span>
              {canEnter && (() => {
                const readyIds = toSend.filter((r) => qbChecks[r.id] && qbChecks[r.id].status === 'ready').map((r) => r.id);
                return (
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {readyIds.length > 0 && (
                      <button className="btn btn-ghost" style={{ fontSize: 12 }}
                        onClick={() => setQbSel(qbSel.size === readyIds.length ? new Set() : new Set(readyIds))}>
                        {qbSel.size === readyIds.length ? 'Clear' : `Select all ready (${readyIds.length})`}
                      </button>
                    )}
                    <button className="btn btn-primary" style={{ fontSize: 12.5 }} disabled={!qbSel.size}
                      onClick={() => setQbSending(true)}>
                      Send {qbSel.size || ''} to QuickBooks
                    </button>
                  </span>
                );
              })()}
            </div>
            {toSend.length ? (
              /* ⚠️ THE SERVER'S ORDER IS THE DEFAULT, not re-sorted by vendor. Mercedes,
                 2026-09-24: "Due date sorts by default when I open Ready to Pay." The
                 query orders by due date with the undated ones last; sorting by vendor
                 on top of that would throw it away, which is exactly what the old
                 sortByVendorThenInvoiceDate call did. Headers are clickable (Mercedes,
                 Oct 14, 2026) so she can flip to vendor or amount or whatever when
                 she wants to - the sort below is a `col.sort` on each column, not a
                 re-sort of the data up front. */
              <QueueTable rows={toSend} onOpen={setOpenId} columns={[
                // Only a bill that passed every check can be ticked; the rest say why
                // under Needs attention.
                ...(canEnter ? [{
                  key: 'sel', label: '',
                  render: r => {
                    const ok = qbChecks[r.id] && qbChecks[r.id].status === 'ready';
                    return (
                      <span onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" disabled={!ok} checked={qbSel.has(r.id)}
                          title={ok ? 'Send to QuickBooks' : 'Not ready for QuickBooks - see Needs attention'}
                          onChange={() => setQbSel((s) => { const n = new Set(s); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); return n; })} />
                      </span>
                    );
                  },
                }] : []),
                { key: 'v', label: partyLabel, render: r => <PartyCell row={r} />,
                  sort: r => (r.vendor_name || '').trim().toLowerCase() || null },
                { key: 'i', label: 'Invoice #', render: r => r.invoice_number || '—',
                  sort: r => (r.invoice_number || '').trim() || null },
                { key: 'd', label: 'Invoice date', render: r => <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.invoice_date)}</span>,
                  sort: r => (r.invoice_date || '').slice(0, 10) || null },
                // The two columns she asked for, in the order she reads them: the
                // terms explain the date, so they sit beside it.
                //
                // ⚠️ TERMS SORT BY DAYS, NOT BY LABEL. "Net 7" sorts before "Net 60"
                // whether or not the alphabet does; prepaid/on_account carry
                // `days:null` on NET_TERMS in lib/ap-db.js, so they land at the end.
                {
                  key: 't', label: 'Terms',
                  render: r => (r.net_terms
                    ? termLabel(r.net_terms, termOptions)
                    : <span style={{ color: 'var(--gray-400, #9ca3af)' }}>—</span>),
                  sort: r => {
                    if (!r.net_terms) return null;
                    const opt = (termOptions || []).find((t) => t.value === r.net_terms);
                    return opt && opt.days != null ? opt.days : null;
                  },
                },
                { key: 'du', label: 'Due', render: r => <DueCell value={r.due_date} terms={r.net_terms} />,
                  sort: r => (r.due_date || '').slice(0, 10) || null },
                { key: 'b', label: 'Billed', render: r => money(r.invoice_amount),
                  sort: r => (r.invoice_amount == null ? null : Number(r.invoice_amount)) },
                {
                  key: 'a', label: 'Pay this', render: r => (
                    <strong style={{
                      color: Number(r.approved_amount) < Number(r.invoice_amount)
                        ? 'var(--orange, #DC582A)' : 'var(--navy, #00395C)',
                    }}>{money(r.approved_amount)}</strong>
                  ),
                  sort: r => (r.approved_amount == null ? null : Number(r.approved_amount)),
                },
                // Where she got to this morning. An entered bill stays on the list
                // with this badge rather than vanishing off it.
                //
                // ⚠️ READ "entered" OFF EITHER SOURCE. The row from ap_inbox does not
                // (yet) carry entered_in_qb_at, so after the push the qbCheck is the
                // only place that fact is visible on this page - without the check
                // fallback the column fell through to QboStatusChip's "not entered"
                // fallback for every bill Mercedes had just sent over. See EnteredBadge
                // for the trace.
                {
                  key: 'qb', label: 'QuickBooks',
                  render: r => {
                    const chk = qbChecks[r.id];
                    const entered = r.entered_in_qb_at || (chk && chk.status === 'entered');
                    return entered
                      ? <EnteredBadge row={r} check={chk} />
                      : <QboStatusChip check={chk} />;
                  },
                  // Groups ready-to-send together, then errors, then everything else -
                  // the one on this page who wants it sorted is the one about to
                  // tick a batch to QuickBooks.
                  sort: r => {
                    const chk = qbChecks[r.id];
                    if (r.entered_in_qb_at || (chk && chk.status === 'entered')) return '1-entered';
                    if (chk && chk.status === 'ready') return '0-ready';
                    if (chk && chk.status === 'error') return '2-error';
                    return '3-' + ((chk && chk.status) || 'unknown');
                  },
                },
                { key: 'e', label: 'Entered by', render: r => <span style={{ whiteSpace: 'nowrap' }}>{r.submitted_by_name || '—'}</span>,
                  sort: r => (r.submitted_by_name || '').trim().toLowerCase() || null },
                { key: 'c', label: 'Waiting', render: r => <AgeChip row={r} />,
                  sort: r => Number(r.days_in_stage || 0) },
              ]} />
            ) : (
              <div style={{ ...cardBody, fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>
                {ready.length ? 'Every approved bill is in QuickBooks. They are listed below until they are paid.'
                  : 'Nothing is approved and waiting to be paid.'}
              </div>
            )}
            <div style={{ padding: '0 16px 14px', fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
              {allPage ? 'Open one to see the trip or the history behind it' : (invoicePage ? 'Open one to see the history behind it' : 'Open one to see the trip behind it')} and mark it paid.
              Tick the ones marked Ready to send and CPx enters them in QuickBooks as bills, line by line,
              with the scan attached. Paying them still happens in QuickBooks — CPx does not post payments.
            </div>
          </div>

          {/* In QuickBooks, waiting to be paid. Mercedes, Oct 14, 2026: an entered bill
              shows Entered in green with a link to the bill, drops out of the to-do
              list above, and moves to Paid on its own once QuickBooks says it is paid
              (lib/ap-qbo-paid.js, hourly, or the button here). */}
          <div style={card}>
            <div style={cardHead}>
              <span>In QuickBooks, waiting to be paid{inQb.length ? ` (${inQb.length})` : ''}</span>
              {canEnter && (
                <button className="btn btn-ghost" style={{ fontSize: 12 }} disabled={paidSync === 'running'} onClick={checkPayments}
                  title="Reads each bill back from QuickBooks. A bill paid there moves to Paid invoices here. Nothing is changed in QuickBooks.">
                  <RefreshCw size={12} style={{ marginRight: 4 }} />
                  {paidSync === 'running' ? 'Checking QuickBooks…' : 'Check QuickBooks for payments'}
                </button>
              )}
            </div>
            {paidSync && paidSync !== 'running' && (
              <div style={{ padding: '8px 16px', fontSize: 12.5, color: paidSync.tone === 'bad' ? '#b42318' : '#1c7c43' }}>{paidSync.text}</div>
            )}
            {inQb.length ? (
              <QueueTable rows={inQb} onOpen={setOpenId} columns={[
                { key: 'v', label: partyLabel, render: r => <PartyCell row={r} />,
                  sort: r => (r.vendor_name || '').trim().toLowerCase() || null },
                { key: 'i', label: 'Invoice #', render: r => r.invoice_number || '—',
                  sort: r => (r.invoice_number || '').trim() || null },
                { key: 'du', label: 'Due', render: r => <DueCell value={r.due_date} terms={r.net_terms} />,
                  sort: r => (r.due_date || '').slice(0, 10) || null },
                { key: 'a', label: 'Pay this', render: r => <strong style={{ color: 'var(--navy, #00395C)' }}>{money(r.approved_amount)}</strong>,
                  sort: r => (r.approved_amount == null ? null : Number(r.approved_amount)) },
                {
                  key: 'qb', label: 'QuickBooks',
                  render: r => {
                    const billId = r.qbo_bill_id || (qbChecks[r.id] && qbChecks[r.id].qboBillId);
                    return (
                      <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                        <EnteredBadge row={r} check={qbChecks[r.id] || { status: 'entered' }} />
                        {billId ? (
                          <a href={qboBillUrl(billId)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                            title="Opens in QuickBooks Online, in the company you are signed in to">Bill {billId}</a>
                        ) : (
                          <span style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }} title="Marked entered by hand, so CPx cannot see when it is paid.">not linked</span>
                        )}
                      </span>
                    );
                  },
                  // Linked bills first, then the hand-marked ones at the end.
                  sort: r => (r.qbo_bill_id || (qbChecks[r.id] && qbChecks[r.id].qboBillId)) ? '0' : '1',
                },
                {
                  key: 'pd', label: 'Paid in QuickBooks',
                  render: r => {
                    if (!r.qbo_bill_id) return <span style={{ color: 'var(--gray-400, #9ca3af)' }}>—</span>;
                    const bal = r.qbo_balance == null ? null : Number(r.qbo_balance);
                    if (bal != null && bal > 0 && bal < Number(r.approved_amount)) {
                      return (
                        <span style={{ color: '#9a4a1c', fontWeight: 600, whiteSpace: 'nowrap' }} title={r.qbo_payment_ref || ''}>
                          Partly paid, {money(bal)} left
                        </span>
                      );
                    }
                    return <span style={{ color: 'var(--gray-500, #6b7280)' }}>not yet</span>;
                  },
                  // Partly-paid first (there is money left to chase), then not-yet,
                  // and the un-linked rows last since the sort cannot speak for them.
                  sort: r => {
                    if (!r.qbo_bill_id) return null;
                    const bal = r.qbo_balance == null ? null : Number(r.qbo_balance);
                    if (bal != null && bal > 0 && bal < Number(r.approved_amount)) return 0;
                    return 1;
                  },
                },
                { key: 'e', label: 'Entered by', render: r => <span style={{ whiteSpace: 'nowrap' }}>{r.entered_in_qb_by_name || '—'}</span>,
                  sort: r => (r.entered_in_qb_by_name || '').trim().toLowerCase() || null },
              ]} />
            ) : (
              <div style={{ ...cardBody, fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>
                Nothing is in QuickBooks waiting to be paid.
              </div>
            )}
          </div>
          {qbSending && (
            <QboSendDialog
              checks={ready.filter((r) => qbSel.has(r.id)).map((r) => qbChecks[r.id]).filter(Boolean)}
              onClose={() => setQbSending(false)}
              onDone={() => { setQbSending(false); setQbSel(new Set()); load(); }} />
          )}

          <div style={card}>
            <div style={cardHead}><span>Paid recently</span></div>
            {inFlight.filter(r => r.status === 'paid').length ? (
              <QueueTable rows={inFlight.filter(r => r.status === 'paid')} onOpen={setOpenId} columns={[
                { key: 'v', label: partyLabel, render: r => <PartyCell row={r} /> },
                { key: 'i', label: 'Invoice #', render: r => r.invoice_number || '—' },
                { key: 'a', label: 'Paid', render: r => money(r.approved_amount) },
                { key: 'c', label: 'When', render: r => fmtWhen(r.completed_at) },
              ]} />
            ) : (
              <div style={{ ...cardBody, fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>
                Nothing marked paid in the last 30 days.
              </div>
            )}
          </div>
        </>
      )}

      {!loading && data && view === 'approvals' && (
        <>
          <div style={card}>
            <div style={cardHead}>
              <span>Waiting on you{waiting.length ? ` (${waiting.length})` : ''}</span>
              <QueueTotals rows={waiting} verb="to approve" grades kind={invoicePage ? 'invoice' : 'trip'} />
            </div>
            {waiting.length ? (
              <QueueTable rows={waiting} onOpen={setOpenId} groupCnc onChanged={load}
                // Greg's "layover" overlay (Mercedes, 2026-09-18): a chevron on each row
                // opens a short key-points summary from the buyer's plan and the vendor
                // history, so he does not have to open every bill just to read a book.
                // Invoice Approvals only; a crabber trip has no plan behind it.
                renderExpand={invoicePage ? (r, close) => (
                  <KeyPointsOverlay documentId={r.id}
                    onOpen={(id) => { close(); setOpenId(id); }} />
                ) : undefined}
                canExpand={allPage ? isInvoice : undefined}
                columns={[
                { key: 'v', label: partyLabel, render: r => <PartyCell row={r} withPdfs onChanged={load} /> },
                {
                  key: 'u', label: 'Units',
                  // ⚠️ A BILL WITH NO YIELD SHEET IS NOT A BILL WITH NOTHING IN IT.
                  // A Crab & Cruise load never gets graded, so the honest cell says
                  // the quantity was never recorded rather than showing a dash that
                  // reads as an empty load.
                  render: r => (
                    <Grades grades={r.grades}
                      empty={r.for_cnc ? 'not recorded' : '—'} />
                  ),
                },
                { key: 'i', label: 'Invoice #', render: r => r.invoice_number || '—' },
                // Greg, Oct 21, 2026: "how come it doesn't have a date? Let's add a date
                // column for the invoice" - the date is what tells him whether a weekly
                // bill sits where it should.
                { key: 'd', label: 'Invoice date',
                  render: r => <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.invoice_date)}</span>,
                  sort: r => (r.invoice_date || '').slice(0, 10) || null },
                { key: 'a', label: 'Billed', render: r => <BilledCell r={r} /> },
                // ⚠️ ON THE INVOICE PAGE ONLY. Mercedes asked for the terms on "the
                // Invoice Approvals list and the Ready to Pay list"; a crabber bill
                // has no terms, so on that page this would be a column of dashes.
                ...(invoicePage ? [{
                  key: 'du', label: 'Due',
                  render: r => <DueCell value={r.due_date} terms={r.net_terms} />,
                }] : []),
                // The step name was only ever a label; the useful thing at that spot
                // is the button that ends the step.
                { key: 's', label: 'Your step', render: r => <ApprovePill row={r} onChanged={load} /> },
                { key: 'e', label: 'Entered by', render: r => r.submitted_by_name || '—' },
                { key: 'w', label: 'With you', render: r => <AgeChip row={r} /> },
              ]} />
            ) : (
              <div style={{ ...cardBody, fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>
                Nothing needs your sign-off right now.
              </div>
            )}
          </div>

          {intake.length > 0 && (
            <div style={card} key="intake">
              <div style={cardHead}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Mail size={14} /> Came in by email, nobody has picked {intake.length === 1 ? 'it' : 'them'} up ({intake.length})
                </span>
              </div>
              <QueueTable rows={intake} onOpen={setOpenId} columns={[
                { key: 'v', label: 'From', render: r => <><strong>{r.vendor_name || r.intake_from || '—'}</strong><PaidPill row={r} small /><FreightPill row={r} small /></> },
                { key: 's', label: 'Subject', render: r => (r.intake_subject || '').slice(0, 70) || '—' },
                { key: 'f', label: 'Attached', render: r => r.file_count + (Number(r.file_count) === 1 ? ' file' : ' files') },
                { key: 'd', label: 'Arrived', render: r => fmtWhen(r.intake_received_at) },
              ]} />
              <div style={{ padding: '0 16px 14px', fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
                Opening one makes it yours. The scan is already attached, so it just needs the
                amount and the trip it came off.
              </div>
            </div>
          )}

          {sentBack.length > 0 && (
            <div style={card}>
              <div style={cardHead}>
                <span style={{ color: '#b42318', fontWeight: 700 }}>
                  Sent back to you ({sentBack.length})
                </span>
              </div>
              <QueueTable rows={sentBack} onOpen={setOpenId} columns={[
                { key: 'v', label: partyLabel, render: r => <PartyCell row={r} /> },
                { key: 'i', label: 'Invoice #', render: r => r.invoice_number || '—' },
                { key: 'a', label: 'Billed', render: r => <BilledCell r={r} /> },
                { key: 's', label: 'Status', render: r => <StatusPill status={r.status} label={r.stage_label} /> },
                {
                  key: 'r', label: 'Why it came back', render: r => r.returned_reason
                    ? <span style={{ color: '#b42318' }}>{r.returned_reason}</span>
                    : '—'
                },
                { key: 'c', label: 'Sitting', render: r => <AgeChip row={r} /> },
              ]} />
            </div>
          )}

          {/* ⚠️ IT SAYS "NOT SENT YET" IN THE HEADING, because that is the fact Matt
              Sr did not have. He uploaded an invoice and reasonably read that as having
              filed it; nothing on the screen said the bill was still his and still
              going nowhere. */}
          {unfinished.length > 0 && (
            <div style={card}>
              <div style={cardHead}>
                <span style={{ color: '#9a4a1c', fontWeight: 700 }}>
                  Unfinished, not sent yet ({unfinished.length})
                  {strandedCount > 0 && ` - ${strandedCount} somebody else started`}
                </span>
                {/* ⚠️ THIS USED TO READ "Yours only. Nobody else can see these and
                    nobody is waiting on them." Both halves were wrong and together
                    they are why Yankee Canal 33671 sat for eight days: the list was
                    the author's alone, and a crabber WAS waiting on it. */}
                <span style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', fontWeight: 400 }}>
                  Nobody has been asked to approve these. {allPage ? 'A vendor or a crabber is' : (invoicePage ? 'A vendor is' : 'A crabber is')} waiting on any
                  that carry an invoice.
                </span>
              </div>
              <QueueTable rows={unfinished} onOpen={setOpenId} columns={[
                { key: 'v', label: partyLabel, render: r => <PartyCell row={r} /> },
                { key: 'i', label: 'Invoice #', render: r => r.invoice_number || '—' },
                { key: 'a', label: 'Billed', render: r => <BilledCell r={r} /> },
                { key: 't', label: 'Trip', render: r => r.purchase_order_id || '—' },
                {
                  key: 'f',
                  label: 'Paperwork',
                  render: r => ((r.file_count || 0) === 0
                    ? <span style={{ color: '#9a4a1c' }}>nothing attached</span>
                    : `${r.file_count} file${r.file_count === 1 ? '' : 's'}`),
                },
                {
                  key: 'w',
                  label: 'Started by',
                  render: r => (r.mine === false
                    ? <span style={{ color: '#9a4a1c', fontWeight: 600 }}>
                        {r.submitted_by_name || 'somebody else'}
                      </span>
                    : <span style={{ color: 'var(--gray-500, #6b7280)' }}>you</span>),
                },
                { key: 'c', label: 'Started', render: r => <AgeChip row={r} /> },
              ]} />
            </div>
          )}

          <div style={card}>
            <div style={cardHead}>
              <span>Everything in flight ({stillWaiting.length})</span>
              <QueueTotals rows={stillWaiting} verb="in the chain" kind={invoicePage ? 'invoice' : 'trip'} />
            </div>
            {stillWaiting.length ? (
              <QueueTable rows={sortByVendorThenInvoiceDate(stillWaiting)} onOpen={setOpenId} columns={[
                {
                  key: 'v', label: partyLabel,
                  render: r => (
                    <>
                      <PartyCell row={r} />
                      {coachMarks.has(r.id) && (
                        <span
                          title="Planned with the buyer coach"
                          style={{
                            display: 'inline-flex', alignItems: 'center',
                            marginLeft: 6, color: 'var(--orange, #DC582A)',
                            verticalAlign: 'middle',
                          }}
                          aria-label="Planned with the buyer coach">
                          <MessageSquare size={13} />
                        </span>
                      )}
                    </>
                  ),
                },
                { key: 'i', label: 'Invoice #', render: r => r.invoice_number || '—' },
                { key: 'd', label: 'Invoice date', render: r => fmtDate(r.invoice_date) },
                ...(invoicePage ? [
                  {
                    key: 't', label: 'Terms',
                    render: r => (r.net_terms
                      ? termLabel(r.net_terms, termOptions)
                      : <span style={{ color: 'var(--gray-400, #9ca3af)' }}>—</span>),
                  },
                  { key: 'du', label: 'Due', render: r => <DueCell value={r.due_date} terms={r.net_terms} /> },
                ] : []),
                { key: 'a', label: 'Billed', render: r => <BilledCell r={r} /> },
                { key: 's', label: 'Status', render: r => <StatusPill status={r.status} label={r.stage_label} /> },
                { key: 'e', label: 'Entered by', render: r => r.submitted_by_name || '—' },
                { key: 'w', label: 'There for', render: r => <AgeChip row={r} /> },
              ]} />
            ) : (
              <div style={{ ...cardBody, fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>
                Nothing has been sent for approval yet.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
